// @ts-ignore (no typings)
import { E_ALREADY_LOCKED, E_TIMEOUT, Mutex, tryAcquire } from 'async-mutex';
import { isEqual, startCase } from 'lodash';

import type { InspectSkill, LocateSkill } from '@sb/ai/types';
import type {
  LocateExecutionUpdate,
  PauseHostCommand,
  ResumeHostCommand,
  RunHostCommand,
  SafetySettingsData,
  StopHostCommand,
} from '@sb/feathers-types';
import type { CartesianPose } from '@sb/geometry';
import type { OnRobot2FG14State } from '@sb/integrations/implementations/onRobot/OnRobot2FG14/types';
import type { OnRobot2FG7State } from '@sb/integrations/implementations/onRobot/OnRobot2FG7/types';
import type {
  DeviceCommand,
  DeviceCommandResult,
} from '@sb/integrations/types';
import { getGripperConnectivity, isEndEffector } from '@sb/integrations/utils';
import * as log from '@sb/log';
import type { MessengerNode } from '@sb/messenger';
import { Empty } from '@sb/messenger/auto_generated/google/protobuf/empty';
import type { Primitive } from '@sb/messenger/auto_generated/messenger/trajectory_controller_service';
import type {
  EchoBotConnector,
  TeleopBotConnector,
  CameraBotConnector,
  ArmControlBotConnector,
  ForceTorqueConnector,
  ComplianceControlBotConnector,
  ExternalControlBotConnector,
  FeathersBotConnector,
  TrajectoryControllerConnector,
} from '@sb/messenger/connectors/generated';
import type {
  ArmAndDeviceMotionPlan,
  ArmJointAccelerations,
  ArmJointPositions,
  ArmJointVelocities,
  ArmTarget,
  ForwardKinematicsRequest,
  InverseKinematicsRequest,
  FrameOfReference,
  JerkLimit,
  JointNumber,
  MotionKind,
  MotionPlan,
  MotionPlannerInterface,
  MotionPlanOptions,
  MotionPlanRequest,
  MotionPlanResponse,
  TCPFrame,
  TCPOffsetOption,
} from '@sb/motion-planning';
import {
  ABSOLUTE_MAX_JOINT_SPEEDS,
  ABSOLUTE_MAX_TOOLTIP_SPEED,
  adHocArmTarget,
  ArmAndDeviceMotionPlanner,
  getBoxingApproachIgnoredPairs,
  getBoxingIgnoredCollisionPairs,
  getBoxingIntermediateAngles,
  getMaxJointAccelerationsForModel,
  getThorMaxAccelForCBVoltage,
  isNearBoxingIntermediate,
  isNearBoxingPosition,
} from '@sb/motion-planning';
import type { LEDColorPattern } from '@sb/robot';
import {
  getCBVoltage,
  getModelType,
  isOnlineJoggingActiveForThisModel,
  isOnlinePlannerAllowedForThisModel,
  isPrimitiveCollisionModelLoadedForModel,
  ModelType,
  onModelTypeUpdate,
} from '@sb/robot';
import { ActionCancelledError } from '@sb/ros/errors/ActionCancelledError';
import {
  isEveryDirectionBlocked,
  type FrameDirections,
} from '@sb/ros/FrameDirections';
import type { JoggingActionRequest } from '@sb/ros/JoggingAction';
import { JointTrajectorySubscription } from '@sb/ros/JointTrajectorySubscription';
import type { PoseSubscription } from '@sb/ros/PoseSubscription';
import type { RosMotionPlanner } from '@sb/ros/RosMotionPlanner';
import type { LinkCollisionParameters } from '@sb/ros/TaskFromRobotGoalAction';
import { StandardBotsSDK } from '@sb/sb-robot-sdk';
import { loadActiveCalibration } from '@sb/service-interfaces/host/calibration';
import { getFeatureFlagSync } from '@sb/service-interfaces/host/featureFlags';
import { loadHostIdentity } from '@sb/service-interfaces/host/identity';
import { getRoutineRunnerConfigSync } from '@sb/service-interfaces/host/routineRunnerConfig';
import type { Bit, Six, Sixteen } from '@sb/utilities';
import {
  EventEmitter,
  six,
  sixteen,
  toBit,
  wait,
  yieldToEventLoop,
} from '@sb/utilities';

import type { BigQueryInterface } from './BigQueryInterface';
import type { CommandResult } from './CommandResult';
import { DEFAULT_JOINT_DEVIATION_THRESHOLDS_RADIANS } from './configuration/defaults';
import {
  CHECK_COLLISIONS_INTERVAL_MS,
  HOLD_STEADY_POSITION_EPSILON,
  RECOVERY_SPEED_PROFILE,
  UNBOXING_SPEED_PROFILE,
  STILL_GOING_CHECK_INTERVAL_MS,
} from './constants';
import type EquipmentInterface from './EquipmentInterface';
import type { ExternalControlFaultSink } from './ExternalControlFaultSink';
import {
  getRecoveryType,
  getIsRecoverableWithWristButton,
} from './FailureDetails';
import { FailureKind } from './FailureKind';
import { getJogDirectionsWithTC } from './getJogDirectionsWithTC';
import type { Globals } from './Globals';
import { GripperDisconnectMonitor } from './GripperDisconnectMonitor';
import { JogMotionWithROS } from './JogMotionWithROS';
import {
  JogMotionWithTC,
  computeJogDirection,
  validateSpeedLDirection,
} from './JogMotionWithTC';
import { legacyRosApiBlocksMotion } from './legacyRosApi';
import { LoadedRoutine, unattributedStepFailureFields } from './LoadedRoutine';
import { RecoveryMotionWithROS } from './RecoveryMotionwithROS';
import type {
  AccelerationCollisionThresholds,
  JointSafetyLimits,
  RobotInterface,
} from './RobotInterface';
import { Routine } from './Routine';
import { RoutineContext } from './RoutineContext';
import { RoutineFrame } from './RoutineFrame';
import type {
  AdHocCommand,
  LoadedRoutineState,
  RoutineRunnerState,
  RoutineRunning,
  RoutineRunningPauseDetails,
  RoutineRunningPauseKind,
} from './RoutineRunnerState';
import {
  humanReadablePauseKind,
  visualizationAtRunBoundary,
} from './RoutineRunnerState';
import {
  calculateSpeedProfile,
  convertSafetySettingsResponse,
  type SpeedProfile,
} from './speed-profile';
import type { StepValidationMessage } from './Step';
import { LoopStep } from './Step';
import AntigravityStep from './Step/Antigravity/Step';
import ElseStep from './Step/Else/Step';
import ElseIfStep from './Step/ElseIf/Step';
import IfStep from './Step/If/Step';
import Step from './Step/Step';
import type {
  ActuateDeviceArgs,
  ButtonStateChangeEvent,
  ControlSystemEvent,
  EStopSource,
  IOLevel,
  IOStateChanged,
  JSRunner,
  MotionResult,
  PayloadState,
  PlayRoutineArgs,
  PyRunner,
  InputSafeguardRule,
  OutputSafeguardRule,
  SafeguardState,
  Space,
  StepFailure,
  AnyOutputPortID,
  OutOfLimitsEvent,
  AnyInputPortID,
  GetToolDirectionsRequest,
  SpeedLJog,
} from './types';
import {
  INPUT_PORT_IDS,
  safeguardRuleIsTriggeredBy,
  formatEStopMessage,
  formatImuCollisionMessage,
  isDeliberatePowerOff,
} from './types';
import type { FullAntigravityConfigs } from './types/AntigravityConfig';
import { forEachCallSubroutine } from './util/callSubroutineSteps';
import { composeCartesianFrames } from './util/tcFrames';
import { resolveTCPFrame } from './util/tcpFrame';
import type { VisionInterface } from './vision/VisionInterface';
import { VisionMethodRunner } from './vision/VisionMethodRunner';

/**
 * The interval to check for state updates from external sources.
 */
const STATE_UPDATE_CHECK_FREQUENCY_MS = 50;

/** How often to check whether an enabled gripper has dropped off the bus. */
const GRIPPER_CONNECTIVITY_CHECK_FREQUENCY_MS = 250;

/**
 * How long a gripper must read disconnected before the routine is failed.
 * Must outlast one driver-level UART recovery (~1s hysteresis, then a config
 * ack and the poll after it), so a transient end-effector desync doesn't fail
 * a routine the driver is about to rescue. A gripper that is really gone never
 * comes back, so it still fails, at driver 1s + this 2s.
 */
const GRIPPER_DISCONNECT_DEBOUNCE_MS = 2_000;

/**
 * Settle window before update()'s failure-state reconciliation
 * forces a latched arm-control-bot failure to converge to `Failure`. At the
 * 50ms update cadence this is ~2 ticks, which gives the fast
 * onControlSystemEvent listener time to win the race and PAUSE genuinely
 * resumable stops (collision / IMU / 3PE) before the reconciliation net would
 * otherwise force a hard fail. Only the un-reconciled wedge (an informational
 * `ioStateChanged`, or a null reason, that never transitions the runner) is
 * left to fall through and force `Failure`.
 */
const LATCHED_FAILURE_GRACE_MS = 100;

/**
 * How long update()'s reconciliation net holds off convergence after this side
 * has asked arm-control-bot to release its failure latch
 * (`setInFailureState(false)`). botman's copy of the latch keeps reading true
 * until the RPC lands and the state stream refreshes it — 150–450 ms measured
 * on the arm — so a resume that has just released its own stop must not be
 * judged against a latch that is already being cleared. A latch still set once
 * the grace has passed is genuinely stuck and converges as usual.
 */
const LATCH_RELEASE_GRACE_MS = 500;

/**
 * Boot grace for the latched-failure reconciliation net. While
 * arm-control-bot's control loop is still initializing, its failure latch
 * reads as "in failure, no reason" — the same shape as the genuine wedge,
 * but transient: it self-clears when init completes. Converging on it puts
 * a freshly booted robot into a `Failure` it never had (and the persisted
 * state kind keeps showing the error after the latch clears).
 *
 * Until arm-control has reported not-in-failure at least once, the net
 * therefore waits out this window instead of forcing `Failure`. A boot that
 * never becomes healthy is a real failure — once the window expires the net
 * converges as usual, so a genuinely broken boot still surfaces. Mirrors
 * the joint-communication boot grace botman's watchdog applies for the same
 * cold-start reason, and uses the same 180s budget.
 */
const BOOT_LATCHED_FAILURE_GRACE_MS = 180_000;

/**
 * Latched arm-control-bot reasons the realtime listener turns into a
 * recoverable pause rather than a terminal failure. When one of these reaches
 * update()'s reconciliation net it means the listener never got to run for it
 * (or ran before the latch existed): the stop is still resumable, so the net
 * replays it through the listener instead of converging to `Failure`. See
 * {@link RoutineRunner.reconcileLatchedFailure}.
 */
const REPLAYABLE_LATCHED_REASON_KINDS: ReadonlySet<ControlSystemEvent['kind']> =
  new Set([
    'imuCollision',
    'collision',
    'safetyWatchdogViolation',
    'controlSystemEvent',
  ]);

/** Extra context for a `handleControlSystemEvent` dispatch. */
interface HandleControlSystemEventOptions {
  /**
   * This dispatch is update()'s reconciliation net replaying a reason
   * arm-control-bot still has latched, not the realtime listener seeing it for
   * the first time. Handlers that de-duplicate repeated *live* events use this
   * to let the replay through.
   */
  isLatchReconciliationReplay?: boolean;
}

/**
 * Identifies one latched stop, so the reconciliation net can tell "the reason I
 * already replayed" from "a new stop that happens to be latched now". Every
 * `ControlSystemEvent` carries an ISO-8601 `time`, so kind + time is stable for
 * a given stop and distinct for the next one — including a re-latch fast enough
 * that update() never sampled the arm as healthy in between.
 */
function latchedReasonKey(event: ControlSystemEvent): string {
  return `${event.kind}@${event.time}`;
}

/**
 * Human-readable reason for a latched arm-control-bot failure that
 * update()'s reconciliation net forces to `Failure`. Usually that reason is an
 * `ioStateChanged` (a safety state change, e.g. an e-stop input going low) or a
 * null reason. A resumable kind (REPLAYABLE_LATCHED_REASON_KINDS) is replayed
 * through the listener first and normally pauses instead — but it still lands
 * here when that replay leaves the routine running, so `collision` and the
 * other replayable kinds remain live inputs. A targeted message for the
 * safety-state case plus a generic fallback covers all of them.
 */
function describeLatchedFailureReason(
  event: ControlSystemEvent | undefined,
): string {
  if (event == null) {
    return 'Robot entered a failure state; arm-control-bot did not report a specific reason.';
  }

  if (event.kind === 'ioStateChanged') {
    return 'Robot stopped by a safety state change (e-stop or safety input).';
  }

  return `Robot entered a failure state (${event.kind}).`;
}

/**
 * Every `CallSubroutine` reference — in the loaded routine or any sub — must
 * resolve within the supplied record. A dangling reference fails at load with
 * a clear error instead of mid-run at the moment of the call (R7: missing
 * refs are load-time spec errors).
 */
function validateSubroutineReferences(
  routine: Routine,
  subroutinesById: Record<string, Routine>,
): Array<StepValidationMessage> {
  const errors: Array<StepValidationMessage> = [];

  for (const spec of [routine, ...Object.values(subroutinesById)]) {
    forEachCallSubroutine(spec.steps, (step) => {
      const target = step.args.routineID;

      if (!target || !subroutinesById[target]) {
        errors.push({
          id: step.id,
          messages: [`CallSubroutine references unknown routine "${target}"`],
        });
      }
    });
  }

  return errors;
}

/**
 * Find the step a play would start from, searching the whole tree rather
 * than the top level so a start step nested inside a decorator resolves.
 */
function findStartStep(
  steps: ReadonlyArray<Step>,
  startStepID: string,
): Step | undefined {
  const remaining = [...steps];
  let step: Step | undefined;

  // eslint-disable-next-line no-cond-assign
  while ((step = remaining.pop())) {
    if (step.id === startStepID) {
      return step;
    }

    if (step.substeps) {
      remaining.push(...step.substeps);
    }
  }

  return undefined;
}

/**
 * [SUP-4118] Failure kinds that represent a real safety / control-system cause
 * raised by arm-control-bot (encoder deviation, collision, torque/joint-limit
 * watchdog, E-stop, etc.). When the routine is stopped by one of these it pauses
 * with a `failureState` that already names the actual cause; the in-flight motion
 * goal then aborts as a *side effect* of that stop and arrives as a generic
 * `ExecutionFailure` ("Controller aborted on … signal"). In that case the
 * control-system reason is the actionable one and should win over the generic
 * motion-abort string. A genuine motion-planning failure has no such concurrent
 * `failureState`, so it is unaffected.
 *
 * NOTE: this set must be extended whenever a new control-system / safety
 * `FailureKind` is added — otherwise its reason will be silently masked by the
 * generic motion-abort string instead of surfacing the real cause.
 */
const CONTROL_SYSTEM_FAILURE_KINDS: ReadonlySet<FailureKind> = new Set([
  FailureKind.ControlSystemFailure,
  FailureKind.CollisionFailure,
  FailureKind.TorqueCollisionFailure,
  FailureKind.CollisionDeviationFailure,
  FailureKind.SafetyWatchdogViolation,
  FailureKind.OutOfJointLimitsFailure,
  FailureKind.EStopTriggered,
  FailureKind.PoweredOff,
  FailureKind.TorqueLimitExceeded,
  FailureKind.BotmanHeartbeatLost,
]);

/**
 * Max number of errors while recovering before raising an internal error
 */
const OUT_OF_JOINT_LIMIT_RECOVERY_MAX_RETRIES = 2;

/**
 * Time to wait between retries for out of joint limit recovery
 */
const OUT_OF_JOINT_LIMIT_RECOVERY_RETRY_WAIT_TIME = 500;

/**
 * Time to wait before invalidating guided mode
 */
const GUIDED_MODE_TIMEOUT_MS_DEFAULT = 300;

/**
 * Max number of errors for joint trajectory subscription before raising an internal error
 */
export const JOINT_TRAJECTORY_MAX_RETRIES = 3;

/**
 * Time to wait between retries for out of joint trajectory subscription
 */
export const JOINT_TRAJECTORY_WAIT_TIME = 1000;

export const BOTMAN_HB_AUTO_RESUME_DURATION_MS = 5000;

/**
 * After a botman heartbeat-lost event, arm-control-bot's safety reaction runs on a
 * detached thread. When heartbeat regains, that detached thread may still be in
 * flight and hasn't yet applied its terminal state writes (rosControl=false,
 * armState=HOLDING, position cache reset).
 *
 * If we auto-resume immediately, the detached stop's writes can land *after* the
 * resume has already enabled ROS control and published scaling=1 — leaving
 * arm-control-bot in HOLDING+rosControl=false while ros2-control-bot's cursor
 * advances. Over time this produces multi-rad position drift that triggers
 * `Rebuilt active trajectory after pause` on the next user pause-resume,
 * synthesizing aggressive bridge motion (SUP-3789 on cb2098).
 *
 * Before committing to auto-resume, wait for the observable robot state to be
 * unchanged for {@link AUTO_RESUME_STABLE_DURATION_MS} (so any in-flight ACB
 * reaction has had time to settle), with an upper bound of
 * {@link AUTO_RESUME_SETTLE_TIMEOUT_MS}. If the state hasn't settled by then,
 * fall back to manual resume.
 */
export const AUTO_RESUME_STABLE_DURATION_MS = 100;

/**
 * Maximum time to wait for ACB to settle into a stable state before falling back
 * to manual resume. See {@link AUTO_RESUME_STABLE_DURATION_MS}.
 */
export const AUTO_RESUME_SETTLE_TIMEOUT_MS = 500;

/**
 * How often to poll robot state while waiting for the stable-state predicate to
 * be satisfied for {@link AUTO_RESUME_STABLE_DURATION_MS}.
 */
export const AUTO_RESUME_POLL_INTERVAL_MS = 20;

const TC_CLEAR_ERROR_TIMEOUT_MS = 500;

const ns = log.namespace('RoutineRunner');

interface RecoveryExecutionParams {
  startingJointPositions: ArmJointPositions;
  targetJointAngles?: ArmJointPositions;
  limitedSpeedProfile: Required<SpeedProfile>;
  isRecoveryMotionRequired: boolean;
  instructionID: number;
  retryCount: number;
  motionPlanOptions: MotionPlanOptions;
  ignoredCollisionLinkPairs?: Array<[string, string]>;
  linkCollisionParameters?: LinkCollisionParameters[];
}

/**
 * Main class that executes routines. This class can control a local simulator when in the browser,
 * or the robot when running on the robot. But it should not be set up to control the actual robot
 * from the browser. This is because we only want one routine runner to control the actual robot
 * so that we never have a situation where multiple browser-based routine runners are trying to
 * send commands to an actual robot at one time.
 */
export class RoutineRunner {
  private sdk: StandardBotsSDK;

  private routineContext: RoutineContext;

  private lastGuidedModeValidation = new Date();

  private startOfGuidedMode: Date | null = null;

  public guidedModeTimeoutMS = GUIDED_MODE_TIMEOUT_MS_DEFAULT;

  /**
   * The loaded routine's step tree, owned by the root frame it was built
   * against. Derived rather than stored so it cannot outlive — or fall out
   * of step with — the load that produced it: dropping the LoadedRoutine
   * unloads the tree with it.
   */
  private get steps(): Array<Step> | undefined {
    return this.routineContext.loadedRoutine?.rootFrame.routineSteps;
  }

  /**
   * This is used by methods that basically actively change the state of the routine runner to
   * the extent that another method should stop.
   *
   * For example, ad-hoc motions should always stop what they're doing if a `stop()` call comes in,
   * so we want to frequently check "should this method be interrupted?" after each time the
   * execution gets pre-empted.
   */
  private instructionID: number = 0;

  // Keep track of the currently running steps, including decorator steps (loop/if) and
  // steps within any 'RunInBackground' blocks. Deliberately NOT the source
  // of `currentStepLoadedRoutineStepId`: this stack also holds background
  // (RunInBackground) dispatches, which must not steal the attribution —
  // `publishCurrentStep` resolves it from the main track only.
  private currentlyRunningSteps: Array<Step> = [];

  /**
   * Publish the main-track current step onto the wire state, including its
   * loaded-routine attribution. Only main-track dispatches call this
   * (`setCurrentStepID` gates on `skipSettingCurrentStepID`), so background
   * RunInBackground subtrees never contend for the attribution.
   */
  private publishCurrentStep(step: Step): void {
    if (this.state.kind !== 'RoutineRunning') {
      return;
    }

    this.state = {
      ...this.state,
      currentStepID: step.id,
      currentStepKind: step.getStepKind(),
      currentStep: step,
      currentStepLoadedRoutineStepId: this.resolveLoadedRoutineStepIdFor(step),
    };
  }

  /**
   * The loaded routine's step to attribute `step`'s execution to: the step
   * itself when it runs on the loaded routine's own frame, else the loaded
   * routine's `CallSubroutine` step that started the running call chain.
   * The running-step analog of R11's `failedStepLoadedRoutineStepId`.
   *
   * The frame's `scopeKey` encodes call lineage
   * (`root:<callStepId>[:<nestedCallStepId>...]`), so the second segment is
   * the loaded-routine call step at any nesting depth.
   */
  private resolveLoadedRoutineStepIdFor(step: Step): string | undefined {
    const loadedFrame = this.routineContext.loadedRoutineFrame;

    if (!loadedFrame) {
      return undefined;
    }

    if (step.routineFrame === loadedFrame) {
      return step.id;
    }

    const segments = step.routineFrame?.scopeKey?.split(':') ?? [];

    return segments[0] === 'root' && segments.length > 1
      ? segments[1]
      : undefined;
  }

  private set state(newState: Readonly<RoutineRunnerState>) {
    this.routineContext.setRoutineRunnerState(newState);
  }

  private get state(): Readonly<RoutineRunnerState> {
    return this.routineContext.getRoutineRunnerState();
  }

  private checkStateUpdatesInterval: ReturnType<typeof setInterval>;

  private gripperConnectivityInterval: ReturnType<typeof setInterval>;

  // The monitor counts polls rather than tracking time, so the debounce window
  // is derived here where both constants are in view. Retuning the poll
  // frequency then keeps the window intact instead of silently changing it.
  private gripperDisconnectMonitor = new GripperDisconnectMonitor({
    requiredDisconnectedPolls: Math.ceil(
      GRIPPER_DISCONNECT_DEBOUNCE_MS / GRIPPER_CONNECTIVITY_CHECK_FREQUENCY_MS,
    ),
  });

  private cancelers: Array<() => void> = [];

  /**
   * Used internally during a routine play to determine whether we should be
   * actually playing the steps.
   *
   * Until we encounter the start step (first step by default), this should be false
   * and we should skip actually calling `.play()` on steps or updating state.
   *
   * Once we traverse through the steps as though we'd bee playing them, set this flag
   * to true so the remainder of the run can be run for real.
   */
  private hasEncounteredStartStep = false;

  /**
   * How the arm came to be boxed: found folded at startup, or commanded to box.
   * Position alone cannot express this.
   */
  private isMovingIntoBoxingPosition = false;

  /** Guards the as-found boxed check in {@link checkCollisions} to a single run. */
  private hasCheckedForBoxedAtStartup = false;

  private lastTorqueCollisionRecoveryAt = Number.MIN_SAFE_INTEGER;

  /**
   * Timestamp (ms) at which arm-control-bot was first observed
   * latched in-failure while the runner had not yet reconciled it into
   * Failure/Recovering. Drives the LATCHED_FAILURE_GRACE_MS settle in
   * reconcileLatchedFailure(); reset to null the moment getInFailureState()
   * clears or the runner reconciles.
   */
  private latchedFailureSince: number | null = null;

  /**
   * Set when acknowledgeControlSystemStop() releases the arm during a pause;
   * cleared when a run starts, when an ordinary pause episode begins, and
   * when the routine resumes through the ordinary path. The lift-pause and
   * lift-resume branches leave it alone: they toggle `isPaused` for the lift
   * only and do not end the arm-stop episode. While set, a control-system
   * `failureState` on the paused routine can only be a stop that landed after
   * the release — one nothing has acknowledged — so resumeRoutine() stays
   * paused with it instead of resuming over it.
   */
  private armReleasedWhilePaused = false;

  /**
   * When a latch release (`setInFailureState(false)`) was last requested while
   * arm-control-bot read as latched, so reconcileLatchedFailure() can tell
   * "still true because the release is propagating" from "nothing is clearing
   * this latch". Cleared when the latch reads false or the net converges, and
   * moot once LATCH_RELEASE_GRACE_MS has elapsed.
   */
  private latchReleaseRequestedAt: number | null = null;

  /**
   * The latched stop (see {@link latchedReasonKey}) reconcileLatchedFailure()
   * has already replayed through the control-system listener, so each stop gets
   * exactly one replay: a reason that pauses the routine needs no second one,
   * and a reason that leaves the routine running falls through to the
   * force-fail on the next tick rather than replaying every 50ms. Keyed on the
   * stop rather than on a per-episode flag so a *different* stop — including
   * one that re-latches before update() samples the arm as healthy — still gets
   * its own replay instead of inheriting a spent one.
   */
  private replayedLatchedReasonKey: string | null = null;

  /**
   * Timestamp (ms) of the first reconcileLatchedFailure() tick — the anchor
   * for BOOT_LATCHED_FAILURE_GRACE_MS.
   */
  private firstReconcileTickMs: number | null = null;

  /**
   * True once arm-control-bot has reported not-in-failure at least once
   * since construction. Before that, a latched failure is presumed to be
   * arm-control still booting (see BOOT_LATCHED_FAILURE_GRACE_MS), not a
   * stop the reconciliation net should converge.
   */
  private hasSeenHealthyArmControl = false;

  /**
   * Re-entrancy guard for the heartbeat-regained auto-resume gate. The gate
   * does multiple `await`s, so a second `botmanHeartbeatRegained` event arriving
   * during the first execution must not start a second concurrent run. See the
   * `botmanHeartbeatRegained` case below.
   */
  private isHeartbeatAutoResumePending = false;

  /**
   * The arm-latch release most recently requested through
   * acknowledgeControlSystemStop() and not yet settled, whoever requested it:
   * the pause card, external control, or resumeRoutine() itself. While it is
   * in flight the routine is still paused and the stop's failureState is
   * already cleared, so a resume arriving then would pass every check and
   * re-dispatch motion against an arm that is still held; resumeRoutine()
   * waits for it instead.
   */
  private pendingArmRelease: Promise<void> | null = null;

  private bigQuery: BigQueryInterface;

  private adHocMotion?: ReturnType<RoutineContext['doMotion']>;

  private tcJogInFlight = false;

  private adHocMotionMutex = new Mutex();

  /**
   * Get the collision link pairs to ignore during boxing/unboxing for the current model type.
   */
  private get boxingIgnoredCollisionPairs(): Array<[string, string]> {
    return getBoxingIgnoredCollisionPairs(this.state.kinematicState.modelType, {
      primitiveCollisionModel: this.primitiveCollisionModel,
    });
  }

  /**
   * Whether MoveIt2 plans against the primitive collision model.
   * ros2-control-bot loads it while online jogging is active and jog collision
   * avoidance is on, and the boxing poses and ignored pairs depend on which
   * model it plans against. Read with the schema defaults and the kinematic
   * state's model, as ros2-control-bot resolves them, so an unloaded flag cache
   * or an unreported model keeps the primitive set: its wider pair list is the
   * safe side to be wrong on.
   */
  private get primitiveCollisionModel(): boolean {
    return isPrimitiveCollisionModelLoadedForModel(
      this.state.kinematicState.modelType,
      {
        useTrajectoryController: getFeatureFlagSync(
          'useTrajectoryController',
          true,
        ),
        // Deliberately not the schema default (false): per the fail-safe
        // above, an unloaded cache must keep the wider primitive pair list.
        onlinePlannerJogging: getFeatureFlagSync('onlinePlannerJogging', true),
        onlinePlannerThor: getFeatureFlagSync('onlinePlannerThor', false),
        onlinePlannerSpark: getFeatureFlagSync('onlinePlannerSpark', false),
        onlinePlannerJoggingCollisionAvoidance: getFeatureFlagSync(
          'onlinePlannerJoggingCollisionAvoidance',
          true,
        ),
      },
    );
  }

  /**
   * Per-link padding/scale overrides sent to the planner for the boxing position.
   * The tight arm fold at joint3=-140° triggers false self-collision detections with
   * the global 2.5mm MoveIt2 padding on forearm_link and wrist_3_link.
   */
  private static readonly BOXING_LINK_COLLISION_PARAMETERS: LinkCollisionParameters[] =
    [
      { linkName: 'forearm_link', padding: 0.0, scale: 1.0 },
      { linkName: 'wrist_3_link', padding: 0.0, scale: 1.0 },
      { linkName: 'eef_collision', padding: 0.0, scale: 1.0 },
    ];

  /**
   * Event emitter for external control IO state changes.
   * Emits formatted input IO state for external control systems.
   */
  private externalControlIOEvents = new EventEmitter<{
    inputIO: Record<AnyInputPortID, IOLevel>;
  }>();

  /**
   * Tracks whether the wrist button has been released since entering failure state.
   * This prevents automatic recovery while the user is still holding the button from
   * when they were in anti-gravity mode and the failure occurred.
   */
  private hasWristButtonBeenReleasedSinceFailure = true;

  private jointTrajectorySubscription?: JointTrajectorySubscription;

  // Track when we last handled a collision event from the control system to prevent rapid-fire events
  private lastCollisionEventHandledAt = 0;

  // Configured joint deviation thresholds (radians) loaded at initialization
  private jointDeviationThresholdsRadians!: ArmJointPositions;

  /**
   * Check if enough time has passed since the last collision event to allow processing
   * @param cooldownMs The cooldown period in milliseconds
   * @returns true if enough time has passed, false otherwise
   */
  private isCollisionEventCooldownExpired(cooldownMs: number = 3000): boolean {
    const timeSinceLastCollisionEvent =
      Date.now() - this.lastCollisionEventHandledAt;

    return timeSinceLastCollisionEvent >= cooldownMs;
  }

  /**
   * Mark that a collision event has been handled (for cooldown tracking)
   */
  private markCollisionEventHandled(): void {
    this.lastCollisionEventHandledAt = Date.now();
  }

  /**
   * Reports safety failures to external-control customers, and is the only
   * thing that clears one. Absent outside botman (and in tests), where nothing
   * consumes these records.
   */
  private readonly externalControlFaultSink?: ExternalControlFaultSink;

  public constructor({
    hybridMotionPlanner, // TODO combine all into hybrid, remove need for following two args
    motionPlanner,
    rosMotionPlanner,
    robot,
    equipment,
    disabledEquipment,
    jsRunner,
    pyRunner,
    vision,
    bigQuery,
    runHostCommand,
    stopHostCommand,
    pauseHostCommand,
    resumeHostCommand,
    createRobotToken,
    revokeRobotToken,
    messengerNode,
    armControlBotConnector,
    forceTorqueConnector,
    trajectoryControllerConnector,
    cameraBotConnector,
    echoBotConnector,
    teleopBotConnector,
    feathersBotConnector,
    feathersApp,
    complianceControlBotConnector,
    externalControlBotConnector,
    robotId,
    poseSubscription,
    onPersistentVariableWrite,
    fetchPersistentVariableValues,
    getLocateSkillById,
    getInspectSkillById,
    updateLocateSkillExecutions,
    externalControlFaultSink,
  }: {
    hybridMotionPlanner: MotionPlannerInterface;
    motionPlanner: MotionPlannerInterface;
    rosMotionPlanner: RosMotionPlanner;
    robot: RobotInterface;
    equipment: EquipmentInterface;
    disabledEquipment: EquipmentInterface;
    jsRunner: JSRunner;
    pyRunner: PyRunner;
    vision: VisionInterface;
    bigQuery: BigQueryInterface;
    runHostCommand: RunHostCommand;
    stopHostCommand?: StopHostCommand;
    pauseHostCommand?: PauseHostCommand;
    resumeHostCommand?: ResumeHostCommand;
    createRobotToken: (
      name: string,
      source: string,
      expiresAt?: string,
    ) => Promise<{ tokenId: string; token: string }>;
    revokeRobotToken: (tokenId: string) => Promise<void>;
    messengerNode: MessengerNode;
    armControlBotConnector?: ArmControlBotConnector;
    forceTorqueConnector?: ForceTorqueConnector;
    trajectoryControllerConnector?: TrajectoryControllerConnector;
    cameraBotConnector?: CameraBotConnector;
    echoBotConnector?: EchoBotConnector;
    teleopBotConnector?: TeleopBotConnector;
    feathersBotConnector?: FeathersBotConnector;
    feathersApp?: {
      service(name: string): {
        get(id: string): Promise<unknown>;
        create(data: unknown): Promise<unknown>;
      };
    };
    complianceControlBotConnector?: ComplianceControlBotConnector;
    externalControlBotConnector?: ExternalControlBotConnector;
    robotId: string;
    poseSubscription?: PoseSubscription;
    onPersistentVariableWrite?: (
      variableId: string,
      value: unknown,
      routineId: string | null,
    ) => Promise<void>;
    fetchPersistentVariableValues?: (
      routineId: string,
    ) => Promise<Map<string, unknown>>;
    getLocateSkillById?: (id: string) => Promise<LocateSkill | undefined>;
    getInspectSkillById?: (id: string) => Promise<InspectSkill | undefined>;
    updateLocateSkillExecutions?: (
      update: LocateExecutionUpdate,
    ) => Promise<void>;
    externalControlFaultSink?: ExternalControlFaultSink;
  }) {
    this.sdk = new StandardBotsSDK({
      url: `http://${process.env.ROBOT_HOSTNAME}:4242`,
      robotKind: robot.robotKind,
    });

    this.externalControlFaultSink = externalControlFaultSink;

    this.routineContext = new RoutineContext({
      robot,
      hybridMotionPlanner,
      motionPlanner,
      rosMotionPlanner,
      jsRunner,
      pyRunner,
      equipment,
      disabledEquipment,
      vision,
      runHostCommand,
      stopHostCommand,
      pauseHostCommand,
      resumeHostCommand,
      createRobotToken,
      revokeRobotToken,
      messengerNode,
      armControlBotConnector,
      forceTorqueConnector,
      trajectoryControllerConnector,
      cameraBotConnector,
      echoBotConnector,
      teleopBotConnector,
      feathersBotConnector,
      feathersApp,
      complianceControlBotConnector,
      externalControlBotConnector,
      sdk: this.sdk,
      robotId,
      poseSubscription,
      onPersistentVariableWrite,
      getLocateSkillById,
      getInspectSkillById,
      updateLocateSkillExecutions,
      fetchPersistentVariableValues,
      externalControlFaultSink,
    });

    this.visionMethodRunner = new VisionMethodRunner({
      equipment,
      vision,
      getState: () => this.routineContext.getRoutineRunnerState(),
    });

    this.bigQuery = bigQuery;

    // save cancelation function for destroy()
    const onVariablesChangeCancel = this.routineContext.onVariablesChange(
      (variables) => {
        if (this.state.kind === 'RoutineRunning') {
          this.state = {
            ...this.state,
            variables,
          };
        }
      },
    );

    this.cancelers.push(onVariablesChangeCancel);

    this.checkStateUpdatesInterval = setInterval(() => {
      this.update();
    }, STATE_UPDATE_CHECK_FREQUENCY_MS);

    this.gripperConnectivityInterval = setInterval(() => {
      this.checkGripperConnectivity();
    }, GRIPPER_CONNECTIVITY_CHECK_FREQUENCY_MS);

    const cancelControlSystemEventListener = this.robot.onControlSystemEvent(
      (event) => {
        this.handleControlSystemEvent(event);
      },
    );

    this.cancelers.push(cancelControlSystemEventListener);

    this.checkCollisions();
    this.checkCollisionsLoop();

    // If any motion plan fails, check collisions.
    // This is sort of temporary. Once motion plan failures are better reported, we can
    // fail more specifically when motion plans fail.
    this.motionPlanner.onMotionPlan((_request, response) => {
      response.once('error', () => {
        this.checkCollisions();
      });
    });

    // Initialize joint trajectory subscription to track expected joint positions.
    // Not awaited, so the final failure is logged here rather than left as an
    // unhandled rejection.
    this.initializeJointTrajectorySubscription().catch((error: unknown) => {
      log.error(
        ns`initializeJointTrajectorySubscription`,
        'Joint trajectory subscription unavailable',
        error,
      );
    });

    if (!getFeatureFlagSync('disableDynamicHardwareConfig', false)) {
      this.cancelers.push(
        onModelTypeUpdate((newModelType) => {
          this.routineContext.updateModelType(newModelType);
          // Decisions cached under a different variant used the wrong boxing
          // angles and ignored-pair list, so they must not survive the change.
          this.resetCollisionCheckCache();
        }),
      );
    }

    const initialModelType = getModelType();

    if (initialModelType) {
      this.routineContext.updateModelType(initialModelType);
    } else {
      // Routinely absent this early: arm-control-bot reports UNKNOWN until CAN
      // joint discovery finishes. Log it, since the placeholder was silent.
      log.warn(
        ns`modelType.unknown`,
        'Hardware variant not yet known, using RO1 placeholder',
      );

      this.routineContext.updateModelType(ModelType.enum.RO1);
    }

    // Load the per-robot kinematic calibration once at startup so the 3D
    // visualizer can align the rendered URDF with the calibrated tooltip pose
    // (SUP-4010). This is static config: it is set once here and never mutated,
    // so it does not contribute to the per-tick state delta. Reads from the
    // host calibration file via fs; resolves to undefined on uncalibrated
    // robots / dev / sim, where the visualizer no-ops.
    loadActiveCalibration()
      .then((calibrationData) => {
        if (calibrationData?.urdfParameters) {
          this.routineContext.updateKinematicCalibration(
            calibrationData.urdfParameters,
          );
        }
      })
      .catch((error) => {
        log.error(
          'RoutineRunner.loadActiveCalibration',
          'Failed to load active kinematic calibration for visualizer',
          error,
        );
      });

    // Configuration is preloaded in makeRoutineRunner(). Use it if present; otherwise defaults
    const cfg = getRoutineRunnerConfigSync() ?? {};
    const t = (cfg as any).jointDeviationThresholdsRadians;

    const valid =
      Array.isArray(t) &&
      t.length === 6 &&
      !t.some((v: any) => typeof v !== 'number' || !Number.isFinite(v));

    this.jointDeviationThresholdsRadians = valid
      ? (t as ArmJointPositions)
      : (DEFAULT_JOINT_DEVIATION_THRESHOLDS_RADIANS as ArmJointPositions);
  }

  /**
   * Initialize joint trajectory subscription with retry logic
   * @throws Error if subscription fails after all retries
   */
  private async initializeJointTrajectorySubscription(): Promise<void> {
    for (
      let attempt = 1;
      attempt <= JOINT_TRAJECTORY_MAX_RETRIES;
      attempt += 1
    ) {
      try {
        // Get robot ID from proper identity system
        const identity = await loadHostIdentity();
        const robotId = identity?.robotId || 'local';

        this.jointTrajectorySubscription = new JointTrajectorySubscription(
          robotId,
        );

        await this.jointTrajectorySubscription.subscribe();

        log.info(
          ns`initializeJointTrajectorySubscription`,
          `Successfully subscribed to joint trajectory topic on attempt ${attempt}`,
        );

        return; // Success, exit the retry loop
      } catch (error: any) {
        log.error(
          ns`initializeJointTrajectorySubscription`,
          `Failed to subscribe to joint trajectory topic on attempt ${attempt}/${JOINT_TRAJECTORY_MAX_RETRIES}`,
          error,
        );

        if (attempt === JOINT_TRAJECTORY_MAX_RETRIES) {
          // Final attempt failed, throw the error
          throw new Error(
            `Failed to subscribe to joint trajectory topic after ${JOINT_TRAJECTORY_MAX_RETRIES} attempts: ${error?.message || 'Unknown error'}`,
          );
        }

        // Wait before retrying
        await new Promise((resolve) =>
          setTimeout(resolve, JOINT_TRAJECTORY_WAIT_TIME),
        );
      }
    }
  }

  private async handleControlSystemEvent(
    event: ControlSystemEvent | undefined,
    options?: HandleControlSystemEventOptions,
  ) {
    // Dispatches ALL control-system events, most of which are not failures
    // (ioStateChanged, INFO/WARNING-level generic events, heartbeat regained,
    // etc.). Two call sites: the onControlSystemEvent listener, and
    // reconcileLatchedFailure()'s replay of a resumable latched reason. Both
    // fire-and-forget it, so that is its contract. Its awaited
    // internals can reject — `fail()` (the `outOfLimits` case, whose `stop()`
    // arm/gripper RPCs can time out) and `resumeRoutine()` via
    // `removePauseKind` (the `botmanHeartbeatRegained` case). Contain here
    // (mirroring `pauseOrFail`) so a rejection can never surface as an
    // unhandledRejection that crashes botman.
    try {
      await this.handleControlSystemEventImpl(event, options);
    } catch (error) {
      log.error(
        ns`handleControlSystemEvent.error`,
        'Error in handleControlSystemEvent',
        error,
      );
    }
  }

  private async handleControlSystemEventImpl(
    event: ControlSystemEvent | undefined,
    options?: HandleControlSystemEventOptions,
  ) {
    if (!event) {
      return;
    }

    switch (event.kind) {
      case 'controlSystemEvent': {
        // only errors and critical should cause a failure
        if (
          !['VIOLATION', 'ERROR', 'CRITICAL'].includes(
            event.controlSystemEvent.level,
          )
        ) {
          break;
        }

        this.pauseOrFail({
          failure: {
            kind: FailureKind.ControlSystemFailure,
            event,
          },
          failureReason: `Robot encountered a control system issue: ${event.controlSystemEvent.message}`,
        });

        break;
      }
      case 'imuCollision': {
        // Same codepath as the CRITICAL General ('controlSystemEvent') event
        // IMU collisions were raised as before becoming a first-class kind:
        // ControlSystemFailure pauses the routine (recoverable) with the same
        // user-facing message.
        this.pauseOrFail({
          failure: {
            kind: FailureKind.ControlSystemFailure,
            event,
          },
          failureReason: `Robot encountered a control system issue: ${formatImuCollisionMessage(event)}`,
        });

        break;
      }
      case 'eStopTriggered': {
        const { source, reason } = event.eStopTriggered;

        // A deliberate power-off — user-requested or the firmware echo of
        // the commanded stop — is modeled as PoweredOff so no consumer has
        // to infer intent from the E-Stop source.
        if (isDeliberatePowerOff(source, reason)) {
          this.pauseOrFail({
            failure: { kind: FailureKind.PoweredOff, source, reason },
            failureReason: 'Arm was powered off',
          });

          break;
        }

        this.pauseOrFail({
          failure: { kind: FailureKind.EStopTriggered, source, reason },
          failureReason: formatEStopMessage(source, reason),
        });

        break;
      }
      case 'safetyWatchdogViolation': {
        const { joint, violationType, violationLevel, actual, limit } =
          event.safetyWatchdogViolation;

        let failureReasonMessage;

        if (violationLevel === 'ARM_CONTROL_BOT_LIMIT') {
          // Suppress user-facing overlay in guided mode while paused:
          // This "too close" warning is informational to stop early, but during guided-mode
          // pause flow we don't want to surface a new failure popup that asks to resume.
          if (
            this.state.kind === 'RoutineRunning' &&
            this.state.shouldCheckGuidedMode &&
            this.state.isPaused
          ) {
            log.info(
              ns`safetyWatchdogViolation.guidedModeSuppressed`,
              `Suppressing safety watchdog warning in guided mode (paused). joint=${joint} violationType=${violationType}`,
              { violationLevel, actual, limit },
            );

            break;
          }

          failureReasonMessage = `Joint ${joint} too close to violating the safety limit in ${violationType}. Stopping prematurely.`;
        } else if (violationLevel === 'SAFETY_FAST_LIMIT') {
          failureReasonMessage = `Joint ${joint} violated the fast safety limit in ${violationType}. Actual: ${actual}. Limit: ${limit}.`;
        } else if (violationLevel === 'SAFETY_SLOW_LIMIT') {
          failureReasonMessage = `Joint ${joint} violated the slow safety limit in ${violationType}. Actual: ${actual}. Limit: ${limit}.`;
        } else {
          failureReasonMessage = `Joint ${joint} violated the user set limit in ${violationType}. Actual: ${actual}. Limit: ${limit}.`;
        }

        this.pauseOrFail({
          failure: {
            kind: FailureKind.SafetyWatchdogViolation,
            event,
            isManualRecoveryRequired:
              violationType === 'Torque' &&
              (this.state.kinematicState.brakesEngaged ||
                Date.now() - this.lastTorqueCollisionRecoveryAt < 5000),
          },
          failureReason: failureReasonMessage,
        });

        break;
      }
      case 'ioStateChanged': {
        try {
          await this.handleIOStateChange(event);
        } catch (error) {
          log.error(
            ns`io.react.failure`,
            'Failure reacting to IO state change',
            error,
          );
        }

        break;
      }
      case 'collision': {
        // Prevent immediate re-triggering of collision events from control
        // system. A latch reconciliation replay is exempt: the net sends at
        // most one per latched stop, and that stop's own listener event is
        // exactly what stamped the cooldown, so applying it here would swallow
        // the recovery instead of the re-trigger it guards against.
        if (
          !options?.isLatchReconciliationReplay &&
          !this.isCollisionEventCooldownExpired()
        ) {
          break;
        }

        this.markCollisionEventHandled();

        const failureReason = `Joint ${event.collision.joint} encountered a collision. Disturbance detected: ${event.collision.disturbanceDetected.toFixed(1)} Nm. Threshold set to ${event.collision.disturbanceThreshold} Nm`;

        this.pauseOrFail({
          failure: {
            kind: FailureKind.TorqueCollisionFailure,
            event,
            isManualRecoveryRequired:
              this.state.kinematicState.brakesEngaged ||
              Date.now() - this.lastTorqueCollisionRecoveryAt < 5000,
          },
          failureReason,
          ...this.currentStepIdentity(),
        });

        break;
      }
      case 'outOfLimits': {
        this.fail(this.getJointLimitViolationFailure(true, event));
        break;
      }

      case 'botmanHeartbeatLost': {
        this.pauseOrFail({
          failure: {
            kind: FailureKind.BotmanHeartbeatLost,
          },
          failureReason: 'Internal communication failure',
        });

        break;
      }

      case 'botmanHeartbeatRegained': {
        const now = Date.now();

        if (this.state.kind !== 'RoutineRunning') {
          break;
        }

        const botmanHeartbeatLostPause = this.state.pauseDetails.find(
          (pause) => pause.kind === 'botmanHeartbeatLost',
        );

        const heartbeatLostDuration = botmanHeartbeatLostPause?.timestamp
          ? now - botmanHeartbeatLostPause.timestamp
          : BOTMAN_HB_AUTO_RESUME_DURATION_MS * 2;

        if (heartbeatLostDuration >= BOTMAN_HB_AUTO_RESUME_DURATION_MS) {
          // For longer outages, require manual resume so we don't have unexpected motion
          this.demoteHeartbeatPauseToManualResume(
            `Communication restored after ${heartbeatLostDuration}ms; outage exceeded ${BOTMAN_HB_AUTO_RESUME_DURATION_MS}ms auto-resume threshold.`,
          );

          break;
        }

        // Re-entrancy guard: the gate below does multiple `await`s. If a second
        // botmanHeartbeatRegained arrives while the first is still running, both
        // would otherwise race to call removePauseKind → resumeRoutine. ACB's
        // 750ms heartbeat threshold makes this geometrically unlikely, but
        // transport-layer duplication or future threshold changes could surface
        // it; the guard is cheap defense-in-depth.
        if (this.isHeartbeatAutoResumePending) {
          break;
        }

        this.isHeartbeatAutoResumePending = true;

        try {
          // ACB's heartbeat-lost reaction runs on a detached thread. When heartbeat
          // regains, that thread may still be in flight. Wait for the observable
          // state to be unchanged for AUTO_RESUME_STABLE_DURATION_MS before
          // committing to auto-resume, so any pending ACB safety reactions have a
          // chance to settle. If they settle into a state we shouldn't auto-resume
          // from (failure, safeguard=eStop, brakes engaged), fall back to manual.
          const settled = await this.waitForStableRobotSnapshot({
            stableDurationMs: AUTO_RESUME_STABLE_DURATION_MS,
            timeoutMs: AUTO_RESUME_SETTLE_TIMEOUT_MS,
          });

          if (!settled) {
            this.demoteHeartbeatPauseToManualResume(
              'Robot state did not settle within timeout after heartbeat regained.',
            );

            break;
          }

          // Re-check routine state — it could have transitioned to Idle/Failure
          // (e.g. user pressed stop, an unrelated failure landed) during the
          // settle wait above.
          if (this.state.kind !== 'RoutineRunning') {
            break;
          }

          if (!this.isStateSafeToAutoResume()) {
            this.demoteHeartbeatPauseToManualResume(
              `Robot is not in a safe state to auto-resume after heartbeat regained: ${this.describeUnsafeAutoResumeState()}.`,
            );

            break;
          }

          await this.removePauseKind('botmanHeartbeatLost');
        } finally {
          this.isHeartbeatAutoResumePending = false;
        }

        break;
      }

      case 'buttonStateChangeEvent': {
        this.handleButtonStateChange(event);
        break;
      }

      case 'powerBoardUnhealthy': {
        this.pauseOrFail({
          failure: {
            kind: FailureKind.PowerBoardCheckFailure,
          },
          failureReason: `EStop health check failed.`,
        });

        break;
      }
    }
  }

  /**
   * Remove listeners and release any resources
   */
  public destroy() {
    this.instructionID += 1;
    clearInterval(this.checkStateUpdatesInterval);
    clearInterval(this.gripperConnectivityInterval);

    this.cancelers.forEach((cancel) => cancel());

    // Clean up joint trajectory subscription
    if (this.jointTrajectorySubscription) {
      this.jointTrajectorySubscription.unsubscribe().catch((error) => {
        log.error(
          ns`destroy`,
          'Failed to unsubscribe from joint trajectory topic',
          error,
        );
      });
    }

    this.routineContext.destroy();
    this.robot.destroy();
  }

  public async triggerUserFault(message: string, isRecoverable: boolean) {
    await this.pauseOrFail({
      failure: {
        kind: FailureKind.TriggerFaultFailure,
        isRecoverable,
      },
      failureReason: message,
      ...this.currentStepIdentity(),
    });
  }

  public static validateRoutine(routine: unknown): Routine {
    return Routine.parse(routine);
  }

  /**
   * Backdoor to access the routine context for the API.
   * Avoid using this if possible.
   */
  public getRoutineContextForAPI(): RoutineContext {
    return this.routineContext;
  }

  /**
   * This loads a routine to be run into the routine runner. When changes are made to a routine, it
   * should be reloaded.
   *
   * @category Command-loading
   * @param routine           the routine to be loaded.
   * @param globals           the global data to load with the routine
   * @param startConditions   the conditions to start the routine with (including start step and
   *                          variable state)
   * @returns All validation errors with the ids of the steps that errored
   */
  public loadRoutine(
    routine: Routine,
    globals: Globals,
    startConditions?: Pick<RoutineRunning, 'currentStepID' | 'variables'>,
    subroutines?: Record<string, Routine>,
  ): { errors: Array<StepValidationMessage> } {
    if (this.state.kind === 'RoutineRunning') {
      throw new Error('Cannot load a routine while the routine is running');
    }

    const validationResults: Array<StepValidationMessage> = [];

    const subroutinesById = subroutines ?? {};

    // Drop any previous load first: from here until this load succeeds, the
    // runner reads as "nothing loaded", so a failed load can't leave a
    // half-installed mix of old and new state.
    this.routineContext.clearLoadedRoutine();

    // Everything with load-to-unload lifetime (root frame, workspace
    // globals, per-load caches) lives on one object. The candidate is built
    // and validated off to the side and installed as the last statement, so
    // no failure path can leave it half-applied.
    //
    // This must stay synchronous end to end: an `await` anywhere in the build
    // would make the "nothing loaded" window above observable to the
    // `checkStateUpdatesInterval` tick.
    const candidate = new LoadedRoutine({
      context: this.routineContext,
      routineId: routine.id,
      routineName: routine.name,
      initialSpace: [...(routine.space ?? [])],
      globals,
    });

    const loadedRoutineFrame = candidate.rootFrame;

    // Cache the subroutines map on the load session so
    // CallSubroutineStep._play can look up specs at dispatch time.
    candidate.subroutinesById = subroutinesById;

    if (startConditions?.variables) {
      Object.entries(startConditions.variables).forEach(
        ([stepID, variableState]) => {
          log.info(
            ns`loadRoutine.setVariable`,
            `Setting variable state for ${stepID} to ${variableState}`,
            {
              stepID,
              variableState,
            },
          );

          loadedRoutineFrame.setVariable(stepID, variableState);
        },
      );
    }

    const loadedTree = this.buildStepTree(routine, loadedRoutineFrame);

    loadedRoutineFrame.routineSteps = loadedTree.steps;
    validationResults.push(...loadedTree.errors);

    // Build one "canonical" instance tree per cached subroutine spec at load
    // time. Each tree has two jobs:
    //   1. Surface validation errors (Zod arg parsing, structural substep
    //      arity, etc.) here as part of `loadRoutine`'s normal error contract
    //      rather than blowing up mid-play when `CallSubroutineStep._play`
    //      first dispatches into the subroutine.
    //   2. Hold step instances to run setup/teardown on them, which is the current contract
    //      considering how little set up and teardown are used we could revisit this
    for (const subRoutine of Object.values(subroutinesById)) {
      // Build a canonical frame for each subroutine spec. Canonical
      // instances never play, but `Step.routineFrame` is non-null on
      // every instance, so this frame stands in for the per-call frames
      // that `CallSubroutineStep._play` allocates later. Variable /
      // space state on the canonical frame is meaningless (canonical
      // steps never read or write it).
      const canonicalFrame = new RoutineFrame({
        loadedRoutine: candidate,
        scopeKey: `canonical:${subRoutine.id}`,
        routineId: subRoutine.id,
        routineName: subRoutine.name,
        callSubroutineStepId: null,
        parentFrame: null,
        initialSpace: [],
      });

      const canonicalTree = this.buildStepTree(subRoutine, canonicalFrame);

      // Setup/teardown failures on canonical instances attribute via
      // `Step.getStepPosition`, which walks the owning frame's tree.
      canonicalFrame.routineSteps = canonicalTree.steps;
      validationResults.push(...canonicalTree.errors);

      candidate.subroutineCanonicalsByRoutineId.set(
        subRoutine.id,
        canonicalTree.steps,
      );
    }

    validationResults.push(
      ...validateSubroutineReferences(routine, subroutinesById),
    );

    if (validationResults.length > 0) {
      // The candidate was never installed — a caller that logs the errors
      // and proceeds (e.g. `loadInitialState`'s payload/speed restore) sees
      // the coherent "no routine loaded" runner established up top.
      return { errors: validationResults };
    }

    const startStepID = startConditions?.currentStepID ?? routine.steps[0].id;

    if (!findStartStep(loadedTree.steps, startStepID)) {
      throw new Error(`Could not find starting step ID ${startStepID}`);
    }

    // Extract environment variable overrides from startConditions so they
    // survive through resetOnPlayRoutine() which would otherwise overwrite
    // them with the default initialValue expressions.
    const environmentVariableOverrides: Record<string, unknown> = {};

    if (startConditions?.variables) {
      for (const [id, variableState] of Object.entries(
        startConditions.variables,
      )) {
        if (
          variableState &&
          'stepKind' in variableState &&
          variableState.stepKind === 'EnvironmentVariable'
        ) {
          environmentVariableOverrides[id] = variableState.value;
        }
      }
    }

    candidate.spec = {
      ...routine,
      hasRunGuided: false,
      startStepID,
      // Deliberately the SAME array as the root frame's localSpace: the
      // spec view and the runtime view of the routine's own space must not
      // drift when items are added or replaced.
      space: candidate.rootFrame.localSpace,
      environmentVariables: routine.environmentVariables ?? [],
      ...(Object.keys(environmentVariableOverrides).length > 0 && {
        environmentVariableOverrides,
      }),
    };

    // Validation passed and the candidate is complete. This assignment is the
    // load's one and only mutation of runner/context state.
    this.routineContext.loadedRoutine = candidate;

    this.update();

    return { errors: [] };
  }

  /**
   * Construct one routine spec's steps against the frame that will own them.
   * The caller assigns the result to `frame.routineSteps`, which is what lets
   * `Step.getStepPosition` resolve a step's 1-based DFS position for R11
   * failure attribution.
   *
   * Each spec validates against a FRESH duplicate-id set: step ids are only
   * unique within a routine (duplication preserves ids), so one spec's ids
   * must not count as duplicates of another's.
   */
  private buildStepTree(
    spec: Routine,
    frame: RoutineFrame,
  ): { steps: Array<Step>; errors: Array<StepValidationMessage> } {
    const seenStepIds = new Set<string>();
    const steps: Array<Step> = [];
    const errors: Array<StepValidationMessage> = [];

    for (const stepConfig of spec.steps) {
      // constructStep recurses
      const result = Step.constructStep(
        stepConfig,
        this.routineContext,
        frame,
        seenStepIds,
      );

      if ('errors' in result) {
        errors.push(...result.errors);
      } else {
        steps.push(result.step);
      }
    }

    return { steps, errors };
  }

  /**
   * This command moves the robot into boxing position and change the routine state into InBoxingPositionFailure
   * @category Commands
   * @throws if the state is not Idle
   * @throws if the robot is braked
   */
  public async moveToBoxingPosition(
    goal: ArmJointPositions,
    options: SpeedProfile,
  ): Promise<CommandResult> {
    this.isMovingIntoBoxingPosition = true;

    return this.stagedBoxingMove(goal, options);
  }

  /**
   * Two-leg boxing move: approach the intermediate with a reduced collision
   * allowance (only forearm↔shoulder + EE wildcards), then fold to the boxing
   * pose with the full allowance. If the arm is already near the intermediate,
   * skip straight to the fold. If leg 1 fails to plan, fall back to single-leg
   * with the full allowance so boxing is never harder than before.
   */
  private async stagedBoxingMove(
    goal: ArmJointPositions,
    options: SpeedProfile,
  ): Promise<CommandResult> {
    const { modelType } = this.state.kinematicState;

    const flagOpts = {
      primitiveCollisionModel: this.primitiveCollisionModel,
    };

    const foldPairs = this.boxingIgnoredCollisionPairs;
    const foldLinkParams = RoutineRunner.BOXING_LINK_COLLISION_PARAMETERS;

    // Already at or past the intermediate → skip straight to the fold.
    if (isNearBoxingIntermediate(this.getJointAngles(), modelType, flagOpts)) {
      log.info(
        ns`moveToBoxingPosition`,
        'Arm near boxing intermediate — skipping to fold leg',
      );

      return this.moveToJointSpacePoint(
        goal,
        options,
        undefined,
        true,
        foldPairs,
        foldLinkParams,
      );
    }

    // Leg 1: approach the intermediate with only forearm↔shoulder allowed.
    const intermediate = getBoxingIntermediateAngles(modelType, flagOpts);
    const approachPairs = getBoxingApproachIgnoredPairs(modelType, flagOpts);

    // An explicit empty list, not undefined: undefined means "use the boxing
    // defaults", which strip the padding on forearm_link and wrist_3_link.
    // Checking forearm↔wrist_3 on the approach is the point of the split, and
    // it has to be checked against padded geometry to mean anything. The
    // intermediate clears that pair by 11.0 mm, well past the 2.5 mm padding.
    const approachLinkParams: LinkCollisionParameters[] = [];

    const leg1Result = await this.moveToJointSpacePoint(
      intermediate,
      options,
      undefined,
      true,
      approachPairs,
      approachLinkParams,
    );

    // Interrupted (button release) — bail, do not start leg 2.
    if (leg1Result === 'Interrupted') {
      return leg1Result;
    }

    // Planning failure or other non-success: fall back to single-leg with full
    // allowance so boxing is never harder than the pre-staging behavior.
    // moveToJointSpacePointROS2 returns FailureKind values (not exceptions)
    // for planning failures, so this branch is the actual fallback path.
    if (leg1Result !== 'Success') {
      log.warn(
        ns`moveToBoxingPosition`,
        'Leg 1 (approach) failed — falling back to single-leg with full allowance',
        { leg1Result },
      );

      return this.moveToJointSpacePoint(
        goal,
        options,
        undefined,
        true,
        foldPairs,
        foldLinkParams,
      );
    }

    // Guard: if guided mode expired during the inter-leg gap (operator
    // released the hold button while leg 1 was completing), do not start
    // leg 2. Without this check, leg 2 enters moveToJointSpacePointROS2
    // uncontended and re-arms guided mode via validateGuidedMode(). An
    // explicit `stop()` lands here too, because it expires
    // `lastGuidedModeValidation`; leg 1's motion is already complete by this
    // point, so `stopRunningWork()` has nothing to cancel and the instruction
    // ID it bumps is superseded by leg 2's own.
    if (!this.isGuidedModeValid()) {
      log.info(
        ns`moveToBoxingPosition`,
        'Guided mode expired between legs — not starting fold',
      );

      return 'Interrupted';
    }

    // Guard: honour the collision state at the intermediate before folding.
    // Leg 2 is a fresh command, so moveToJointSpacePointROS2 runs its own
    // checkCollisions() on entry — but `boxing` bypasses the idle guard below
    // it, so the fold would start anyway on top of a failure that same call
    // just latched. Run the check here and stop on its verdict instead.
    // Contacts the fold list covers are suppressed inside the corridor, so
    // reaching Failure here means a contact it does not cover.
    await this.checkCollisions();

    if (this.state.kind === 'Failure') {
      const failureKind = this.state.failureState.failure.kind;

      log.warn(
        ns`moveToBoxingPosition`,
        'Collision at the intermediate — not starting fold',
        { failureKind },
      );

      return failureKind;
    }

    // Leg 2: fold to the boxing pose with the full allowance.
    return this.moveToJointSpacePoint(
      goal,
      options,
      undefined,
      true,
      foldPairs,
      foldLinkParams,
    );
  }

  public async moveToJointSpacePointROS2(
    goal: ArmJointPositions,
    options: SpeedProfile,
    intermediateGoals?: ArmJointPositions[],
    boxing?: boolean,
    _useCache?: boolean,
    overrideIgnoredPairs?: Array<[string, string]>,
    overrideLinkCollisionParams?: LinkCollisionParameters[],
  ): Promise<CommandResult> {
    log.info(
      ns`moveToJointSpacePointROS2`,
      'Moving to joint space point with ROS2',
      {
        goal,
        jointAngles: this.getJointAngles(),
      },
    );

    await this.checkCollisions();

    if (
      this.state.kind !== 'Idle' &&
      !boxing // if trying to box, we can ignore the non-idle
    ) {
      return 'NonIdle';
    }

    if (this.robot.isRobotBraked()) {
      return 'Braked';
    }

    if (this.routineContext.isPause3PEActive()) {
      return 'SafetyStopped';
    }

    // SUP-3983: ad-hoc motions queue on `adHocMotionMutex`. Spamming the unbox
    // button enqueues several `moveToJointSpacePoint(boxing)` calls behind the
    // one already running. Record, *before* we queue, whether another ad-hoc
    // motion currently holds the mutex.
    const wasQueuedBehindRunningMotion = this.adHocMotionMutex.isLocked();

    return await this.adHocMotionMutex.runExclusive(async () => {
      // A command that was queued behind a running motion only gets to start
      // once it acquires the mutex if guided mode is still valid -- i.e. the
      // operator is still holding the button (the heartbeat is fresh).
      // Otherwise it would re-arm guided mode itself (via validateGuidedMode
      // below) and start a fresh motion after the button was released -- the
      // arm "moving on its own" -- and because `boxing` bypasses the idle guard
      // above, even after a motion-planning error was cleared. The first /
      // uncontended command is unaffected; the guided-mode watchdog continues
      // to govern motions once they are running.
      if (wasQueuedBehindRunningMotion && !this.isGuidedModeValid()) {
        return 'Interrupted';
      }

      this.validateGuidedMode();

      this.instructionID += 1;
      const { instructionID } = this;

      await this.untilStill(5000);

      if (instructionID !== this.instructionID) {
        return 'Interrupted';
      }

      const allTargets = [
        ...(intermediateGoals ?? []).map((intermediateGoal) => ({
          jointAngles: intermediateGoal,
          motionKind: 'joint',
        })),
        { jointAngles: goal, motionKind: 'joint' },
      ] as [ArmTarget];

      const motion = this.routineContext.doMotionWithROS({
        request: {
          startingJointPositions: this.getJointAngles(),
          targets: allTargets,
        },
        speedProfiles: Array(allTargets.length).fill(
          this.routineContext.limitAdHocSpeeds(options),
        ),
        ignoredCollisionLinkPairs:
          overrideIgnoredPairs ??
          (boxing ? this.boxingIgnoredCollisionPairs : undefined),
        linkCollisionParameters:
          overrideLinkCollisionParams ??
          (boxing ? RoutineRunner.BOXING_LINK_COLLISION_PARAMETERS : undefined),
        useCache: false,
        checkWaypointImmediately: false,
      });

      if (instructionID !== this.instructionID) {
        return 'Interrupted';
      }

      // Create PAUSE3PE monitor for ad-hoc motion
      const pause3PEMonitor = this.routineContext.createPause3PEMonitor(() => {
        motion.emit('cancel');
      });

      // save the motion so it can be cancelled if user
      // interrupts the motion.
      this.adHocMotion = motion;

      this.state = {
        kind: 'RunningAdHocCommand',
        command: {
          kind: 'MoveToJointSpacePoint',
          jointPositions: goal,
        },
        kinematicState: this.state.kinematicState,
        equipmentState: this.state.equipmentState,
        configuration: this.state.configuration,
        failureState: this.state.failureState,
        visualizationState: this.state.visualizationState,
        forceRecoveryMode: this.state.forceRecoveryMode,
        shouldCheckGuidedMode: true,
      };

      // Start monitoring for PAUSE3PE during motion execution
      pause3PEMonitor.start('moveToJointSpacePoint');

      const returnVal = await motion.race('failure', 'complete', 'cancelled');

      // Stop monitoring for PAUSE3PE
      pause3PEMonitor.stop();

      // check the return value of race() and
      // handle the case where the motion plan fails
      if (returnVal === undefined) {
        // `race('failure', 'complete', 'cancelled')` resolves to `undefined`
        // for BOTH a natural 'complete' and a 'cancelled' motion. A cancelled
        // ad-hoc move (operator released the guided-mode hold button, guided
        // mode lapsed, or a 3PE pause fired) must NOT be reported as 'Success':
        // callers treat 'Success' as "reached the goal" and act on it — e.g.
        // the Move page clears the staged target joint angles on success, so an
        // aborted move wiped the operator's target even when the arm was far
        // from it. Mirror the ROS1 path, which returns 'Interrupted' when
        // pre-empted.
        const wasCancelled = motion.hasEmittedAny('cancelled');

        log.debug(
          ns`moveToJointSpacePointROS2`,
          'Setting state.kind to Idle.',
          {
            oldPartialState: {
              kind: this.state.kind,
              failureState: this.state.failureState,
            },
            returnVal,
            wasCancelled,
          },
        );

        // motion completed (or was cancelled)
        this.state = {
          kind: 'Idle',
          forceRecoveryMode: this.state.forceRecoveryMode ?? false,
          cannotStartReasons: this.cannotStartReasons(),
          kinematicState: this.state.kinematicState,
          equipmentState: this.state.equipmentState,
          configuration: this.state.configuration,
          failureState: this.state.failureState,
          visualizationState: this.state.visualizationState,
        };

        this.update();

        return wasCancelled ? 'Interrupted' : 'Success';
      }

      // motion failed
      this.fail(returnVal);

      return returnVal.failure.kind;
    });
  }

  /**
   * @category Commands
   * @throws if the state is not Idle
   * @throws if the robot is braked
   */
  public async moveToJointSpacePointROS1(
    goal: ArmJointPositions,
    options: SpeedProfile,
    intermediateGoals?: ArmJointPositions[],
  ): Promise<CommandResult> {
    await this.checkCollisions();

    if (this.state.kind !== 'Idle') {
      return 'NonIdle';
    }

    if (this.robot.isRobotBraked()) {
      return 'Braked';
    }

    if (this.state.kind === 'Idle' && this.routineContext.isPause3PEActive()) {
      return 'SafetyStopped';
    }

    this.validateGuidedMode();

    this.state = {
      kind: 'RunningAdHocCommand',
      command: {
        kind: 'MoveToJointSpacePoint',
        jointPositions: goal,
      },
      kinematicState: this.state.kinematicState,
      equipmentState: this.state.equipmentState,
      configuration: this.state.configuration,
      failureState: this.state.failureState,
      visualizationState: this.state.visualizationState,
      forceRecoveryMode: this.state.forceRecoveryMode,
      // start outside of guided mode because the motion plan sometimes
      // uses a lot of executor time
      shouldCheckGuidedMode: false,
    };

    this.instructionID += 1;
    const { instructionID } = this;

    await this.untilStill(5000);

    if (instructionID !== this.instructionID) {
      return 'Interrupted';
    }

    log.info(
      ns`moveToJointSpacePoint`,
      'Moving to joint space point with ROS1',
      {
        goal,
        jointAngles: this.getJointAngles(),
      },
    );

    const allTargets = [
      ...(intermediateGoals ?? []).map((intermediateGoal) => ({
        jointAngles: intermediateGoal,
        motionKind: 'joint',
      })),
      { jointAngles: goal, motionKind: 'joint' },
    ];

    const request = {
      startingJointPositions: this.getJointAngles(),
      targets: allTargets as [ArmTarget],
      ...this.routineContext.getMotionPlanOptions(options),
    };

    const motionPlanResponse = this.motionPlanner.planMotion(request);

    let motionPlan: MotionPlan;

    try {
      motionPlan = await motionPlanResponse.complete();
    } catch (error) {
      if (this.instructionID !== instructionID) {
        // pre-empted by something like `stop` or `pause`
        return 'Interrupted';
      }

      this.fail({
        failure: { kind: FailureKind.PlanningFailure },
        failureReason: `Failed to plan motion.  Arm may be colliding with itself or another object.  Message: ${error.message}`,
        error,
      });

      return FailureKind.PlanningFailure;
    }

    if (this.instructionID !== instructionID) {
      // pre-empted by something like `stop` or `pause`
      return 'Interrupted';
    }

    this.state = {
      ...(this.state as Extract<
        typeof this.state,
        { kind: 'RunningAdHocCommand' }
      >),
      shouldCheckGuidedMode: true,
    };

    try {
      const motionResult = await this.robot.playMotionPlan({
        motionPlan,
        maxJointVelocities: request.maxVelocities,
        maxJointAccelerations: request.maxAccelerations,
        maxTooltipVelocity: request.maxTooltipVelocity,
        checkTorqueLimitsRealtime: true,
      });

      log.debug(ns`moveToJointSpacePointROS1`, 'Received motion result', {
        motionResult,
      });

      this.checkMotionResult(motionResult);
    } catch (error) {
      if (this.instructionID !== instructionID) {
        // pre-empted by something like `stop` or `pause`
        return 'Interrupted';
      }

      this.fail({
        failure: { kind: FailureKind.ExecutionFailure },
        failureReason: 'Failed to execute motion',
        error,
      });

      return FailureKind.ExecutionFailure;
    }

    log.debug(ns`moveToJointSpacePointROS1`, 'Setting state.kind to Idle.', {
      oldPartialState: {
        kind: this.state.kind,
        failureState: this.state.failureState,
      },
    });

    this.state = {
      kind: 'Idle',
      forceRecoveryMode: this.state.forceRecoveryMode ?? false,
      cannotStartReasons: this.cannotStartReasons(),
      kinematicState: this.state.kinematicState,
      equipmentState: this.state.equipmentState,
      configuration: this.state.configuration,
      failureState: this.state.failureState,
      visualizationState: this.state.visualizationState,
    };

    this.update();

    return 'Success';
  }

  public async moveToJointSpacePoint(
    goal: ArmJointPositions,
    options: SpeedProfile,
    intermediateGoals?: ArmJointPositions[],
    boxing?: boolean,
    overrideIgnoredPairs?: Array<[string, string]>,
    overrideLinkCollisionParams?: LinkCollisionParameters[],
  ): Promise<CommandResult> {
    if (getFeatureFlagSync('ros2', true)) {
      return this.moveToJointSpacePointROS2(
        goal,
        options,
        intermediateGoals,
        boxing,
        undefined, // useCache
        overrideIgnoredPairs,
        overrideLinkCollisionParams,
      );
    }

    return this.moveToJointSpacePointROS1(goal, options, intermediateGoals);
  }

  public async getJointAnglesForCartesianSpacePose(
    goal: CartesianPose,
    motionKind: MotionKind,
    isJogging: boolean,
    tooltipFrame?: TCPFrame,
  ): Promise<ArmJointPositions | null> {
    return this.routineContext.getJointAnglesForCartesianSpacePose(
      goal,
      motionKind,
      isJogging,
      undefined,
      tooltipFrame,
    );
  }

  public getTooltipTransformForFrame(frame: TCPFrame) {
    return this.routineContext.getTooltipTransformForFrame(frame);
  }

  public async moveToCartesianSpacePoseROS2(
    goal: CartesianPose,
    motionKind: MotionKind,
    speedProfile: SpeedProfile,
    tooltipFrame?: TCPFrame,
  ): Promise<CommandResult> {
    log.info(
      ns`moveToCartesianSpacePose`,
      'Moving to cartesian space pose with ROS2',
      {
        goal,
        motionKind,
        jointAngles: this.getJointAngles(),
      },
    );

    await this.checkCollisions();

    if (this.state.kind !== 'Idle') {
      return 'NonIdle';
    }

    if (this.robot.isRobotBraked()) {
      return 'Braked';
    }

    if (this.routineContext.isPause3PEActive()) {
      return 'SafetyStopped';
    }

    return await this.adHocMotionMutex.runExclusive(async () => {
      this.validateGuidedMode();

      this.instructionID += 1;
      const { instructionID } = this;

      if (instructionID !== this.instructionID) {
        log.info(ns`moveToCartesianSpacePoseROS2`, 'motion interrupted');

        return 'Interrupted';
      }

      // Settle lift height and get value before constructing motion
      const reportedLiftMeters = await this.settleAndGetLiftHeight();

      log.info(ns`jog.preTarget`, 'Pre-jog state', {
        frame: 'world',
        targetOffsetZ: goal.z,
        reportedLiftMeters,
      });

      this.adHocMotion = this.routineContext.doMotionWithROS({
        request: {
          startingJointPositions: this.getJointAngles(),
          targets: [adHocArmTarget(goal, motionKind, tooltipFrame)] as [
            ArmTarget,
          ],
        },
        speedProfiles: [this.routineContext.limitAdHocSpeeds(speedProfile)],
        useCache: false,
        checkWaypointImmediately: false,
        perturbSingularity: true,
      });

      // Create PAUSE3PE monitor for ad-hoc motion
      const pause3PEMonitor = this.routineContext.createPause3PEMonitor(() => {
        this.adHocMotion?.emit('cancel');
      });

      this.state = {
        kind: 'RunningAdHocCommand',
        command: {
          kind: 'MoveToCartesianSpacePose',
          pose: goal,
          motionKind,
        },
        kinematicState: this.state.kinematicState,
        equipmentState: this.state.equipmentState,
        configuration: this.state.configuration,
        failureState: this.state.failureState,
        visualizationState: this.state.visualizationState,
        forceRecoveryMode: this.state.forceRecoveryMode,
        shouldCheckGuidedMode: true,
      };

      // Start monitoring for PAUSE3PE during motion execution
      pause3PEMonitor.start('moveToCartesianSpacePose');

      const returnVal = await this.adHocMotion.race(
        'failure',
        'complete',
        'cancelled',
      );

      // Stop monitoring for PAUSE3PE
      pause3PEMonitor.stop();

      // handle the case where the motion plan fails
      if (returnVal === undefined) {
        // `race('failure', 'complete', 'cancelled')` resolves to `undefined`
        // for BOTH a natural 'complete' and a 'cancelled' motion. A cancelled
        // ad-hoc move (operator released the guided-mode hold button, guided
        // mode lapsed, or a 3PE pause fired) must NOT be reported as 'Success':
        // callers treat 'Success' as "reached the goal" (e.g. the tool-control
        // "Apply changes" button clears the staged target pose on success), so
        // an aborted move would wipe the operator's target. Mirror the ROS1
        // path, which returns 'Interrupted' when pre-empted.
        const wasCancelled = this.adHocMotion.hasEmittedAny('cancelled');

        // motion completed (or was cancelled)
        this.state = {
          kind: 'Idle',
          forceRecoveryMode: this.state.forceRecoveryMode ?? false,
          cannotStartReasons: this.cannotStartReasons(),
          kinematicState: this.state.kinematicState,
          equipmentState: this.state.equipmentState,
          configuration: this.state.configuration,
          failureState: this.state.failureState,
          visualizationState: this.state.visualizationState,
        };

        this.update();

        log.info(
          ns`moveToCartesianSpacePoseROS2`,
          wasCancelled ? 'motion cancelled' : 'motion successful',
        );

        return wasCancelled ? 'Interrupted' : 'Success';
      }

      log.info(ns`moveToCartesianSpacePoseROS2`, 'motion failed');
      // motion failed
      this.fail(returnVal);

      return FailureKind.ExecutionFailure;
    });
  }

  /**
   * @category Commands
   * @throws if the state is not Idle
   * @throws if the robot is braked
   */
  public async moveToCartesianSpacePoseROS1(
    goal: CartesianPose,
    motionKind: MotionKind,
    options: SpeedProfile,
    tooltipFrame?: TCPFrame,
  ): Promise<CommandResult> {
    await this.checkCollisions();

    if (this.state.kind !== 'Idle') {
      return 'NonIdle';
    }

    if (this.robot.isRobotBraked()) {
      return 'Braked';
    }

    this.validateGuidedMode();

    this.state = {
      kind: 'RunningAdHocCommand',
      command: {
        kind: 'MoveToCartesianSpacePose',
        pose: goal,
        motionKind,
      },
      kinematicState: this.state.kinematicState,
      equipmentState: this.state.equipmentState,
      configuration: this.state.configuration,
      failureState: this.state.failureState,
      visualizationState: this.state.visualizationState,
      forceRecoveryMode: this.state.forceRecoveryMode,
      // start outside of guided mode because the motion plan sometimes
      // uses a lot of executor time
      shouldCheckGuidedMode: false,
    };

    this.instructionID += 1;
    const { instructionID } = this;

    await this.untilStill(5000);

    if (instructionID !== this.instructionID) {
      return 'Interrupted';
    }

    let motionPlanResult: ArmAndDeviceMotionPlan<DeviceCommand>;

    const request: MotionPlanRequest = {
      startingJointPositions: this.getJointAngles(),
      targets: [adHocArmTarget(goal, motionKind, tooltipFrame)] as [ArmTarget],
      ...this.routineContext.getMotionPlanOptions(options),
    };

    try {
      motionPlanResult = await new ArmAndDeviceMotionPlanner({
        hybridMotionPlanner: this.hybridMotionPlanner,
        motionPlanner: this.motionPlanner,
        request,
        deviceKinematics: this.routineContext.equipment.getDeviceKinematics(),
        tcpOffsetOption:
          tooltipFrame?.tcpOption ?? this.state.kinematicState.tcpOffsetOption,
        jointLimits: this.state.kinematicState.jointLimits,
        moveDynamicBaseToReachPosition: true,
      }).plan();
    } catch (error) {
      if (this.instructionID !== instructionID) {
        // pre-empted by something like `stop` or `pause`
        return 'Interrupted';
      }

      this.fail({
        failure: { kind: FailureKind.PlanningFailure },
        failureReason: `Failed to plan motion: ${error.message}`,
        error,
      });

      return FailureKind.PlanningFailure;
    }

    if (this.instructionID !== instructionID) {
      // pre-empted by something like `stop` or `pause`
      return 'Interrupted';
    }

    this.state = {
      ...(this.state as Extract<
        typeof this.state,
        { kind: 'RunningAdHocCommand' }
      >),
      shouldCheckGuidedMode: true,
    };

    try {
      for (const deviceCommand of motionPlanResult.deviceCommands) {
        await this.routineContext.actuateDevice(deviceCommand);

        if (this.instructionID !== instructionID) {
          // pre-empted by something like `stop` or `pause`
          return 'Interrupted';
        }
      }

      const motionResult = await this.robot.playMotionPlan({
        motionPlan: motionPlanResult.motionPlan,
        maxJointVelocities: request.maxVelocities,
        maxJointAccelerations: request.maxAccelerations,
        maxTooltipVelocity: request.maxTooltipVelocity,
        checkTorqueLimitsRealtime: true,
      });

      log.debug(ns`moveToCartesianSpacePoseROS1`, 'Received motion result', {
        motionResult,
      });

      this.checkMotionResult(motionResult);
    } catch (error) {
      if (this.instructionID !== instructionID) {
        // pre-empted by something like `stop` or `pause`
        return 'Interrupted';
      }

      this.fail({
        failure: { kind: FailureKind.ExecutionFailure },
        failureReason: 'Failed to execute motion',
        error,
      });

      return FailureKind.ExecutionFailure;
    }

    log.debug(ns`moveToCartesianSpacePose`, 'Setting state.kind to Idle.', {
      oldPartialState: {
        kind: this.state.kind,
        failureState: this.state.failureState,
      },
    });

    this.state = {
      kind: 'Idle',
      forceRecoveryMode: this.state.forceRecoveryMode ?? false,
      kinematicState: this.state.kinematicState,
      equipmentState: this.state.equipmentState,
      configuration: this.state.configuration,
      failureState: this.state.failureState,
      visualizationState: this.state.visualizationState,
      cannotStartReasons: this.cannotStartReasons(),
    };

    this.update();

    return 'Success';
  }

  public async moveToCartesianSpacePose(
    goal: CartesianPose,
    motionKind: MotionKind,
    options: SpeedProfile,
    tooltipFrame?: TCPFrame,
  ): Promise<CommandResult> {
    if (getFeatureFlagSync('ros2', true)) {
      return this.moveToCartesianSpacePoseROS2(
        goal,
        motionKind,
        options,
        tooltipFrame,
      );
    }

    return this.moveToCartesianSpacePoseROS1(
      goal,
      motionKind,
      options,
      tooltipFrame,
    );
  }

  /**
   * Whether a joint jog requested now runs as a recovery jog: during manual
   * recovery, or from Idle with forceRecoveryMode set.
   */
  private isRecoveryJointJog(): boolean {
    return (
      this.state.failureState?.recoveryType === 'ManualRecoveryMode' ||
      (this.state.kind === 'Idle' && this.state.forceRecoveryMode)
    );
  }

  /**
   * Shared precondition checks for joint-jog methods (ROS2 and TC paths).
   *
   * In ManualRecoveryMode (or forceRecoveryMode), verifies the target joint
   * is not braked and enables recovery on the robot.  Otherwise requires
   * Idle state, no collisions, no brakes, and no active 3PE pause.
   */
  private async validateJointJogPreconditions(
    jointNumber: JointNumber,
  ): Promise<void> {
    if (this.isRecoveryJointJog()) {
      if (this.state.kinematicState.jointBrakesEngaged[jointNumber]) {
        throw new Error(`Cannot move joint ${jointNumber} when it is braked`);
      }

      if (this.routineContext.isPause3PEActive()) {
        throw new Error(
          'Cannot move robot: 3PE pause is active (tablet case connected and 3PE switch not in run position)',
        );
      }

      await this.robot.setManualRecoveryMode(true);
    } else if (this.state.kind === 'Idle') {
      await this.checkCollisions();

      if (this.robot.isRobotBraked()) {
        throw new Error('Cannot move robot while robot is braked');
      }

      if (this.routineContext.isPause3PEActive()) {
        throw new Error(
          'Cannot move robot: 3PE pause is active (tablet case connected and 3PE switch not in run position)',
        );
      }
    } else {
      throw new Error(`Cannot move robot while in state ${this.state.kind}`);
    }
  }

  /**
   * @category Commands
   * @throws if the state is not Idle
   * @throws if the robot is braked
   */
  public async moveJointRelativeROS2(
    jointNumber: JointNumber,
    options: SpeedProfile,
  ): Promise<void> {
    // Taken before any await, as moveJointRelativeTC does: stop() bumps
    // instructionID, so a release that lands while this jog awaits its
    // preconditions or waits on adHocMotionMutex shows up as a change. Without
    // this check, validateGuidedMode below would re-arm the deadman for a
    // button that was already released.
    const instructionIDAtRequest = this.instructionID;

    await this.validateJointJogPreconditions(jointNumber);

    await this.adHocMotionMutex.runExclusive(async () => {
      if (instructionIDAtRequest !== this.instructionID) {
        log.info(
          ns`moveJointRelativeROS2.stoppedBeforeStart`,
          'Dropping a jog that was stopped before it started',
          { jointNumber },
        );

        return;
      }

      this.instructionID += 1;
      const { instructionID } = this;
      await this.untilStill(5000);

      if (instructionID !== this.instructionID) {
        return;
      }

      const maxJointVelocity = options.maxJointSpeeds[jointNumber];

      this.validateGuidedMode();

      const offset = six(0);
      // set offset to positive or negative for the correct joint
      offset[jointNumber] = maxJointVelocity > 0 ? 1 : -1;

      // ROS expects positive maxJointSpeeds
      const newOptions = {
        ...options,
        maxJointSpeeds: options.maxJointSpeeds.map((speed) =>
          Math.abs(speed),
        ) as ArmJointVelocities,
      };

      const speedProfile = this.routineContext.limitAdHocSpeeds(newOptions);

      const jogActionRequest: JoggingActionRequest = {
        jogType: 'joint',
        displacements: offset,
        speedProfile,
        startJointState: this.getJointAngles(),
        manualRecoveryMode:
          this.state.failureState?.recoveryType === 'ManualRecoveryMode' ||
          this.state.forceRecoveryMode,
        payloadMass: this.state.configuration.payload.mass,
      };

      await this.runRosJogMotion(
        {
          kind: 'MoveJointRelative',
          jointNumber,
          maxJointVelocity:
            Math.sign(maxJointVelocity) *
            speedProfile.maxJointSpeeds[jointNumber],
        },
        jogActionRequest,
      );
    });
  }

  /** Call from inside {@link adHocMotionMutex}. */
  private async runRosJogMotion(
    command: AdHocCommand,
    jogActionRequest: JoggingActionRequest,
  ): Promise<void> {
    const jogMotionWithROS = new JogMotionWithROS(this.routineContext);
    this.adHocMotion = jogMotionWithROS.getEvents();

    this.state = {
      kind: 'RunningAdHocCommand',
      command,
      kinematicState: this.state.kinematicState,
      equipmentState: this.state.equipmentState,
      configuration: this.state.configuration,
      failureState: this.state.failureState,
      visualizationState: this.state.visualizationState,
      forceRecoveryMode: this.state.forceRecoveryMode,
      shouldCheckGuidedMode: true,
    };

    jogMotionWithROS.start(jogActionRequest);

    const returnVal = await jogMotionWithROS
      .getEvents()
      .race('complete', 'failure', 'cancelled');

    // undefined means the jog completed or was cancelled
    if (returnVal === undefined) {
      this.state = {
        kind: 'Idle',
        forceRecoveryMode: this.state.forceRecoveryMode ?? false,
        cannotStartReasons: this.cannotStartReasons(),
        kinematicState: this.state.kinematicState,
        equipmentState: this.state.equipmentState,
        configuration: this.state.configuration,
        failureState: this.state.failureState,
        visualizationState: this.state.visualizationState,
      };

      this.update();

      if (jogMotionWithROS.wasCollisionCheckedPlanRejected()) {
        await this.recheckCollisionsAfterPlannerRejection(
          jogActionRequest.jogType,
        );
      }
    } else {
      this.fail(returnVal);
    }
  }

  /**
   * @category Commands
   * @throws if the state is not Idle
   * @throws if the robot is braked
   */
  public async moveJointRelativeROS1(
    jointNumber: JointNumber,
    options: SpeedProfile,
  ): Promise<void> {
    if (
      this.state.failureState?.recoveryType === 'ManualRecoveryMode' ||
      this.state.forceRecoveryMode
    ) {
      if (this.state.kinematicState.jointBrakesEngaged[jointNumber]) {
        throw new Error(`Cannot move joint ${jointNumber} when it is braked`);
      }

      // `robot.manualRecoveryMode` will be reset when the state eventually returns to Idle
      await this.robot.setManualRecoveryMode(true);
    } else if (this.state.kind === 'Idle') {
      await this.checkCollisions();

      if (this.robot.isRobotBraked()) {
        throw new Error('Cannot move robot while robot is braked');
      }

      if (this.routineContext.isPause3PEActive()) {
        throw new Error(
          'Cannot move robot: 3PE pause is active (tablet case connected and 3PE switch not in run position)',
        );
      }
    } else {
      throw new Error(`Cannot move robot while in state ${this.state.kind}`);
    }

    this.instructionID += 1;
    const { instructionID } = this;
    await this.untilStill(5000);

    if (instructionID !== this.instructionID) {
      return;
    }

    const maxJointVelocity = options.maxJointSpeeds[jointNumber];

    this.validateGuidedMode();

    this.state = {
      kind: 'RunningAdHocCommand',
      command: {
        kind: 'MoveJointRelative',
        jointNumber,
        maxJointVelocity,
      },
      kinematicState: this.state.kinematicState,
      equipmentState: this.state.equipmentState,
      configuration: this.state.configuration,
      failureState: this.state.failureState,
      visualizationState: this.state.visualizationState,
      forceRecoveryMode: this.state.forceRecoveryMode,
      // start outside of guided mode because the motion plan sometimes
      // uses a lot of executor time
      shouldCheckGuidedMode: false,
    };

    const lineStartPosition = this.getJointAngles();

    const maxVelocities: ArmJointVelocities = six(0);
    const offset = six(0);
    // set offset to positive or negative for the correct joint
    offset[jointNumber] = maxJointVelocity > 0 ? 1 : -1;

    maxVelocities[jointNumber] = Math.abs(maxJointVelocity);

    let motionPlan: MotionPlan;

    const request = {
      startingJointPositions: this.getJointAngles(),
      lineStartPosition,
      offset,
      ...this.routineContext.getMotionPlanOptions(options),
    };

    try {
      const motionPlanResponse =
        this.motionPlanner.planRelativeJointMotion(request);

      motionPlan = await motionPlanResponse.complete();
    } catch (error) {
      this.restoreFailureStateKind();

      if (this.instructionID !== instructionID) {
        // pre-empted by something like `stop` or `pause`
        return;
      }

      this.fail({
        failure: { kind: FailureKind.PlanningFailure },
        failureReason: `Failed to plan joint motion: ${error.message}`,
        error,
      });

      return;
    }

    try {
      if (this.instructionID !== instructionID) {
        // pre-empted by something like `stop` or `pause`
        return;
      }

      this.state = {
        ...(this.state as Extract<
          typeof this.state,
          { kind: 'RunningAdHocCommand' }
        >),
        shouldCheckGuidedMode: true,
      };

      const motionResult = await this.robot.playMotionPlan({
        motionPlan,
        maxJointVelocities: request.maxVelocities,
        maxJointAccelerations: request.maxAccelerations,
        maxTooltipVelocity: request.maxTooltipVelocity,
        checkTorqueLimitsRealtime: true,
      });

      log.debug(ns`moveJointRelativeROS1`, 'Received motion result', {
        motionResult,
      });

      this.checkMotionResult(motionResult);
    } catch (error) {
      if (this.instructionID !== instructionID) {
        // pre-empted by something like `stop` or `pause`
        return;
      }

      this.fail({
        failure: { kind: FailureKind.ExecutionFailure },
        failureReason: 'Failed to execute joint motion',
        error,
      });
    } finally {
      this.restoreFailureStateKind();

      await this.untilStill(5000);
    }
  }

  /**
   * Joint jog via TrajectoryController SpeedJ primitive.
   * Same preconditions as moveJointRelativeROS2 but uses TC instead of ROS2.
   * @category Commands
   */
  public async moveJointRelativeTC(
    jointNumber: JointNumber,
    options: SpeedProfile,
  ): Promise<void> {
    const { instructionID } = this;

    await this.validateJointJogPreconditions(jointNumber);

    await this.runTcJogExclusive(async () => {
      await this.untilStill(5000);

      if (instructionID !== this.instructionID) {
        return;
      }

      const requestedVelocity = options.maxJointSpeeds[jointNumber];

      this.validateGuidedMode();

      const limitedOptions = this.routineContext.limitSpeeds(options);

      const direction = computeJogDirection(jointNumber, {
        ...limitedOptions,
        maxJointSpeeds: limitedOptions.maxJointSpeeds.map((_speed, i) =>
          i === jointNumber ? requestedVelocity : 0,
        ) as ArmJointVelocities,
      });

      await this.runTcJogMotion(
        {
          kind: 'MoveJointRelative',
          jointNumber,
          maxJointVelocity: requestedVelocity,
        },
        { speedJ: { direction: { entries: direction } } },
        {
          manualRecoveryMode:
            this.state.failureState?.recoveryType === 'ManualRecoveryMode',
        },
      );
    });
  }

  // Refuses rather than queues: a queued jog would start after the operator let go.
  private async runTcJogExclusive(jog: () => Promise<void>): Promise<void> {
    try {
      await tryAcquire(this.adHocMotionMutex).runExclusive(jog);
    } catch (error) {
      if (error === E_ALREADY_LOCKED) {
        throw new Error('Cannot jog until the previous jog has finished');
      }

      throw error;
    }
  }

  private async runTcJogMotion(
    command: AdHocCommand,
    primitive: Primitive,
    options: { manualRecoveryMode?: boolean } = {},
  ): Promise<void> {
    const jogMotionWithTC = new JogMotionWithTC(this.routineContext);
    this.adHocMotion = jogMotionWithTC.getEvents();
    this.tcJogInFlight = true;

    this.state = {
      kind: 'RunningAdHocCommand',
      command,
      kinematicState: this.state.kinematicState,
      equipmentState: this.state.equipmentState,
      configuration: this.state.configuration,
      failureState: this.state.failureState,
      visualizationState: this.state.visualizationState,
      forceRecoveryMode: false,
      shouldCheckGuidedMode: true,
    };

    jogMotionWithTC.start(primitive, options);

    const returnVal = await jogMotionWithTC
      .getEvents()
      .race('complete', 'failure', 'cancelled');

    this.tcJogInFlight = false;

    if (returnVal === undefined) {
      this.state = {
        kind: 'Idle',
        forceRecoveryMode: false,
        cannotStartReasons: this.cannotStartReasons(),
        kinematicState: this.state.kinematicState,
        equipmentState: this.state.equipmentState,
        configuration: this.state.configuration,
        failureState: this.state.failureState,
        visualizationState: this.state.visualizationState,
      };

      this.update();
    } else {
      this.fail(returnVal);
    }
  }

  /**
   * @category Commands
   * @throws if the state is not Idle
   * @throws if the robot is braked
   */
  public async moveJointRelative(
    jointNumber: JointNumber,
    options: SpeedProfile,
  ): Promise<void> {
    // Tiered TC flags: useTrajectoryController is the master (the TC module
    // and connector exist), onlinePlannerJogging routes jogging
    // through it. Both default on; master-on with jogging-off runs the TC
    // for streaming only while jogs stay on the legacy path.
    //
    // Recovery jogs stay on ROS2 even with both flags on. The TC takes only a
    // jog direction and composes the speed from its own jog config and the
    // speed slider, so the caller's recovery profile (0.1 rad/s,
    // MANUAL_RECOVERY_SPEED_PROFILE) would be dropped and the joint would jog
    // at normal speed. ROS2 applies the profile and tells arm-control-bot the
    // jog is a recovery. Route recovery to the TC once it accepts a recovery
    // speed ceiling.
    if (!this.isRecoveryJointJog() && isOnlineJoggingActiveForThisModel()) {
      return this.moveJointRelativeTC(jointNumber, options);
    }

    if (getFeatureFlagSync('ros2', true)) {
      return this.moveJointRelativeROS2(jointNumber, options);
    }

    return this.moveJointRelativeROS1(jointNumber, options);
  }

  /**
   * Shared precondition checks for tooltip/tool-relative jog methods.
   *
   * Requires Idle state, no brakes engaged, and no active 3PE pause.
   */
  private validateToolJogPreconditions(): void {
    if (this.state.kind !== 'Idle') {
      throw new Error(`Cannot move robot while in state ${this.state.kind}`);
    }

    if (this.robot.isRobotBraked()) {
      throw new Error('Cannot move robot while robot is braked');
    }

    if (this.routineContext.isPause3PEActive()) {
      throw new Error(
        'Cannot move robot: 3PE pause is active (tablet case connected and 3PE switch not in run position)',
      );
    }
  }

  /**
   * The Active Tooltip's `gripperID` for the current motion frame — see
   * `RoutineContext.activeTooltipGripperIDForFrame`, which the displayed
   * tooltip pose resolves through as well.
   */
  private activeTooltipGripperIDForCurrentFrame(): string | undefined {
    return this.routineContext.activeTooltipGripperIDForFrame(
      this.state.kinematicState.tcpOffsetOption,
    );
  }

  /**
   * Tool jog through the trajectory controller (SpeedL primitive).
   *
   * Follows the same pattern as {@link moveJointRelativeTC}. The packet
   * receiver routes here when `useTrajectoryController` is on. This
   * method takes only the `speedL` fields. The ros2 offset is
   * pre-rotated and cannot be turned back into SpeedL frames.
   *
   * The command carries no speed. The trajectory controller composes
   * the jog speed at enqueue time from its jog config, the speed
   * slider, and the active safety limits.
   *
   * @category Commands
   * @throws if the state is not Idle
   * @throws if the robot is braked
   */
  public async speedLJog(jog: SpeedLJog): Promise<void> {
    const { instructionID } = this;

    this.validateToolJogPreconditions();

    await this.runTcJogExclusive(async () => {
      await this.checkCollisions();
      // TODO: remove once we confirm the TC handles primitive transitions
      // without requiring stillness. Kept for parity with the ROS2 path.
      await this.untilStill(5000);

      if (instructionID !== this.instructionID) {
        return;
      }

      this.validateGuidedMode();

      validateSpeedLDirection(jog.direction);

      const frames = composeCartesianFrames(
        jog.velocityExpressedIn,
        this.routineContext.activeTooltipTransform(),
      );

      await this.runTcJogMotion(
        {
          kind: 'SpeedLJog',
          direction: jog.direction,
          velocityExpressedIn: jog.velocityExpressedIn,
        },
        { speedL: { direction: jog.direction, ...frames } },
      );
    });
  }

  /**
   * @category Commands
   * @throws if the state is not Idle
   * @throws if the robot is braked
   */
  public async moveToolRelativeROS2(
    frame: FrameOfReference,
    offset: CartesianPose,
    options: SpeedProfile,
  ) {
    this.validateToolJogPreconditions();

    await this.adHocMotionMutex.runExclusive(async () => {
      await this.checkCollisions();
      this.instructionID += 1;
      const { instructionID } = this;
      await this.untilStill(5000);

      if (instructionID !== this.instructionID) {
        return;
      }

      this.validateGuidedMode();

      const { tcpLink, transformFromTool0 } = resolveTCPFrame(
        this.routineContext.allEquipmentItems,
        {
          tcpOption: this.state.kinematicState.tcpOffsetOption,
          gripperID: this.activeTooltipGripperIDForCurrentFrame(),
        },
      );

      const tcpOrientationFromTool0 = {
        i: transformFromTool0.i,
        j: transformFromTool0.j,
        k: transformFromTool0.k,
        w: transformFromTool0.w,
      };

      // Settle lift height and get value before constructing jog
      const reportedLiftMeters = await this.settleAndGetLiftHeight();

      log.info(ns`jog.preTarget`, 'Pre-jog state', {
        frame,
        targetOffsetZ: offset.z,
        reportedLiftMeters,
      });

      const speedProfile = this.routineContext.limitAdHocSpeeds(options);

      const jogActionRequest: JoggingActionRequest = {
        jogType: 'cartesian',
        pose: offset,
        frame,
        speedProfile,
        startJointState: this.getJointAngles(),
        manualRecoveryMode: false,
        payloadMass: this.state.configuration.payload.mass,
        tcpLink,
        tcpOrientationFromTool0,
      };

      await this.runRosJogMotion(
        {
          kind: 'MoveToolRelative',
          frame,
          offset,
          maxTooltipVelocity: speedProfile.maxTooltipSpeed,
        },
        jogActionRequest,
      );
    });
  }

  /**
   * @category Commands
   * @throws if the state is not Idle
   * @throws if the robot is braked
   */
  public async moveToolRelativeROS1(
    frame: FrameOfReference,
    offset: CartesianPose,
    options: SpeedProfile,
  ) {
    await this.checkCollisions();

    if (this.state.kind !== 'Idle') {
      throw new Error(`Cannot move robot while in state ${this.state.kind}`);
    }

    if (this.robot.isRobotBraked()) {
      throw new Error('Cannot move robot while robot is braked');
    }

    if (this.routineContext.isPause3PEActive()) {
      throw new Error(
        'Cannot move robot: 3PE pause is active (tablet case connected and 3PE switch not in run position)',
      );
    }

    this.instructionID += 1;
    const { instructionID } = this;
    await this.untilStill(5000);

    if (instructionID !== this.instructionID) {
      return;
    }

    this.validateGuidedMode();

    this.state = {
      kind: 'RunningAdHocCommand',
      command: {
        kind: 'MoveToolRelative',
        frame,
        offset,
        maxTooltipVelocity: options.maxTooltipSpeed,
      },
      kinematicState: this.state.kinematicState,
      equipmentState: this.state.equipmentState,
      configuration: this.state.configuration,
      failureState: this.state.failureState,
      visualizationState: this.state.visualizationState,
      forceRecoveryMode: this.state.forceRecoveryMode,
      // start outside of guided mode because the motion plan sometimes
      // uses a lot of executor time
      shouldCheckGuidedMode: false,
    };

    const lineStartPosition = this.getJointAngles();

    let motionPlan: MotionPlan;

    const request = {
      startingJointPositions: this.getJointAngles(),
      lineStartPosition,
      frame,
      offset,
      ...this.routineContext.getMotionPlanOptions(options),
    };

    try {
      const motionPlanResponse =
        this.motionPlanner.planRelativeCartesianMotion(request);

      motionPlan = await motionPlanResponse.complete();
    } catch (error) {
      if (this.instructionID !== instructionID) {
        // pre-empted by something like `stop` or `pause`
        return;
      }

      this.fail({
        failure: { kind: FailureKind.PlanningFailure },
        failureReason: `Failed to plan tooltip motion: ${error.message}`,
        error,
      });

      return;
    }

    try {
      if (this.instructionID !== instructionID) {
        // pre-empted by something like `stop` or `pause`
        return;
      }

      this.state = {
        ...(this.state as Extract<
          typeof this.state,
          { kind: 'RunningAdHocCommand' }
        >),
        shouldCheckGuidedMode: true,
      };

      const motionResult = await this.robot.playMotionPlan({
        motionPlan,
        maxJointVelocities: request.maxVelocities,
        maxJointAccelerations: request.maxAccelerations,
        maxTooltipVelocity: request.maxTooltipVelocity,
        checkTorqueLimitsRealtime: true,
      });

      log.debug(ns`moveToolRelativeROS1`, 'Received motion result', {
        motionResult,
      });

      this.checkMotionResult(motionResult);
    } catch (error) {
      if (this.instructionID !== instructionID) {
        // pre-empted by something like `stop` or `pause`
        return;
      }

      this.fail({
        failure: { kind: FailureKind.ExecutionFailure },
        failureReason: `Failed to enact tooltip motion: ${error.message}`,
        error,
      });
    }
  }

  /**
   * The ros2/ros1 jog path. It takes only the ros2 format. When
   * `useTrajectoryController` is on, the jog button sends the
   * {@link speedLJog} command instead.
   *
   * @category Commands
   * @throws if the state is not Idle
   * @throws if the robot is braked
   */
  public async moveToolRelative(
    frame: FrameOfReference,
    offset: CartesianPose,
    options: SpeedProfile,
  ) {
    if (getFeatureFlagSync('ros2', true)) {
      return this.moveToolRelativeROS2(frame, offset, options);
    }

    return this.moveToolRelativeROS1(frame, offset, options);
  }

  public async executeDeviceCommand(
    command: DeviceCommand,
  ): Promise<DeviceCommandResult> {
    return this.routineContext.equipment.executeCommand(command);
  }

  public async setLEDPattern(
    pattern: LEDColorPattern,
    durationMs: number,
  ): Promise<void> {
    log.info(ns`setLEDPattern`, 'Beginning of Setting LED pattern', {
      pattern,
      durationMs,
    });

    try {
      return this.robot.changeRobotLEDEphemeral(pattern, durationMs);
    } catch (error) {
      log.error(ns`setLEDPattern`, 'Failed to set LED pattern', { error });
      throw error;
    }
  }

  /**
   * @category Commands
   *
   * Actuate a device.
   *
   * @param gripperCommand   The command to give the device.
   * @param payload         The payload to expect after the command completes.
   *
   * @resolves When the device command is complete.
   * @throws   If the state is not Idle.
   */
  public async actuateDevice({
    command: gripperCommand,
    payload,
    retry,
  }: ActuateDeviceArgs): Promise<CommandResult> {
    if (this.state.kind !== 'Idle') {
      return 'NonIdle';
    }

    this.instructionID += 1;
    const { instructionID } = this;

    this.validateGuidedMode();

    const { equipmentState, kinematicState } = this.state;

    if (
      gripperCommand.kind === 'OnRobot2FG7Command' ||
      gripperCommand.kind === 'OnRobot2FG14Command'
    ) {
      const { gripKind } = gripperCommand;

      const gripperState = equipmentState.find((state) => {
        const gripperKind =
          gripperCommand.kind === 'OnRobot2FG7Command'
            ? 'OnRobot2FG7'
            : 'OnRobot2FG14';

        return state.state.kind === gripperKind;
      })?.state as OnRobot2FG7State | OnRobot2FG14State | null;

      if (gripperState != null) {
        gripperState.gripKind = gripKind;
      } else {
        const failureReason = `Failed to actuate gripper: valid gripper for ${gripperCommand.kind} not found`;

        this.fail({
          failure: {
            kind: FailureKind.GripperFailure,
            message: failureReason,
          },
          failureReason,
        });

        return FailureKind.GripperFailure;
      }
    }

    this.state = {
      kind: 'RunningAdHocCommand',
      command: {
        kind: 'ActuateGripper',
        gripperCommand,
      },
      kinematicState,
      equipmentState,
      configuration: this.state.configuration,
      failureState: this.state.failureState,
      visualizationState: this.state.visualizationState,
      forceRecoveryMode: this.state.forceRecoveryMode,
      shouldCheckGuidedMode: true,
    };

    try {
      await this.routineContext.actuateDevice(gripperCommand, { retry });

      if (payload) {
        await this.routineContext.setPayload(payload);
      }
    } catch (error) {
      if (error instanceof ActionCancelledError) {
        return 'Interrupted';
      }

      if (this.instructionID !== instructionID) {
        // pre-empted by something like `stop` or `pause`
        return 'Interrupted';
      }

      const failureReason = `Failed to actuate gripper: ${error.message}`;

      this.fail({
        failure: {
          kind: FailureKind.GripperFailure,
          message: failureReason,
        },
        failureReason,
        error,
      });

      return FailureKind.GripperFailure;
    }

    log.debug(
      ns`actuateDevice`,
      'Setting state.kind to Idle after actuateDevice success',
      {
        oldPartialState: {
          kind: this.state.kind,
          failureState: this.state.failureState,
        },
      },
    );

    this.state = {
      kind: 'Idle',
      forceRecoveryMode: this.state.forceRecoveryMode ?? false,
      kinematicState,
      equipmentState,
      configuration: this.state.configuration,
      failureState: this.state.failureState,
      visualizationState: this.state.visualizationState,
      cannotStartReasons: this.cannotStartReasons(),
    };

    this.update();

    return 'Success';
  }

  /**
   * @category Commands
   *
   * Command the IO board's outputs to set a port to high or low.
   */
  public setOutputIO(
    changes: Array<{ label: AnyOutputPortID; level: IOLevel }>,
  ): Promise<void> {
    return this.robot.setOutputIO(changes);
  }

  /**
   * @category Commands
   *
   * Recover from a state of `Failure`. Action depends on the type of failure (`failureKind`)
   * but the interface is always that of an Ad-Hoc motion.
   *
   * - PlanningFailure
   *  - Clears the error; `recover()` indicates that the frontend/user has acknowledged the
   *    planning failure.
   * - ExecutionFailure
   *  - Clears the error; `recover()` indicates that the frontend/user has acknowledged the
   *    execution failure.
   * - OutOfJointLimitsFailure
   *  - Command/ad-hoc motion to recover from out of joint limits
   * - CollisionFailure & MotionPlannerFailureStartPositionCollision
   *  - Command/ad-hoc motion to recover from a collision
   * - MotionPlannerFailure
   *  - Clears the error; `recover()` indicates that the frontend/user has acknowledged the
   *    planning failure.
   * - InvalidRoutineLoadedFailure
   *  - Clears the error; `recover()` indicates that the frontend/user has acknowledged the
   *    planning failure.
   *  - Unloads the currently loaded routine
   * - InternalFailure
   *  - Throws an error (internal errors are unrecoverable)
   *
   * @param initializedByWristButton  set true if recovery attempted with wrist button
   *
   * @throws if the robot is not in a state of Failure
   * @throws if the state is not Failure
   * @throws if the robot is braked
   */
  public async recover(options?: {
    initializedByWristButton?: boolean;
    targetJointAngles?: ArmJointPositions;
  }): Promise<CommandResult> {
    if (this.state.kind !== 'Failure') {
      log.info(
        ns`recover`,
        'Tried to recover in non-failure state. Exiting with error.',
        {
          partialState: {
            kind: this.state.kind,
            failureState: this.state.failureState,
          },
        },
      );

      throw new Error(
        `Tried to recover when in a(n) "${this.state.kind}" state`,
      );
    }

    if (
      options?.initializedByWristButton &&
      !this.state.failureState.isRecoverableWithWristButton
    ) {
      throw new Error(
        `Wrist button recovery unavailable when in a(n) "${this.state.failureState.failure}" state`,
      );
    }

    const failureKind = this.state.failureState.failure.kind;

    let success = false;
    let recoverByMoving = false;

    if (this.state.failureState.recoveryType === 'ManualRecoveryMode') {
      // Special case for exiting manual recovery mode.
      // This "recovery" is triggered when the user has resolved all collision torque thresholds.
      // This is not a true recovery, but a way to exit manual recovery mode.
      // If the arm is still in "soft collision", this will be detected by the checkCollisions() call.
      await this.robot.setManualRecoveryMode(false);
      success = true;
      recoverByMoving = true;
    } else {
      switch (failureKind) {
        case FailureKind.OutOfJointLimitsFailure: {
          recoverByMoving = true;

          success = await this.recoverByMoving({
            ...options,
            speedProfile: RECOVERY_SPEED_PROFILE,
          });

          break;
        }

        case FailureKind.MotionPlannerFailureStartPositionCollision:
        case FailureKind.CollisionFailure: {
          recoverByMoving = true;

          success = await this.recoverByMoving({
            ...options,
            speedProfile: RECOVERY_SPEED_PROFILE,
          });

          break;
        }

        case FailureKind.TorqueCollisionFailure: {
          success = await this.recoverByMoving({
            ...options,
            speedProfile: RECOVERY_SPEED_PROFILE,
          });

          // if the next torque collision is within 5 seconds to go into manual recovery mode
          this.lastTorqueCollisionRecoveryAt = Date.now();
          // always mark successful so we pick up the next torque collision failure
          success = true;
          break;
        }

        case FailureKind.SafetyWatchdogViolation: {
          const isTorqueViolation =
            this.state.failureState.failure.kind ===
              FailureKind.SafetyWatchdogViolation &&
            this.state.failureState.failure.event?.safetyWatchdogViolation
              .violationType === 'Torque';

          // Torque safety watchdog violations need the same recovery motion
          // as torque collisions — the arm must physically move away.
          // Position and velocity violations resolve when the arm stops,
          // so simple recovery suffices.
          if (isTorqueViolation) {
            await this.recoverByMoving({
              ...options,
              speedProfile: RECOVERY_SPEED_PROFILE,
            });

            this.lastTorqueCollisionRecoveryAt = Date.now();
          }

          success = true;

          break;
        }

        case FailureKind.InBoxingPositionFailure: {
          if (!options?.targetJointAngles) {
            // user can skip moving to 'start' position to get going quicker
            success = true;
          } else {
            recoverByMoving = true;

            success = await this.recoverByMoving({
              ...options,
              speedProfile: UNBOXING_SPEED_PROFILE,
              ignoredCollisionLinkPairs: this.boxingIgnoredCollisionPairs,
              linkCollisionParameters:
                RoutineRunner.BOXING_LINK_COLLISION_PARAMETERS,
            });
          }

          break;
        }

        case FailureKind.InvalidRoutineLoadedFailure: {
          // remove routine from state
          this.routineContext.clearLoadedRoutine();
          success = true;
          break;
        }

        case FailureKind.InternalFailure: {
          throw new Error(
            'Tried to recover when an internal, unrecoverable error has occurred. These are errors that may require specific Standard Bots employee intervention.',
          );
        }

        case FailureKind.PoweredOff: {
          // The safety relays read as an E-Stop for a few seconds while power
          // is being cut, even for a deliberate power-off — recovery has to
          // wait for the relays to settle, but there are no physical E-Stops
          // for the operator to release.
          if (this.robot.getSafeguardState() === 'eStop') {
            throw new Error('Arm is still powering off');
          }

          log.info(ns`poweredOff.reset`, 'Resetting PoweredOff');

          const { equipmentItems } = this.routineContext.equipment;

          log.info(ns`poweredOff.reset`, 'Restarting all equipment.', {
            equipmentItems,
          });

          await this.routineContext.equipment.resetEquipmentList(
            equipmentItems,
            true,
          );

          success = true;

          break;
        }

        case FailureKind.EStopTriggered: {
          if (this.robot.getSafeguardState() === 'eStop') {
            throw new Error('E-Stop must be reset to recover');
          }

          log.info(ns`estop.reset`, 'Resetting EStopTriggered');

          const { equipmentItems } = this.routineContext.equipment;

          log.info(ns`estop.reset`, 'Restarting all equpment.', {
            equipmentItems,
          });

          await this.routineContext.equipment.resetEquipmentList(
            equipmentItems,
            true,
          );

          success = true;

          break;
        }

        default: {
          log.info(
            ns`reset.unknown`,
            `Resetting ${this.state.failureState.failure.kind}`,
            { failureState: this.state.failureState },
          );

          success = true;
          break;
        }
      }
    }

    if (success) {
      if (recoverByMoving) {
        const isColliding = await this.checkCollisions();
        const isOutOfJointLimits = this.isOutOfJointLimits();

        if (isColliding) {
          return FailureKind.CollisionFailure;
        }

        if (isOutOfJointLimits) {
          return FailureKind.OutOfJointLimitsFailure;
        }

        log.info(ns`jointAngles.recovered`, 'Recovered to joint angles', {
          jointAngles: this.robot.getJointAngles(),
        });
      }

      log.debug(ns`recover`, 'Setting state.kind to Idle.', {
        oldPartialState: {
          kind: this.state.kind,
          failureState: this.state.failureState,
        },
      });

      await this.transitionToIdleFromFailure();
    }

    return 'Success';
  }

  /**
   * Shared Failure → Idle transition used by recover() (after any
   * successful recovery branch) and enableArmPower() (after the operator
   * clears a power-side failure at the hardware level). Keeps both paths
   * symmetric so trajectory-controller error clearing, collision-cache
   * invalidation, and wrist-button release tracking don't drift.
   */
  private async transitionToIdleFromFailure(): Promise<void> {
    // Reset the wrist button release flag since we've successfully recovered
    this.hasWristButtonBeenReleasedSinceFailure = true;

    if (
      getFeatureFlagSync('useTrajectoryController', false) &&
      isOnlinePlannerAllowedForThisModel()
    ) {
      try {
        await this.routineContext.trajectoryControllerConnector?.ClearError(
          {},
          TC_CLEAR_ERROR_TIMEOUT_MS,
        );
      } catch (err) {
        log.warn(
          ns`recover.clearError`,
          'Trajectory controller ClearError failed; recovery proceeding',
          { err },
        );
      }
    }

    // Clear any latched teleop-bot error too. Without this, a prior teleop
    // session's error (e.g. an engage-guard abort) survives in teleop-bot
    // state — StopTeleop doesn't clear it — and the next ephemeral Loop{Teleop}
    // step reads it via getState (before the operator's StartTeleop can clear
    // it) and re-fails with the identical reason. Recover is the explicit
    // "operator acknowledged" signal, so clearing here is race-free vs. the
    // abort that surfaced the error. Mirrors the trajectory-controller clear.
    try {
      await this.routineContext.teleopBotConnector?.ClearErrors(
        {},
        TC_CLEAR_ERROR_TIMEOUT_MS,
      );
    } catch (err) {
      log.warn(
        ns`recover.clearError`,
        'Teleop bot ClearErrors failed; recovery proceeding',
        { err },
      );
    }

    // Invalidate the collision cache on the Failure→Idle transition. The
    // cache short-circuits checkCollisions() for an unchanged FK request and
    // only returns the cached boolean — it does not re-invoke fail(). If the
    // user acknowledges a non-moving failure (e.g. ExecutionFailure after a
    // failed recovery motion) while the arm is still in a colliding pose,
    // the next loop tick would otherwise hit the cache and leave the state
    // Idle despite the arm being in collision. Force a fresh FK on the next
    // tick so CollisionFailure is re-raised.
    this.resetCollisionCheckCache();

    this.clearExternalControlFaults();

    this.state = {
      kind: 'Idle',
      forceRecoveryMode: this.state.forceRecoveryMode ?? false,
      kinematicState: this.state.kinematicState,
      equipmentState: this.state.equipmentState,
      configuration: this.state.configuration,
      failureState: null,
      visualizationState: this.state.visualizationState,
      cannotStartReasons: this.cannotStartReasons(),
    };
  }

  private checkMotionResult(result: MotionResult) {
    switch (result.kind) {
      case 'torqueLimitExceeded':
        this.fail({
          failure: { kind: FailureKind.ExecutionFailure },
          failureReason: `Torque limited exceeded: ${result.torqueLimitExceeded.message}`,
        });

        throw new Error(
          `Torque limited exceeded: ${result.torqueLimitExceeded.message}`,
        );
      case 'collision':
        // No need to fail here, as the collision is already raised. Just need to make sure that catch handler checks the instructionID.
        throw new Error('Motion stopped due to collision');
    }
  }

  private async initializeRecoveringState(
    initializedByWristButton: boolean = false,
  ): Promise<boolean> {
    const { instructionID } = this;

    // unbrake before setting `Recovering` state kind,
    // otherwise a 'Robot braked in the middle of recovering' failure will be triggered
    try {
      if (this.robot.isRobotBraked()) {
        await this.robot.unbrake();
      }
    } catch (error) {
      if (this.instructionID !== instructionID) {
        // pre-empted by something like `stop` or `pause`
        return false;
      }

      log.error(
        ns`arm.disengage.failure`,
        'Failed to disengage arm brakes',
        error,
      );

      throw new Error('Recovery failed: could not unbrake');
    }

    // the main thing we're checking is that it's not currently moving;
    // technically Idle would probably be fine, but it shouldn't be in state idle
    // if we're about to recover
    if (this.state.kind !== 'Failure') {
      throw new Error('Recovery failed: the robot is not in Failure state');
    }

    this.state = {
      kind: 'Recovering',
      kinematicState: this.state.kinematicState,
      equipmentState: this.state.equipmentState,
      configuration: this.state.configuration,
      failureState: this.state.failureState,
      visualizationState: this.state.visualizationState,
      forceRecoveryMode: this.state.forceRecoveryMode,
      // start out not checking guided mode because the motion plan sometimes
      // uses a lot of executor time
      shouldCheckGuidedMode: false,
      isRecoveringWithWristButton: initializedByWristButton,
    };

    await this.untilStill(5000);

    return instructionID === this.instructionID;
  }

  /**
   * Recover from a collision (similar to an ad-hoc motion)
   * Resolves to true if successful and false if not
   */
  private async recoverByMoving(options: {
    speedProfile: SpeedProfile;
    initializedByWristButton?: boolean;
    targetJointAngles?: ArmJointPositions;
    recoverIntoJointLimits?: boolean;
    retryCount?: number;
    ignoredCollisionLinkPairs?: Array<[string, string]>;
    linkCollisionParameters?: LinkCollisionParameters[];
  }): Promise<boolean> {
    this.instructionID += 1;
    const { instructionID } = this;
    const retryCount = options.retryCount ?? 0;
    const ignoredCollisionLinkPairs = options.ignoredCollisionLinkPairs ?? [];

    if (
      !retryCount &&
      !(await this.initializeRecoveringState(options.initializedByWristButton))
    ) {
      return false;
    }

    const startingJointPositions = this.getJointAngles();
    this.validateGuidedMode();

    const motionPlanOptions = this.routineContext.getMotionPlanOptions(
      options.speedProfile,
    );

    const isRecoveryMotionRequired =
      (this.isOutOfJointLimits() || (await this.checkCollisions())) &&
      !ignoredCollisionLinkPairs;

    if (this.instructionID !== instructionID) {
      return false;
    }

    // Speed profile setup
    const { routineSpeedProfile } = this.state.configuration;

    const limitedSpeedProfile: Required<SpeedProfile> =
      this.routineContext.limitSpeeds(
        options.speedProfile,
        routineSpeedProfile,
      );

    // If recovering with wrist button (from anti-gravity mode), skip automatic motion
    // and let the user manually guide the robot out of collision using anti-gravity
    if (
      this.state.kind === 'Recovering' &&
      this.state.isRecoveringWithWristButton
    ) {
      log.info(
        ns`recoverByMoving`,
        'Wrist button recovery - skipping automatic motion, allowing manual anti-gravity recovery',
        {
          isRecoveringWithWristButton: this.state.isRecoveringWithWristButton,
          failureKind: this.state.failureState?.failure.kind,
        },
      );

      return true;
    }

    // Execute motion using appropriate planner
    let success = false;
    let failureReason: string | undefined;

    try {
      if (getFeatureFlagSync('ros2', true)) {
        ({ success, failureReason } = await this.executeRecoveryWithROS({
          targetJointAngles: options.targetJointAngles,
          limitedSpeedProfile,
          instructionID,
          retryCount,
          ignoredCollisionLinkPairs,
          linkCollisionParameters: options.linkCollisionParameters,
        }));
      } else {
        success = await this.executeRecoveryWithMotionPlanner({
          startingJointPositions,
          targetJointAngles: options.targetJointAngles,
          limitedSpeedProfile,
          isRecoveryMotionRequired,
          instructionID,
          retryCount,
          motionPlanOptions,
        });
      }
    } catch (error) {
      if (this.instructionID !== instructionID) {
        // pre-empted by something like `stop` or `pause`
        return false;
      }

      this.fail({
        failure: { kind: FailureKind.ExecutionFailure },
        failureReason: 'Failed to execute recovery motion: Error in recovery',
        error,
      });

      return false;
    }

    if (!success) {
      this.fail({
        failure: { kind: FailureKind.ExecutionFailure },
        failureReason:
          failureReason ?? 'Failed to execute recovery motion: Motion failed',
      });

      return false;
    }

    if (options.recoverIntoJointLimits && this.isOutOfJointLimits()) {
      if (retryCount <= OUT_OF_JOINT_LIMIT_RECOVERY_MAX_RETRIES) {
        await wait(
          OUT_OF_JOINT_LIMIT_RECOVERY_RETRY_WAIT_TIME * 2 ** retryCount,
        );

        return this.recoverByMoving({
          ...options,
          retryCount: retryCount + 1,
        });
      }

      this.fail(this.getJointLimitViolationFailure(false));

      return false;
    }

    if (
      options.targetJointAngles &&
      isRecoveryMotionRequired &&
      retryCount < 2
    ) {
      return this.recoverByMoving({
        ...options,
        retryCount: retryCount + 1,
      });
    }

    return true;
  }

  private async executeRecoveryWithROS(
    params: Omit<
      RecoveryExecutionParams,
      | 'startingJointPositions'
      | 'motionPlanOptions'
      | 'isRecoveryMotionRequired'
    >,
  ): Promise<{ success: boolean; failureReason?: string }> {
    // wrap with mutex
    return this.adHocMotionMutex.runExclusive(async () => {
      const recoveryMotionWithROS = new RecoveryMotionWithROS(
        this.routineContext,
        OUT_OF_JOINT_LIMIT_RECOVERY_MAX_RETRIES,
      );

      const motion = recoveryMotionWithROS.getEvents();
      this.adHocMotion = motion;

      recoveryMotionWithROS.start({
        targetJointAngles: params.targetJointAngles,
        limitedSpeedProfile: params.limitedSpeedProfile,
        instructionID: params.instructionID,
        retryCount: params.retryCount,
        ignoredCollisionLinkPairs: params.ignoredCollisionLinkPairs,
        linkCollisionParameters: params.linkCollisionParameters,
      });

      const ended = await motion.race('failure', 'complete', 'cancelled');

      return {
        success: recoveryMotionWithROS.isSuccess(),
        failureReason: ended === undefined ? undefined : ended.failureReason,
      };
    });
  }

  private async executeRecoveryWithMotionPlanner(
    params: RecoveryExecutionParams,
  ): Promise<boolean> {
    const {
      targetJointAngles,
      isRecoveryMotionRequired,
      instructionID,
      motionPlanOptions,
    } = params;

    let response: MotionPlanResponse;

    if (targetJointAngles && !isRecoveryMotionRequired) {
      response = this.motionPlanner.planMotion({
        startingJointPositions: this.getJointAngles(),
        targets: [{ jointAngles: targetJointAngles, motionKind: 'joint' }],
        ...motionPlanOptions,
      });
    } else {
      response = this.motionPlanner.planRecoveryMotion({
        startingJointPositions: this.getJointAngles(),
        ...motionPlanOptions,
      });
    }

    const motionPlan = await response.complete();

    if (
      this.state.kind !== 'Recovering' ||
      this.instructionID !== instructionID
    ) {
      return false;
    }

    this.state = {
      ...this.state,
      shouldCheckGuidedMode: !this.state.isRecoveringWithWristButton,
    };

    const motionResult = await this.robot.playMotionPlan({
      motionPlan,
      maxJointVelocities: motionPlanOptions.maxVelocities,
      maxJointAccelerations: motionPlanOptions.maxAccelerations,
      maxTooltipVelocity: motionPlanOptions.maxTooltipVelocity,
      checkTorqueLimitsRealtime: true,
    });

    log.debug(ns`executeRecoveryWithMotionPlanner`, 'Received motion result', {
      motionResult,
    });

    return true;
  }

  /**
   * Short-hand to quickly access the robot
   */
  private get robot(): RobotInterface {
    return this.routineContext.robot;
  }

  /**
   * Short-hand to quickly access the motion planner
   */
  private get motionPlanner(): MotionPlannerInterface {
    return this.routineContext.motionPlanner;
  }

  private get rosMotionPlanner(): RosMotionPlanner {
    return this.routineContext.rosMotionPlanner;
  }

  private get hybridMotionPlanner(): MotionPlannerInterface {
    return this.routineContext.hybridMotionPlanner;
  }

  private lastStateUpdateTime = new Date();

  /**
   * Returns the current Ewellix lift height in meters from equipment state, if available.
   */
  private getLiftHeightFromEquipment(): number | undefined {
    const eq = this.routineContext.equipment.getEquipmentState();
    const device = eq.find((item) => item.kind === 'EwellixLiftTLT');

    try {
      if (
        device &&
        (device as any).state &&
        typeof (device as any).state.heightMeters === 'number'
      ) {
        return (device as any).state.heightMeters as number;
      }
    } catch {}

    return undefined;
  }

  /**
   * Refreshes kinematic state and briefly settles until lift height stabilizes.
   * Returns the reported lift height after settling.
   */
  private async settleAndGetLiftHeight(): Promise<number | undefined> {
    // Ensure latest kinematic/equipment state (fresh lift height)
    await this.routineContext.updateKinematicState();

    let lastLift = this.getLiftHeightFromEquipment();

    for (let i = 0; i < 8; i += 1) {
      await new Promise((r) => setTimeout(r, 50));
      await this.routineContext.updateKinematicState();

      const nowLift = this.getLiftHeightFromEquipment();

      if (
        lastLift != null &&
        nowLift != null &&
        Math.abs(nowLift - lastLift) < 0.0005
      ) {
        break;
      }

      lastLift = nowLift;
    }

    return this.getLiftHeightFromEquipment();
  }

  private get currentStepID(): string | undefined {
    if (this.state.kind === 'RoutineRunning') {
      return this.state.currentStepID;
    }

    return undefined;
  }

  /**
   * The step to attribute a system failure to when the `fail` call site has
   * no step in scope. While running, this is the published main-track step
   * (`publishCurrentStep`) — `currentlyRunningSteps` also holds background
   * (RunInBackground) dispatches, which never publish and must never take
   * the attribution. Falls back to the deepest in-flight step when nothing
   * has been published (e.g. failures outside the running state).
   */
  private get currentStep(): Step | undefined {
    if (this.state.kind === 'RoutineRunning' && this.state.currentStep) {
      return this.state.currentStep;
    }

    return this.currentlyRunningSteps[0];
  }

  /**
   * `step`/`stepID` for a `StepFailure` raised by a system-failure path
   * with no step of its own in scope. Spread into the failure payload so
   * every such call site attributes identically (the published main-track
   * step, with the published id as fallback).
   */
  private currentStepIdentity(): Pick<StepFailure, 'step' | 'stepID'> {
    return { step: this.currentStep, stepID: this.currentStepID };
  }

  private getJointLimitViolationFailure(
    recoverable: boolean = true,
    event: OutOfLimitsEvent | undefined = undefined,
  ): StepFailure {
    const getLimitViolations = () => {
      if (event !== undefined) {
        // If receiving the event from ACB, use that data instead
        // of the current state, which may be outdated.
        const result: Six<number | null> = six(null);
        result[event.outOfLimits.joint] = event.outOfLimits.position;

        return result;
      }

      const jointLimitViolations = this.jointLimitViolations();

      log.info(
        ns`getJointLimitViolationFailure.getLimitViolations`,
        'Raw joint limit violations',
        { jointLimitViolations },
      );

      return jointLimitViolations.map(({ error }) => error) as Six<
        number | null
      >;
    };

    const limitViolations = getLimitViolations();

    const jointAngles = this.getJointAngles();

    log.info(ns`getJointLimitViolationFailure`, 'Joint limits violation', {
      event,
      limitViolations,
      jointAngles,
    });

    return {
      failure: {
        kind: FailureKind.OutOfJointLimitsFailure,
        jointAngles,
        limitViolations,
        recoverable,
      },
      ...this.currentStepIdentity(),
      failureReason: (() => {
        let reason = 'The following joints are out of joint limits: ';

        const violationMessages = limitViolations
          .map((err, index) =>
            err === null
              ? null
              : `Joint ${index} at position ${jointAngles[index]}`,
          )
          .filter((err) => err !== null)
          .join(',');

        reason += violationMessages;

        return reason;
      })(),
    };
  }

  /**
   * Reconcile a latched arm-control-bot failure that the realtime
   * onControlSystemEvent listener did not (or could not) transition the runner
   * out of.
   *
   * Background: when an e-stop / safety trip lands mid-motion, ACB can latch an
   * informational `ioStateChanged` (or a null reason) as its terminal
   * failureReason instead of the `eStopTriggered` event. The listener's
   * ioStateChanged handler only recomputes speed-scale-factor — it never
   * transitions the runner — so getInFailureState() stays true while the runner
   * sits in a `RoutineRunning` in-failure limbo. The previous update() logic
   * re-dispatched that same latched reason through the event switch on every
   * 50ms tick (log/CPU spam) without ever converging.
   *
   * Instead of re-dispatching, force a single convergence to `Failure` once ACB
   * has been latched in-failure for LATCHED_FAILURE_GRACE_MS AND nothing else
   * has already claimed the stop (no concurrent `failureState`). Because
   * fail() flips state.kind to `Failure` synchronously (via
   * RoutineContext.fail -> setRoutineRunnerState), the update() guard closes and
   * this fires exactly once per failure episode — no explicit dedupe needed.
   *
   * The settle window + not-paused gate let the fast listener win the race for
   * genuinely resumable stops: any pause flips `isPaused` — whether a
   * listener-applied `failureState` (collision / IMU / 3PE) or a `pauseDetails`
   * pause kind (e.g. heartbeat-lost) — so only the un-reconciled wedge, an
   * actively-running routine ACB reports in-failure, is forced to Failure.
   * Scoped to `RoutineRunning` (the documented limbo, and the only state the
   * repro reaches): an idle/other-state latch is either already transitioned by
   * the listener or must not be force-failed on this safety path.
   *
   * A cold boot produces the same latch shape before arm-control's control
   * loop finishes initializing, so convergence is additionally gated on
   * having observed arm-control healthy at least once — bounded by
   * BOOT_LATCHED_FAILURE_GRACE_MS so a boot that never becomes healthy still
   * surfaces as `Failure`.
   *
   * A release this side has just requested (acknowledgeControlSystemStop, on
   * the pause card's Resume or inside resumeRoutine) also leaves the latch
   * reading true for the RPC and state-stream round trip, so convergence is
   * held for LATCH_RELEASE_GRACE_MS after such a request: a resume that has
   * released its own stop is not judged against a latch already being cleared.
   *
   * Those windows narrow the race but cannot close it: arm-control-bot raises
   * the latch when its graceful stop finishes, not when it emits the event, so
   * a latch can always appear on a running routine that the listener has
   * already handled (or never saw). Converging on it would answer a stop the
   * operator can acknowledge and resume from with a terminal
   * "Robot entered a failure state (imuCollision)." recovery card. So a
   * latched reason that is resumable (REPLAYABLE_LATCHED_REASON_KINDS) is
   * replayed through the listener once instead, and only what the listener
   * leaves un-transitioned — the genuine wedge — still converges to `Failure`.
   *
   * @returns true when update() should early-return this tick — mirrors the
   *   previous in-failure short-circuit, minus the per-tick re-dispatch.
   */
  private reconcileLatchedFailure(nowMs: number): boolean {
    if (this.firstReconcileTickMs === null) {
      this.firstReconcileTickMs = nowMs;
    }

    if (!this.robot.getInFailureState()) {
      this.hasSeenHealthyArmControl = true;
      this.latchedFailureSince = null;
      this.latchReleaseRequestedAt = null;
      this.replayedLatchedReasonKey = null;

      return false;
    }

    // Already reconciled (listener failed to Failure, or we're recovering).
    // Behave like the previous guard, which did not early-return for these.
    if (this.state.kind === 'Failure' || this.state.kind === 'Recovering') {
      this.latchedFailureSince = null;

      return false;
    }

    if (this.latchedFailureSince === null) {
      this.latchedFailureSince = nowMs;
    }

    const settled =
      nowMs - this.latchedFailureSince >= LATCHED_FAILURE_GRACE_MS;

    // Boot gate: until arm-control has been observed healthy once, the latch
    // is presumed to be its control loop still initializing — wait it out
    // (bounded by BOOT_LATCHED_FAILURE_GRACE_MS so a boot that never becomes
    // healthy still converges to Failure below).
    const withinBootGrace =
      !this.hasSeenHealthyArmControl &&
      nowMs - this.firstReconcileTickMs < BOOT_LATCHED_FAILURE_GRACE_MS;

    // A release this side requested is still propagating (RPC plus the
    // state-stream refresh): converging now would turn a legitimate resume
    // into a Failure over a latch that is already being cleared. Once the
    // grace passes, a still-set latch is genuinely stuck and converges below.
    const latchReleaseInFlight = this.isLatchReleaseInFlight(nowMs);

    // Force convergence ONLY for the genuine wedge: an actively-running
    // (un-paused) routine that ACB reports in-failure, past the settle window.
    // A paused RoutineRunning — whether via a listener-applied `failureState`
    // (collision / 3PE) or a `pauseDetails` pause kind (e.g. heartbeat-lost) —
    // is a resumable stop something else already claimed; never force-fail it.
    if (
      settled &&
      !withinBootGrace &&
      !latchReleaseInFlight &&
      this.state.kind === 'RoutineRunning' &&
      !this.state.isPaused &&
      this.state.failureState == null
    ) {
      const latchedReason = this.robot.getFailureReason() ?? undefined;

      // A resumable stop the listener never applied. Replay it through the
      // listener once so the operator gets that stop's own recoverable pause
      // card, not a terminal failure over a stop they can acknowledge and
      // resume from. Replaying rather than pausing here keeps one owner of the
      // per-kind pause semantics (pause kind, recovery type, rich message).
      //
      // The replay is fire-and-forget by the same contract as the listener's
      // own call site — handleControlSystemEvent contains its own rejections.
      // If it does not leave the routine paused, the next tick finds the same
      // stop already replayed and converges to Failure below, so a reason that
      // no longer transitions the runner still surfaces.
      if (
        latchedReason != null &&
        REPLAYABLE_LATCHED_REASON_KINDS.has(latchedReason.kind) &&
        this.replayedLatchedReasonKey !== latchedReasonKey(latchedReason)
      ) {
        log.warn(
          ns`reconcileLatchedFailure.replayResumableReason`,
          'Replaying a resumable latched ACB reason as a pause',
          { latchedReason: latchedReason.kind },
        );

        this.replayedLatchedReasonKey = latchedReasonKey(latchedReason);

        this.handleControlSystemEvent(latchedReason, {
          isLatchReconciliationReplay: true,
        });

        return true;
      }

      log.warn(
        ns`reconcileLatchedFailure.forceFailure`,
        'Forcing convergence to Failure for un-reconciled latched ACB failure',
        { latchedReason: latchedReason?.kind ?? null },
      );

      this.latchedFailureSince = null;
      this.latchReleaseRequestedAt = null;

      // Fire-and-forget, matching the other failure entrypoints: fail() wraps
      // its awaited body in a try/catch, so a rejection can never escape as an
      // unhandledRejection (the fail() containment contract).
      this.fail(this.latchedReconciliationFailure(latchedReason));
    }

    return true;
  }

  /** True while a latch release this side requested may still be propagating. */
  private isLatchReleaseInFlight(nowMs: number): boolean {
    return (
      this.latchReleaseRequestedAt !== null &&
      nowMs - this.latchReleaseRequestedAt < LATCH_RELEASE_GRACE_MS
    );
  }

  /**
   * Builds the StepFailure used to converge a latched ACB failure to `Failure`
   * (see reconcileLatchedFailure). Reuses `ControlSystemFailure` — the wedge
   * event (an `ioStateChanged`) is not a `GeneralControlSystemEvent` so it is
   * not attached; the human-readable reason derived from the latched event
   * carries the actionable payload.
   */
  private latchedReconciliationFailure(
    event: ControlSystemEvent | undefined,
  ): StepFailure {
    return {
      ...this.currentStepIdentity(),
      failure: { kind: FailureKind.ControlSystemFailure },
      failureReason: describeLatchedFailureReason(event),
    };
  }

  /**
   * This function checks if the state needs to be updated due to external conditions changing.
   * The intention is to call it whenever a state change might be viable. In practice, it runs
   * on an interval to make sure we don't miss realtime connection validation updates.
   */
  private async update() {
    try {
      const now = new Date();

      this.routineContext.updateKinematicState();

      // Reconcile a latched arm-control-bot failure. Converges the
      // wedge to Failure rather than re-dispatching the latched reason through
      // the event switch every tick, and short-circuits the rest of update()
      // while ACB is latched in-failure (as the previous check did).
      if (this.reconcileLatchedFailure(now.getTime())) {
        return;
      }

      // Go into a failure state if the joints are out of limits.
      if (
        this.isOutOfJointLimits() &&
        // don't report if we're already marked as Failure or if we're trying to recover
        this.state.kind !== 'Failure' &&
        this.state.kind !== 'Recovering'
      ) {
        const violations = this.jointLimitViolations();

        log.error(ns`outOfLimits.detected`, 'Out of joint limits detected', {
          jointAngles: this.getJointAngles(),
          violations,
        });

        await this.fail(this.getJointLimitViolationFailure());
      }

      // If guidedModeInvalidation is populated (non-null), we should `stop` if we're
      // in Ad-Hoc mode.
      //
      // Otherwise, we just invalidate startOfGuidedMode and skip logging or stopping.
      let guidedModeInvalidation: string | null = null;

      if (!this.isGuidedModeValid()) {
        const diff = now.getTime() - this.lastGuidedModeValidation.getTime();
        const start = this.startOfGuidedMode ?? new Date();
        const wholeDuration = now.getTime() - start.getTime();
        this.startOfGuidedMode = null;

        guidedModeInvalidation = `guided mode has not been validated (${diff}ms = now ${now} - last validation ${this.lastGuidedModeValidation.getTime()}). Guided mode lasted ${wholeDuration}ms`;
      }

      switch (this.state.kind) {
        case 'Idle': {
          // 1 = pressed, 2 = double pressed
          const antigravButtonState = this.robot.getWristButtonStates()[0];

          const slowAntigravButtonState =
            this.robot.getWristButtonStates()[0] === 2;

          if (antigravButtonState) {
            this.state = {
              ...this.state,
              kind: slowAntigravButtonState ? 'AntigravitySlow' : 'Antigravity',
            };
          }

          break;
        }

        case 'RunningAdHocCommand': {
          if (this.state.shouldCheckGuidedMode && guidedModeInvalidation) {
            if (getFeatureFlagSync('ros2', true)) {
              if (
                this.adHocMotion !== undefined &&
                !this.adHocMotion?.hasEmittedAny(
                  'cancel',
                  'complete',
                  'failure',
                  'cancelled',
                )
              ) {
                this.stop(guidedModeInvalidation);
              }
            } else {
              this.stop(guidedModeInvalidation);
            }

            break;
          }

          if (
            this.robot.isRobotBraked() &&
            !this.state.forceRecoveryMode &&
            this.state.failureState?.recoveryType !== 'ManualRecoveryMode'
          ) {
            this.fail({
              failure: { kind: FailureKind.ExecutionFailure },
              failureReason: `Robot braked in the middle of executing the "${startCase(
                this.state.command.kind,
              )}" command`,
            });

            break;
          }

          if (this.robot.getWristButtonStates()[0]) {
            this.fail({
              failure: { kind: FailureKind.ExecutionFailure },
              failureReason: `Antigravity button pressed during "${startCase(
                this.state.command.kind,
              )}" command`,
            });

            break;
          }

          break;
        }

        case 'Recovering': {
          if (this.state.shouldCheckGuidedMode && guidedModeInvalidation) {
            this.stop(guidedModeInvalidation);
            break;
          }

          if (
            !this.robot.getWristButtonStates()[0] &&
            this.state.isRecoveringWithWristButton
          ) {
            this.stop('Wrist button released during recovery');
            break;
          }

          if (
            this.state.failureState.recoveryType !== 'ManualRecoveryMode' &&
            this.robot.isRobotBraked()
          ) {
            this.fail({
              failure: { kind: FailureKind.ExecutionFailure },
              failureReason: 'Robot braked in the middle of recovering',
            });

            break;
          }

          if (
            this.robot.getWristButtonStates()[0] &&
            !this.state.isRecoveringWithWristButton
          ) {
            this.fail({
              failure: { kind: FailureKind.ExecutionFailure },
              failureReason: 'Antigravity button pressed during recovery',
            });

            break;
          }

          break;
        }

        case 'RoutineRunning': {
          const antigravityStepEnabled = getFeatureFlagSync(
            'antigravityStep',
            false,
          );

          const isAntigravityStep =
            this.state.currentStepKind === 'Antigravity';

          if (
            this.robot.getWristButtonStates()[0] &&
            !(isAntigravityStep && antigravityStepEnabled)
          ) {
            this.fail({
              failure: { kind: FailureKind.ExecutionFailure },
              failureReason:
                'Antigravity button was pressed in the middle of a routine',
              ...this.currentStepIdentity(),
            });

            break;
          }

          if (this.state.shouldCheckGuidedMode) {
            if (guidedModeInvalidation) {
              if (!this.state.isPaused) {
                log.info(
                  ns`guidedModeInvalidation.detected`,
                  'Pausing routine until guided mode continues:',
                  guidedModeInvalidation,
                );

                this.pauseRoutine({ kind: 'guidedModeInvalidation' });
              }

              break;
            }

            // Check for 3PE during guided mode motions (like collision is checked)
            if (
              !this.state.isPaused &&
              this.routineContext.isPause3PEActive()
            ) {
              log.info(
                ns`pause3pe.detected`,
                '3PE became active during guided mode motion - triggering pause',
              );

              this.pauseOrFail({
                failure: {
                  kind: FailureKind.Pause3PEFailure,
                },
                failureReason: 'Motion stopped: 3PE safety stop is active',
                ...this.currentStepIdentity(),
              });

              break;
            }
          }

          if (!this.state.isPaused) {
            const elapsedMilliseconds =
              now.getTime() - this.lastStateUpdateTime.getTime();

            this.state = {
              ...this.state,
              runTimeSeconds:
                this.state.runTimeSeconds + elapsedMilliseconds / 1000,
            };
          }

          // Auto-confirm Antigravity step if its condition has been met
          if (
            this.state.isPaused &&
            this.state.pauseDetails.some((d) => d.kind === 'antigravityStep')
          ) {
            for (const step of this.currentlyRunningSteps) {
              if (
                step instanceof AntigravityStep &&
                step.isConditionConfirmed()
              ) {
                log.info(
                  ns`antigravity.autoConfirm`,
                  'Antigravity step condition met, auto-confirming',
                );

                // Use confirmStep to properly resume
                this.confirmStep(step.id).catch((err) => {
                  log.error(
                    ns`antigravity.autoConfirm.error`,
                    'Failed to auto-confirm antigravity step',
                    { error: err },
                  );
                });

                break;
              }
            }
          }

          const mainLoop = this.routineContext.loadedRoutineState?.mainLoop;

          if (mainLoop) {
            let variables;

            try {
              variables =
                this.routineContext.loadedRoutineFrame!.getVariable(mainLoop);
            } catch (e) {
              log.warn(
                ns`mainLoop.variables.notFound`,
                `Tried to access main loop ${mainLoop} variables but the step does not exist`,
                { mainLoop, routine: this.routineContext.loadedRoutineState },
              );

              break;
            }

            try {
              variables = LoopStep.Variables.parse(variables);
            } catch (e) {
              log.warn(
                ns`mainLoop.variables.invalid`,
                `Tried to access main loop ${mainLoop} variables but the step is not a loop step`,
                { mainLoop, routine: this.routineContext.loadedRoutineState },
              );

              return;
            }

            this.state = {
              ...this.state,
              cycleCount: variables.currentIteration,
            };

            if (typeof variables.expectedTotalIterations === 'number') {
              this.state = {
                ...this.state,
                totalExpectedCycles: variables.expectedTotalIterations,
              };

              break;
            }
          }

          break;
        }

        case 'Antigravity':
        case 'AntigravitySlow': {
          // Can transition between anti-gravity modes
          if (this.robot.getWristButtonStates()[0]) {
            this.state = {
              ...this.state,
              // 1 = pressed, 2 = double pressed
              kind:
                this.robot.getWristButtonStates()[0] === 2
                  ? 'AntigravitySlow'
                  : 'Antigravity',
            };
          }

          if (!this.robot.getWristButtonStates()[0]) {
            log.debug(
              ns`update`,
              'Setting state.kind to Idle. Based on wrist button state.',
              {
                oldPartialState: {
                  kind: this.state.kind,
                  failureState: this.state.failureState,
                },
              },
            );

            this.state = {
              kind: 'Idle',
              forceRecoveryMode: this.state.forceRecoveryMode ?? false,
              kinematicState: this.state.kinematicState,
              equipmentState: this.state.equipmentState,
              configuration: this.state.configuration,
              failureState: this.state.failureState,
              visualizationState: this.state.visualizationState,
              cannotStartReasons: this.cannotStartReasons(),
            };
          }

          break;
        }
        case 'Failure': {
          // Track wrist button releases to ensure user has released button at least once
          // since entering failure state (important for AG mode collisions)
          if (!this.robot.getWristButtonStates()[0]) {
            this.hasWristButtonBeenReleasedSinceFailure = true;
          }

          if (
            this.state.failureState.isRecoverableWithWristButton &&
            this.robot.getWristButtonStates()[0] &&
            this.hasWristButtonBeenReleasedSinceFailure
          ) {
            await this.recover({ initializedByWristButton: true });
          }

          break;
        }
        default: {
          break;
        }
      }

      this.lastStateUpdateTime = now;
    } catch (error) {
      if (this.state.kind !== 'Failure') {
        log.error(
          ns`internalFailure`,
          'Internal Failure in RoutineRunner update',
          error,
        );

        this.fail({
          failure: { kind: FailureKind.InternalFailure },
          failureReason: `Failed to update: ${error.message}`,
          ...this.currentStepIdentity(),
          error,
        });
      }
    }
  }

  private lastCollisionCheck: ForwardKinematicsRequest | null = null;

  private lastCollisionCheckWasColliding: boolean = false;

  /**
   * Fingerprint of the enabled-equipment set at the time of the last
   * collision check. Enabling/disabling equipment can mutate the planner's
   * ACM (e.g. active_gripper_manager toggles gripper-link collision pairs)
   * without changing joint angles, so we invalidate the FK caches whenever
   * this fingerprint changes between checks.
   */
  private lastEquipmentFingerprint: string | null = null;

  /**
   * Build a stable signature of the currently enabled equipment items.
   * Any change here is treated as a potential ACM change and forces the
   * next collision check to round-trip to the planner.
   */
  private getEnabledEquipmentFingerprint(): string {
    return this.routineContext.equipment.equipmentItems
      .filter((item) => item.isEnabled)
      .map((item) => item.id)
      .sort()
      .join(',');
  }

  /**
   * Drop the checkCollisions() cache so the next call performs a fresh FK and
   * re-runs its side effects (notably fail() on re-detected collision). Call
   * this on any state transition that crosses the Failure boundary, or when
   * the last FK response can't be trusted.
   */
  private resetCollisionCheckCache(): void {
    this.lastCollisionCheck = null;
    this.lastCollisionCheckWasColliding = false;
  }

  /**
   * The FK cache rounds joint angles to 4 decimals, so its "not colliding"
   * verdict can come from a nearby pose the arm has since settled out of.
   */
  private async recheckCollisionsAfterPlannerRejection(
    rejectedRequest: JoggingActionRequest['jogType'] | 'toolDirections',
  ): Promise<void> {
    if (this.state.kind !== 'Idle') {
      return;
    }

    log.info(
      ns`checkCollisions.plannerRejection`,
      'Planner rejected motion from the current pose; re-checking collisions without cache',
      { rejectedRequest, jointAngles: this.getJointAngles() },
    );

    this.resetCollisionCheckCache();
    this.rosMotionPlanner.clearForwardKinematicsCache();

    await this.checkCollisions();
  }

  /**
   * Continuously checks for collisions.
   * Only checks while in Antigravity mode.
   *
   */
  private checkCollisionsLoop() {
    let cancelled = false;

    const runLoop = async () => {
      while (!cancelled) {
        if (
          this.state.kind === 'Idle' ||
          this.state.kind === 'Antigravity' ||
          this.state.kind === 'AntigravitySlow' ||
          (this.state.failureState?.failure.kind ===
            'InBoxingPositionFailure' &&
            this.state.kind === 'Recovering') // recheck boxing collisions during recovery
        ) {
          await this.checkCollisions();
        }

        await wait(CHECK_COLLISIONS_INTERVAL_MS);
      }
    };

    runLoop();

    this.cancelers.push(() => {
      cancelled = true;
    });
  }

  /**
   * Looks for collisions and marks state as failure if there are collisions.
   *
   * Returns whether collisions were found.
   */
  private async checkCollisions(): Promise<boolean> {
    // Skip while brakes are engaged: encoder values read during the brake-
    // release transient can land in poses that FK flags as self-colliding,
    // producing a spurious CollisionFailure right as the user unbrakes.
    // Motion paths gate on `isRobotBraked()` separately.
    if (this.state.kinematicState.brakesEngaged) {
      return false;
    }

    // If the set of enabled equipment changed since the last check, the
    // planner's ACM may have changed (e.g. a gripper was enabled and now
    // counts as a collidable body). The cached FK request key only covers
    // joint angles + gripperOpenness, so without this guard a same-pose
    // re-check would short-circuit and we'd never surface a CollisionFailure
    // on the equipment-change tick. Bust both caches and force a fresh FK.
    const equipmentFingerprint = this.getEnabledEquipmentFingerprint();

    if (
      this.lastEquipmentFingerprint !== null &&
      this.lastEquipmentFingerprint !== equipmentFingerprint
    ) {
      this.resetCollisionCheckCache();
      this.rosMotionPlanner.clearForwardKinematicsCache();
    }

    this.lastEquipmentFingerprint = equipmentFingerprint;

    const jointAngles = this.getJointAngles();

    // Undefined until arm-control-bot reports the hardware variant.
    const reportedModelType = getModelType();

    // Only this check waits for a variant; classification below never does,
    // since returning "no collision" reads as safe to move. Deciding at
    // construction instead read joint angles still at their zero initializer and
    // a variant still unknown, once, with no retry. The brake guard above means
    // the arm has not moved since power-on, so this still answers how we found it.
    if (
      !this.hasCheckedForBoxedAtStartup &&
      reportedModelType &&
      this.robot.getIsPositionAvailable()
    ) {
      this.hasCheckedForBoxedAtStartup = true;

      if (isNearBoxingPosition(jointAngles, reportedModelType)) {
        this.isMovingIntoBoxingPosition = true;

        log.info(
          ns`checkCollisions`,
          'Arm was already boxed when we found it',
          {
            modelType: this.state.kinematicState.modelType,
            jointAngles,
          },
        );
      }
    }

    // If we're in a state of failure, we don't need to check for collisions.
    // Except if it's a MotionPlannerFailure. In that case, we want to retry
    // the motion planner until it succeeds, at which point we can resolve
    // the failure
    const isInMotionPlannerFailure =
      this.state.kind === 'Failure' &&
      this.state.failureState.failure.kind === 'MotionPlannerFailure';

    // Or, if we're in a boxing position failure, we want to continue to check for collisions
    const isInBoxingPositionFailure =
      this.state.kind === 'Failure' &&
      this.state.failureState.failure.kind === 'InBoxingPositionFailure';

    if (
      this.state.kind === 'Failure' &&
      !isInMotionPlannerFailure &&
      !isInBoxingPositionFailure
    ) {
      return false;
    }

    // While we are in a recovery state, we don't want to check for collisions. Except during a boxing recovery, we want to know when
    // we are no longer in any boxing collisions, so we know when to allow the user to use move controls.
    if (
      this.state.kind === 'Recovering' &&
      this.state.failureState?.failure.kind !== 'InBoxingPositionFailure'
    ) {
      log.info(
        ns`checkCollisions`,
        'In recovery state, not checking collisions',
      );

      return false;
    }

    const request: ForwardKinematicsRequest = {
      gripperOpenness: 1,
      jointAngles,
      checkValidity: true,
    };

    // if the request has already been checked, don't check collisions
    if (isEqual(this.lastCollisionCheck, request)) {
      return this.lastCollisionCheckWasColliding;
    }

    try {
      this.lastCollisionCheck = request;

      const { isColliding: rawIsColliding, collisions } =
        await this.hybridMotionPlanner.forwardKinematics(request);

      if (isInMotionPlannerFailure) {
        await this.recover();
      }

      // Suppress collisions at the boxing pose, where the collision model
      // overlaps geometry the folded arm does not actually touch, and between
      // the intermediate and the fold, where releasing the hold button stops
      // the arm among the pairs the fold allows.
      // Falls through so InBoxingPositionFailure detection still runs.
      const nearBoxing = isNearBoxingPosition(
        jointAngles,
        this.state.kinematicState.modelType,
      );

      const inBoxingCorridor =
        this.isMovingIntoBoxingPosition &&
        isNearBoxingIntermediate(
          jointAngles,
          this.state.kinematicState.modelType,
          { primitiveCollisionModel: this.primitiveCollisionModel },
        );

      const hasProhibitedBoxingCollision = collisions?.some((collision) => {
        // Check if this collision pair matches any of the allowed pairs
        const isAllowedCollision = this.boxingIgnoredCollisionPairs.some(
          ([body1, body2]) => {
            if (body1 === '*' || body2 === '*') {
              // If either is wildcard, check if other body exists in collision
              const nonWildcardBody = body1 === '*' ? body2 : body1;

              return collision.elementNames.some((name) =>
                name.includes(nonWildcardBody),
              );
            }

            // For explicit pairs, just check if both elements are present
            return (
              collision.elementNames.includes(body1) &&
              collision.elementNames.includes(body2)
            );
          },
        );

        return !isAllowedCollision;
      });

      // In the corridor only the pairs the fold list covers are suppressed:
      // the arm folding past the intermediate is expected to report those,
      // but a contact with anything else there is still a real collision.
      const isColliding =
        nearBoxing || (inBoxingCorridor && !hasProhibitedBoxingCollision)
          ? false
          : rawIsColliding;

      if (typeof isColliding === 'boolean') {
        this.lastCollisionCheckWasColliding = isColliding;
      }

      // It was boxed, and it still is. Requiring both keeps an arm merely passing
      // through these angles out of a failure that blocks playing the routine
      // driving it. Shares the predicate used to suppress the collision above, so
      // a suppressed collision is always a recognized one.
      const isBoxed = this.isMovingIntoBoxingPosition && nearBoxing;

      const hasPermittedBoxingCollision =
        isColliding && !hasProhibitedBoxingCollision;

      // If we are colliding, then want to throw a collision detection failure.
      // But, if we are in the boxing position and the detected collision is considered allowed (e.g. gripper collision),
      // Then, we don't consider it as a collision, instead just that we are in the boxing position.
      // Can enter here if recovering and InBoxingPositionFailure. But we don't want to throw a collision failure, instead, updating the boxing failure
      if (
        isColliding &&
        (!isBoxed || hasProhibitedBoxingCollision) &&
        this.state.kind !== 'Recovering'
      ) {
        log.info(ns`collision.detected`, 'Collision detected', {
          isBoxed,
          jointAngles,
        });

        // we need recovery if we are in this state
        await this.fail({
          failure: {
            kind: FailureKind.CollisionFailure,
            collisions: collisions ?? [],
            jointAngles,
            hasProhibitedBoxingCollisions: hasProhibitedBoxingCollision, // add to failure, so in boxing page, show box button instead of recovery if the collisions are permitted
          },
          failureReason: 'Arm has encountered a collision',
          ...this.currentStepIdentity(),
        });
      } else if (
        isBoxed ||
        this.state.failureState?.failure.kind === 'InBoxingPositionFailure'
      ) {
        log.info(ns`boxing.detected`, 'Arm is in boxing position', {
          jointAngles,
        });

        if (this.state.kind === 'Recovering') {
          // Just update the state without failing
          this.state = {
            ...this.state,
            failureState: {
              ...this.state.failureState,
              failure: {
                kind: FailureKind.InBoxingPositionFailure,
                hasPermittedCollisions: hasPermittedBoxingCollision,
                hasProhibitedCollisions: hasProhibitedBoxingCollision || false,
              },
            },
          };
        } else {
          await this.fail({
            failure: {
              kind: FailureKind.InBoxingPositionFailure,
              hasPermittedCollisions: hasPermittedBoxingCollision,
              hasProhibitedCollisions: hasProhibitedBoxingCollision || false,
            },
            failureReason: 'Arm is in boxed position',
            ...this.currentStepIdentity(),
          });
        }
      }

      // Only exit the boxing state once the arm is out of the boxing corridor
      // and not colliding, or on a collision the fold list does not cover. It
      // has to survive the whole corridor: a check runs at the start of each
      // leg, and clearing it at the intermediate would disarm the suppression
      // above and the boxed detection below before either is needed.
      if (
        (!isColliding && !nearBoxing && !inBoxingCorridor) ||
        hasProhibitedBoxingCollision
      ) {
        this.isMovingIntoBoxingPosition = false;
      }

      await this.removePauseKind('motionPlannerFailure');

      return isColliding ?? false;
    } catch (error) {
      // Invalidate the last collision check because it didn't actually
      // give back meaningful information.
      this.resetCollisionCheckCache();

      await this.pauseOrFail({
        failureReason: 'Failed to check for collisions',
        failure: {
          kind: FailureKind.MotionPlannerFailure,
          request: {
            method: 'forwardKinematics',
            args: [request],
          },
        },
        error,
      });

      return false;
    }
  }

  /**
   * Find the path of a step id in a routine.
   *
   * Returns null if the step doesn't exist
   *
   * @internal
   */
  // @ts-ignore (unused)
  private static getStepPath(
    routine: Routine,
    stepID: string,
  ): Array<string> | null {
    // recursive
    function lookForStep(steps: Routine['steps']): Array<string> | null {
      for (const step of steps) {
        if (step.id === stepID) {
          return [step.id];
        }

        if (step.steps) {
          const subpath = lookForStep(step.steps);

          if (subpath) {
            return [...subpath, step.id];
          }
        }
      }

      return null;
    }

    return lookForStep(routine.steps);
  }

  /**
   * @throws if the routine isn't loaded
   * @throws if the runner is in the wrong state
   */
  public async playRoutine({
    firstArmMoveIsGuidedMode = false,
    isPreflightTestRun = false,
    speedProfile,
    fireAndForget = false,
  }: PlayRoutineArgs) {
    await this.checkCollisions();

    const startTime = new Date();
    this.update();

    if (!this.routineContext.loadedRoutineState || !this.steps?.length) {
      throw new Error('Routine is not loaded');
    }

    if (this.state.kind !== 'Idle') {
      throw new Error(`Cannot play routine while in state ${this.state.kind}`);
    }

    if (this.state.kinematicState.brakesEngaged) {
      throw new Error('Robot needs to unbrake');
    }

    // Start each run with a clean slate. checkGripperConnectivity also resets
    // while idle, but back-to-back runs can both land inside one poll interval,
    // so don't rely on a tick having happened in between.
    this.gripperDisconnectMonitor.reset();

    log.info(
      ns`playRoutine`,
      `Playing routineID=${this.routineContext.loadedRoutineState.id}`,
      { routineId: this.routineContext.loadedRoutineState.id, speedProfile },
    );

    // Per-run: no pause episode has released the arm yet.
    this.armReleasedWhilePaused = false;

    this.state = {
      kind: 'RoutineRunning',
      phase: 'setup',
      shouldCheckGuidedMode: false,
      readyToBeginMotion: false,
      isPaused: false,
      pauseDetails: [],
      kinematicState: this.state.kinematicState,
      equipmentState: this.state.equipmentState,
      currentStepID: this.steps[0].id,
      currentStepKind: this.steps[0].getStepKind(),
      currentStep: this.steps[0],
      // Play starts at the first top-level step, so it is its own
      // loaded-routine attribution.
      currentStepLoadedRoutineStepId: this.steps[0].id,
      configuration: {
        ...this.state.configuration,
        routineSpeedProfile: speedProfile,
      },
      failureState: this.state.failureState,
      visualizationState: this.state.visualizationState,
      forceRecoveryMode: this.state.forceRecoveryMode,
      variables: this.routineContext.getAllVariables(),
      startTime: startTime.toISOString(),
      runTimeSeconds: 0,
      shouldNextArmMoveBeGuidedMode: firstArmMoveIsGuidedMode,
      isPreflightTestRun,
      completedCycles: 0,
    };

    // encapsulate the rest of this function in a promise
    const playRoutinePromise = async () => {
      this.instructionID += 1;
      const { instructionID } = this;
      await this.untilStill(5000);

      if (instructionID !== this.instructionID) {
        return;
      }

      const cannotStartReasons = this.cannotStartReasons();

      if (cannotStartReasons.length > 0) {
        let failureReason: string = '';

        if (cannotStartReasons.length === 1) {
          failureReason = humanReadablePauseKind(cannotStartReasons[0]);
        } else {
          failureReason = `Cannot play routine because: ${cannotStartReasons
            .map(humanReadablePauseKind)
            .join(', ')}`;
        }

        this.fail({
          failure: { kind: FailureKind.IOFailure },
          failureReason,
        });

        return;
      }

      const cancelAsynchronousStepFailureListener =
        this.routineContext.onAsynchronousStepFailure(({ stepID }) => {
          if (this.instructionID !== instructionID) {
            cancelAsynchronousStepFailureListener();

            return;
          }

          this.stop(`Routine step ${stepID} failed asynchronously.`);
          cancelAsynchronousStepFailureListener();
        });

      // Collect all steps (including nested substeps)
      const allSteps = this.collectAllSteps(this.steps!);

      this.hasEncounteredStartStep = false;
      this.currentlyRunningSteps = [];

      try {
        // Call setup on all steps before execution
        log.info(ns`playRoutine.setup`, 'Calling setup on all steps', {
          stepCount: allSteps.length,
        });

        for (const step of allSteps) {
          if (instructionID !== this.instructionID) {
            return;
          }

          try {
            await step.setup({
              fail: async (failure) => {
                if (instructionID === this.instructionID) {
                  await this.pauseOrFail({
                    ...failure,
                    step,
                  });
                }
              },
            });
          } catch (error) {
            log.error(ns`playRoutine.setup`, 'Setup failed', {
              stepId: step.id,
              stepKind: step.getStepKind(),
              error,
            });

            // Check for robot failure reason (e.g., IK failure, collision) to surface better error messages
            const robotFailureReason =
              this.routineContext.robot.getFailureReason();

            let failureReason = `Step ${step.getStepKind()} setup failed: ${
              error instanceof Error ? error.message : String(error)
            }`;

            if (robotFailureReason?.kind === 'controlSystemEvent') {
              failureReason = robotFailureReason.controlSystemEvent.message;
            } else if (robotFailureReason?.kind === 'imuCollision') {
              failureReason = formatImuCollisionMessage(robotFailureReason);
            } else if (robotFailureReason?.kind === 'collision') {
              failureReason = `Joint ${robotFailureReason.collision.joint} encountered a collision`;
            }

            await this.pauseOrFail({
              failure: {
                kind: FailureKind.StepPlayFailure,
                stepKind: step.getStepKind(),
              },
              failureReason,
              step,
              error: error instanceof Error ? error : new Error(String(error)),
            });

            return;
          }
        }

        // Setup complete, transition to running phase
        if (this.state.kind === 'RoutineRunning') {
          this.state = { ...this.state, phase: 'running' };
        }

        while (true) {
          // Populates `routineContext.persistentValuesByRoutineId` so that
          // sub-local env-var seeding in `CallSubroutineStep._play` can
          // read cached values without re-hitting the underlying fetch
          // injection. We pre-fetch for the loaded routine *and* every reachable
          // subroutine id (transitive via CallSubroutine refs); cache hits
          // win over `initialValue` evaluation when the sub frame is built.
          let persistentVariableValues: Map<string, unknown> | undefined;

          try {
            persistentVariableValues = this.routineContext.loadedRoutineId
              ? await this.routineContext.fetchPersistentVariableValuesFor(
                  this.routineContext.loadedRoutineId,
                )
              : undefined;

            // Serialized: reachable-sub count is small in practice and the
            // fetch injection often shares a backend.
            for (const subRoutineId of this.routineContext.loadedRoutine?.collectReachableSubroutineIds() ??
              []) {
              await this.routineContext.fetchPersistentVariableValuesFor(
                subRoutineId,
              );
            }
          } catch (error) {
            // Returning here skips the inner `finally` that normally
            // unsubscribes this attempt's listener, so drop it by hand.
            cancelAsynchronousStepFailureListener();

            // A fetch already in flight can't be cancelled, so a Stop (or a
            // fresh play) can bump `instructionID` before this rejection
            // lands. `fail()` doesn't gate on the instruction, so without
            // this check a late rejection would drag an already-stopped,
            // idle runner into Failure and demand recovery for a run the
            // operator deliberately halted.
            if (instructionID !== this.instructionID) {
              return;
            }

            // Fail loudly rather than seeding from `initialValue`: a
            // persistent variable is state carried across runs, so a counter
            // silently resetting mid-production repeats work the cell has
            // already done. Without this the rejection unwinds past every
            // failure path into the play `finally`, and the runner settles in
            // Idle with no failure recorded — indistinguishable from a
            // routine that ran to completion.
            await this.fail({
              failure: { kind: FailureKind.ExecutionFailure },
              failureReason: `Could not read persistent variable values: ${
                error instanceof Error ? error.message : String(error)
              }`,
              error: error instanceof Error ? error : new Error(String(error)),
            });

            return;
          }

          await this.routineContext.resetOnPlayRoutine(
            persistentVariableValues,
          );

          try {
            await this.playSteps({
              steps: this.steps!,
              hasBeenPreempted: () => {
                return (
                  this.state.kind !== 'RoutineRunning' ||
                  instructionID !== this.instructionID
                );
              },
            });
          } finally {
            await this.cleanupCurrentlyRunningSteps();

            this.currentlyRunningSteps = [];
            cancelAsynchronousStepFailureListener();
          }

          if (this.instructionID !== instructionID) {
            return;
          }

          if (!this.hasEncounteredStartStep) {
            const err = new Error(
              `Could not find start step ${this.routineContext.loadedRoutineState!.startStepID}`,
            );

            this.fail({
              failure: { kind: FailureKind.InvalidRoutineLoadedFailure },
              failureReason: `Loaded routine has specified a start step ID that could not be found (${this.routineContext.loadedRoutineState!.startStepID})`,
            });

            throw err;
          }

          if (this.state.kind !== 'RoutineRunning') {
            return;
          }

          // Increment completed cycle counter so pollers can detect cycle
          // completion whether or not the run pauses here.

          this.state = {
            ...this.state,
            completedCycles: (this.state.completedCycles ?? 0) + 1,
          };

          // A preflight test run parks paused after one pass so the operator
          // can inspect the result and resume the same run at full speed.
          // Clearing the flag is what makes the resumed run an ordinary one, so
          // it stops the next time it completes instead of parking here again.
          // `skipPreflightTestRun` clears the same flag to the same effect.
          if (this.state.isPreflightTestRun) {
            this.state = {
              ...this.state,
              isPreflightTestRun: false,
            };

            // pause, next resume will restart the routine
            await this.pauseRoutine({
              kind: 'routineCompleted',
              reason: 'The routine completed.',
            });

            this.routineContext.resetOnCompletedRoutine(this.steps!);
          } else {
            break;
          }
        }
      } finally {
        // Mark teardown phase so the UI can show a "Tearing Down…" indicator
        if (this.state.kind === 'RoutineRunning') {
          this.state = { ...this.state, phase: 'teardown' };
        }

        // Always call teardown, even if routine failed or was stopped
        log.info(ns`playRoutine.teardown`, 'Calling teardown on all steps', {
          stepCount: allSteps.length,
        });

        // Call teardown in reverse order (last setup = first teardown)
        // This allows steps to clean up in the opposite order of initialization
        for (let i = allSteps.length - 1; i >= 0; i -= 1) {
          const step = allSteps[i];

          try {
            await step.teardown({
              fail: async (failure) => {
                // Log teardown failures but don't fail the routine
                // (routine is already done or failed)
                log.error(
                  ns`playRoutine.teardown`,
                  'Teardown reported failure',
                  {
                    stepId: step.id,
                    stepKind: step.getStepKind(),
                    failure,
                  },
                );
              },
            });
          } catch (error) {
            // Log teardown errors but don't fail the routine
            // (routine is already done or failed)
            log.error(ns`playRoutine.teardown`, 'Teardown error', {
              stepId: step.id,
              stepKind: step.getStepKind(),
              error,
            });
          }
        }

        // After teardown completes, transition to Idle if still in teardown phase
        if (
          this.state.kind === 'RoutineRunning' &&
          this.state.phase === 'teardown'
        ) {
          log.debug(
            ns`playRoutine.teardown`,
            'Teardown complete, transitioning to Idle.',
          );

          this.state = {
            kind: 'Idle',
            forceRecoveryMode: this.state.forceRecoveryMode ?? false,
            kinematicState: this.state.kinematicState,
            equipmentState: this.state.equipmentState,
            configuration: this.state.configuration,
            failureState: this.state.failureState,
            // Reaching teardown while still `RoutineRunning` is what makes
            // this the completion path: `stop()` and the failure paths have
            // already moved the state on by the time this `finally` runs, so
            // the guard above declines for them. Every other `Idle` leaves the
            // count unset, so only a completed run ever reports one.
            completedCycles: this.state.completedCycles ?? 0,
            // Same run boundary `stop()` applies. A run that ends on its own
            // must leave the operator looking at what stopping one would.
            visualizationState: visualizationAtRunBoundary(
              this.state.visualizationState,
            ),
            cannotStartReasons: this.cannotStartReasons(),
          };
        }
      }
    };

    // If fireAndForget is true, start the routine execution in background and return immediately
    if (fireAndForget) {
      log.info(
        ns`playRoutine`,
        `Starting routine execution in background (fire and forget)`,
        { routineId: this.routineContext.loadedRoutineState.id },
      );

      // Start the routine execution in the background without awaiting
      playRoutinePromise().catch((error: unknown) => {
        log.error(
          ns`playRoutine.background.error`,
          'Error while executing routine in background',
          { error, routineId: this.routineContext.loadedRoutineState?.id },
        );
      });
    } else {
      // Execute the routine normally (awaiting completion)
      await playRoutinePromise();
    }
  }

  public async skipPreflightTestRun() {
    if (
      this.state.kind !== 'RoutineRunning' ||
      !this.state.isPreflightTestRun
    ) {
      throw new Error('Test run must be running to skipPreflightTestRun');
    }

    this.state = {
      ...this.state,
      isPreflightTestRun: false,
    };
  }

  /**
   * Changes configuration state at runtime to allow routines to run faster or slower.
   * Speed profile provides limits which will not be exceeded even when a routine specifies
   * a faster plan in the routine configuration.
   *
   * @throws if RoutineRunner instance is not paused
   * @category Routine Playback
   */
  public async changeRoutineSpeedProfile(speedProfile: SpeedProfile) {
    this.routineContext.setRoutineSpeedProfile(speedProfile);
  }

  /**
   * Set the speed restriction and bring the planned speed profile with it.
   *
   * The restriction and the profile are two halves of one setting: under ros2
   * the profile is deliberately planned at full speed and the restriction is
   * applied downstream, and off ros2 the restriction has to be baked into the
   * profile. Callers that set only one of them leave the arm moving at a speed
   * nothing in the system is reporting, so this owns both.
   */
  public async setSpeedRestrictionPercentage(percentage: number) {
    this.routineContext.setSpeedRestrictionPercentage(percentage);

    await this.applySpeedProfileFromConfiguration();
  }

  /**
   * Recompute the routine speed profile from the configuration already held
   * here — the restriction, the safety settings, and the kinematic state.
   *
   * This used to live in the remote-control client, which meant every speed
   * writer had to reproduce the ros2 branch correctly and a writer that forgot
   * silently left the arm unrestricted.
   */
  private async applySpeedProfileFromConfiguration() {
    const { safetySettingsData } = this.state.configuration;

    if (safetySettingsData == null) {
      log.info(
        ns`applySpeedProfileFromConfiguration`,
        'Skipping speedProfile update: no safety settings found.',
      );

      return;
    }

    const safetySettings = convertSafetySettingsResponse(safetySettingsData);

    let rosControlEnabled = false;

    if (getFeatureFlagSync('useControlSourceManager', false)) {
      const controlSource =
        await this.routineContext.armControlBotConnector?.GetControlSource(
          Empty,
        );

      rosControlEnabled =
        controlSource?.value === 'ROS_JOINT_TRAJECTORY' ||
        controlSource?.value === 'COMPLIANCE_JOINT_TRAJECTORY';
    } else {
      rosControlEnabled = this.robot.getROSControlEnabled();
    }

    // A preflight test run carries its own slower profile on top of the
    // restriction; dropping it here would speed the test run up.
    const additionalSpeedProfile =
      this.state.kind === 'RoutineRunning' && this.state.isPreflightTestRun
        ? this.state.configuration.testRunSpeedProfile
        : undefined;

    // Plan at full speed whenever something downstream already applies the
    // restriction, or it lands twice: ros2 applies it in the scale-factor
    // publisher, and a simulated robot folds it into
    // `getCurrentSpeedScaleFactor`. A simulator reports ROS control as off, so
    // it needs saying separately — otherwise a 50% slider runs the simulation
    // at 25% and the on-screen robot disagrees with the live arm.
    const appliedDownstream =
      rosControlEnabled || this.robot.robotKind === 'simulated';

    const speedProfile = calculateSpeedProfile({
      safetySettings,
      additionalSpeedProfile,
      speedRestrictionPercentage: appliedDownstream
        ? 1.0
        : this.state.configuration.speedRestrictionPercentage,
      modelType: this.state.kinematicState.modelType,
      cbVoltage: this.state.kinematicState.cbVoltage,
    });

    log.info(
      ns`applySpeedProfileFromConfiguration`,
      'Updating speed profile based on safety settings',
      { safetySettings, speedProfile },
    );

    await this.changeRoutineSpeedProfile(speedProfile);
  }

  public async setTestRunSpeed(speedProfile: SpeedProfile) {
    log.info(ns`setTestRunSpeed`, 'Setting test run speed', { speedProfile });
    this.routineContext.setTestRunSpeed(speedProfile);
  }

  /**
   * Set the antigravity configs.
   *
   * @throws if RoutineRunner instance is not paused
   * @category Routine Playback
   */
  public async setAntigravityConfigs(
    configs: FullAntigravityConfigs,
  ): Promise<FullAntigravityConfigs> {
    return this.routineContext.setAntigravityConfigs(configs);
  }

  /**
   * Get the antigravity configs.
   *
   * @throws if RoutineRunner instance is not paused
   * @category Routine Playback
   */
  public async getAntigravityConfigs(): Promise<FullAntigravityConfigs> {
    return this.routineContext.getAntigravityConfigs();
  }

  private async handleIOStateChange({ ioStateChanged }: IOStateChanged) {
    log.info(ns`io.change`, 'IO State change:', { ioStateChanged });

    // Emit formatted input IO state for external control systems
    if (ioStateChanged.newState) {
      const inputIO = Object.fromEntries(
        ioStateChanged.newState.externalToRobotIO.map((io, index) => [
          `Input ${index + 1}` as AnyInputPortID,
          (io ? 'high' : 'low') as IOLevel,
        ]),
      ) as Record<AnyInputPortID, IOLevel>;

      this.externalControlIOEvents.emit('inputIO', inputIO);

      // Update kinematicState.ioState so the frontend reflects the current I/O state
      this.routineContext.updateIOState();
    }

    if (this.state.kind === 'Idle') {
      this.state = {
        ...this.state,
        cannotStartReasons: this.cannotStartReasons(),
      };

      return;
    }

    if (
      this.state.kind !== 'RoutineRunning' &&
      this.state.kind !== 'RunningAdHocCommand'
    ) {
      return;
    }

    const { oldState, newState } = ioStateChanged;

    let reset = false;

    const oldIn = (oldState?.externalToRobotIO ?? sixteen(false)).map(
      toBit,
    ) as Sixteen<Bit>;

    if (!newState) {
      this.pauseRoutine({
        kind: 'ioStateStale',
        isAutoResume: true,
      });

      return;
    }

    await this.removePauseKind('ioStateStale');

    const newIn = newState.externalToRobotIO.map(toBit) as Sixteen<Bit>;

    const activePauses: InputSafeguardRule[] = [];

    // The vizbot simulator's IO doesn't reflect real hardware — skip
    // IO-based input safeguard rule evaluation for simulated robots to
    // match the behavior in `cannotStartReasons()`.
    const inputSafeguardRulesToEvaluate =
      this.robot.robotKind === 'simulated'
        ? []
        : this.state.configuration.inputSafeguardRules;

    // Yield to the macrotask queue before iterating safeguard rules. Each
    // rule emits a synchronous "Safeguard rule conditions" log, and when
    // multiple IO change events land at a step boundary the cumulative
    // sync logging contributes to heartbeat drift.
    if (inputSafeguardRulesToEvaluate.length > 1) {
      await yieldToEventLoop();
    }

    for (const inputSafeguardRule of inputSafeguardRulesToEvaluate) {
      const wasTriggered = safeguardRuleIsTriggeredBy(
        inputSafeguardRule,
        oldIn,
      );

      const isTriggered = safeguardRuleIsTriggeredBy(inputSafeguardRule, newIn);

      const isPause =
        inputSafeguardRule.kind === 'pausehigh' ||
        inputSafeguardRule.kind === 'pauselow';

      const isReset = inputSafeguardRule.kind === 'reset';

      log.info(ns`io.change`, 'Safeguard rule conditions:', {
        wasTriggered,
        isTriggered,
        isPause,
        isReset,
      });

      if (!wasTriggered && isTriggered) {
        if (isPause) {
          log.info(
            ns`io.pause`,
            `Pausing due to ${inputSafeguardRule.pair} transitioning to triggered`,
            { inputSafeguardRule },
          );

          activePauses.push(inputSafeguardRule);
        } else if (isReset) {
          log.info(
            ns`reset.triggered`,
            `Reset triggered (${inputSafeguardRule.pair})`,
            { inputSafeguardRule },
          );

          reset = true;
        }
      } else if (wasTriggered && !isTriggered) {
        if (inputSafeguardRule.isAutoReset && isPause) {
          log.info(
            ns`io.pause`,
            `Auto-resetting safeguard pair ${inputSafeguardRule.pair}`,
            { inputSafeguardRule },
          );

          await this.removePauseKind(
            `ioPauseTriggered_${inputSafeguardRule.pair}`,
          );

          if (this.state.kind === 'RoutineRunning') {
            log.debug(ns`io.paused`, 'Pause details:', this.state.pauseDetails);
          }
        }
      }
    }

    if (reset) {
      log.info(ns`io.pause.reset`, 'Resetting IO pause');

      for (const { pair } of this.state.configuration.inputSafeguardRules) {
        await this.removePauseKind(`ioPauseTriggered_${pair}`);
      }
    }

    for (const pause of activePauses) {
      const pauseKind: RoutineRunningPauseKind = `ioPauseTriggered_${pause.pair}`;

      this.pauseRoutine({
        kind: pauseKind,
        isAutoResume: pause.isAutoReset,
        reason: humanReadablePauseKind(pauseKind),
      });
    }

    if (
      ioStateChanged.newState?.safeguardState !==
      ioStateChanged.oldState?.safeguardState
    ) {
      log.info(
        ns`io`,
        `Safeguard state change from ${ioStateChanged.oldState?.safeguardState} to ${ioStateChanged.newState?.safeguardState}`,
      );

      await this.routineContext.updateKinematicState();

      await this.routineContext.publishSpeedScaleFactor();
    }
  }

  private handleButtonStateChange({
    buttonStateChangeEvent: event,
  }: ButtonStateChangeEvent) {
    log.info(ns`buttonState.changed`, 'Button state change:', { event });

    const isDownPress = event.newButtonState;

    const buttonTypes = {
      power: 0,
      playPause: 1,
      control: 2,
    } as const;

    if (event.buttonLocation === 'controlBox') {
      if (isDownPress && event.buttonIndex === buttonTypes.playPause) {
        this.toggleControlBoxPlayPauseButton();
      }

      if (isDownPress && event.buttonIndex === buttonTypes.control) {
        this.togglePrimaryIO();
      }
    }
  }

  private async togglePrimaryIO() {
    const currentLevel = this.robot.getIO()['Output 1'];
    const newLevel = currentLevel === 'high' ? 'low' : 'high';

    try {
      await this.robot.setOutputIO([
        {
          label: 'Output 1',
          level: newLevel,
        },
      ]);
    } catch (e) {
      log.error(ns`io.togglePrimary.failure`, 'Failed to toggle primary IO', e);
    }
  }

  private toggleControlBoxPlayPauseButton() {
    if (this.state.kind !== 'RoutineRunning') {
      log.info(
        ns`playPause.ignored`,
        'Cannot toggle play/pause when not running a routine',
        {
          stateKind: this.state.kind,
        },
      );

      return;
    }

    if (this.state.isPaused) {
      this.toggleControlBoxPlayPauseButtonPlay();
    } else {
      this.pauseRoutine({
        kind: 'user',
      });
    }
  }

  /**
   * Handles "play" button press from control box.
   *
   * Main wrinkle: Need to update speed.
   *
   * Should track what we see in: /apps/remote-control/hooks/usePlayRobot.ts#L96
   */
  private async toggleControlBoxPlayPauseButtonPlay() {
    // Short-circuit if not we can't actually resume.
    this.resumeRoutinePreChecks();

    await this.applySpeedProfileFromConfiguration();

    await this.resumeRoutine();
  }

  /**
   * Determine reasons why a routine should not be started.
   * Uses `pauseKind` because these are all reasons a routine would be paused
   * rather than failed.
   */
  private cannotStartReasons(): Array<RoutineRunningPauseKind> {
    const pauseKinds = new Set<RoutineRunningPauseKind>();

    if (this.state.kinematicState.safeguardState === 'eStop') {
      pauseKinds.add('eStopTriggered');
    }

    if (this.state.kinematicState.brakesEngaged) {
      pauseKinds.add('brakesEngaged');
    }

    if (legacyRosApiBlocksMotion()) {
      pauseKinds.add('legacyRosApiEnabled');
    }

    // The vizbot simulator's IO pins default to "low" and don't reflect real
    // hardware state, so evaluating IO-based input safeguard rules against it
    // would spuriously trigger pause/estop at routine start. Skip IO-based
    // rule evaluation for simulated robots; real robots still evaluate them.
    if (this.robot.robotKind === 'simulated') {
      return Array.from(pauseKinds.values());
    }

    const ioLevels = this.robot.getIO();

    const digitalIn = INPUT_PORT_IDS.map((portID) =>
      toBit(ioLevels[portID] === 'high'),
    ) as Sixteen<Bit>;

    for (const inputSafeguardRule of this.state.configuration
      .inputSafeguardRules) {
      const isTriggered = safeguardRuleIsTriggeredBy(
        inputSafeguardRule,
        digitalIn,
      );

      if (!isTriggered) {
        continue;
      }

      switch (inputSafeguardRule.kind) {
        case 'pausehigh':
        case 'pauselow': {
          pauseKinds.add(`ioPauseTriggered_${inputSafeguardRule.pair}`);

          break;
        }
        case 'estop': {
          pauseKinds.add('eStopTriggered');
          pauseKinds.add(`ioPauseTriggered_${inputSafeguardRule.pair}`);
          break;
        }
        default:
          break;
      }
    }

    return Array.from(pauseKinds.values());
  }

  /**
   * Plays through a series of routine steps
   * @category Routine Playback
   */
  /**
   * Recursively collect all steps including nested substeps from the loaded
   * routine's step tree and every cached subroutine's canonical instance tree.
   *
   * Used by `playRoutine` to drive setup at play start (forward order) and
   * teardown at play end (reverse order). The loaded routine comes first so its
   * setup runs before any subroutine's; ordering between subroutine specs
   * is unspecified — per the design contract, `_setup` overrides target
   * system-level state and must not depend on the order in which other
   * specs' setups ran.
   *
   * Per-call instance trees built in `CallSubroutineStep._play` are NOT
   * walked here — they intentionally don't have setup / teardown re-run.
   */
  private collectAllSteps(steps: Array<Step>): Array<Step> {
    const allSteps: Array<Step> = [];

    const collect = (stepList: Array<Step>) => {
      for (const step of stepList) {
        allSteps.push(step);

        if (step.substeps) {
          collect(step.substeps);
        }
      }
    };

    collect(steps);

    for (const canonicalSteps of this.routineContext.subroutineCanonicalsByRoutineId.values()) {
      collect(canonicalSteps);
    }

    return allSteps;
  }

  private async playSteps(args: {
    steps: Array<Step>;
    pushMode?: boolean;
    /**
     * Provided on internal recursions to stop the LoopStep from continuing.
     *
     * @internal
     */
    breakOutOfLoop?: () => void;

    /**
     * Provided on internal recursions to continue the LoopStep.
     *
     * @internal
     */
    continueLoop?: () => void;

    /**
     * Determine whether the steps have been preempted and whether any more steps
     * should be played.
     *
     * Allows the caller to stop execution of steps in the middle, for example
     * if the user stops the routine or if a `LoopControl` `Continue` step occurs.
     *
     * @internal
     */
    hasBeenPreempted(): boolean;

    /**
     * If `true`, won't set `currentStepID` in RoutineRunnerState
     */
    skipSettingCurrentStepID?: boolean;

    /**
     * If running a step with substeps such as Loop, we pass in the id of the
     * for logging purposes
     */
    parentStepID?: string;
  }) {
    const playStepsStartTime = Date.now();

    if (this.state.kind !== 'RoutineRunning') {
      let metadata = `(${this.state.kind})`;

      if (this.state.failureState?.failedStep) {
        const fs = this.state.failureState;

        const routinePart = fs.failedStepRoutineName
          ? ` in "${fs.failedStepRoutineName}"`
          : '';

        const positionPart = fs.failedStepPosition
          ? ` at step ${fs.failedStepPosition}`
          : '';

        metadata = `(Failed on step: ${fs.failedStep}${routinePart}${positionPart})`;
      }

      log.error(
        ns`playSteps`,
        `Routine not running when calling playSteps. ${metadata}`,
      );

      throw new Error(
        `Routine not running when calling playSteps. ${metadata}`,
      );
    }

    const {
      steps,
      pushMode,
      breakOutOfLoop,
      continueLoop,
      hasBeenPreempted,
      skipSettingCurrentStepID,
      parentStepID,
    } = args;

    // Track If/ElseIf chain state for conditional execution coordination.
    // When an If or ElseIf condition is satisfied (true), subsequent ElseIf/Else
    // steps in the same chain should be skipped.
    let ifElseBlockSatisfied = false;

    // loop through and play each step
    for (let ii = 0; ii < steps.length; ii += 1) {
      log.info(ns`playSteps`, `Playing step ${ii + 1}/${steps.length}`);
      const step = steps[ii];

      // Mark the start step as encountered *before* the suppressed-skip check.
      // When no explicit startConditions is passed, startStepID defaults to
      // `routine.steps[0].id`; if that step is suppressed, skipping without
      // flipping this flag would cause the post-playSteps check at
      // "Loaded routine has specified a start step ID that could not be found"
      // to fail the routine.
      if (!this.hasEncounteredStartStep) {
        this.hasEncounteredStartStep =
          step.id === this.routineContext.loadedRoutineState?.startStepID;
      }

      // Skip effectively-suppressed steps *before* If/ElseIf/Else chain
      // coordination — a suppressed step is treated as absent, so it must
      // neither evaluate as ElseIf/Else nor reset `ifElseBlockSatisfied`.
      // Otherwise a suppressed non-conditional (e.g. a Wait) between a
      // satisfied If and a following ElseIf would reset chain state and let
      // the ElseIf evaluate when it should be skipped.
      const effectivelySuppressed =
        step.suppressed || step.parentSteps.some((p) => p.suppressed);

      if (effectivelySuppressed) {
        log.info(
          ns`playSteps.suppressedSkip`,
          `Skipping suppressed step ${step.getStepKind()} (${step.id})`,
        );

        continue;
      }

      // Handle If/ElseIf/Else chain coordination
      if (step instanceof IfStep) {
        // New If starts a fresh chain
        ifElseBlockSatisfied = false;
      } else if (step instanceof ElseIfStep || step instanceof ElseStep) {
        // Skip ElseIf/Else if previous If/ElseIf was satisfied
        if (ifElseBlockSatisfied) {
          log.info(
            ns`playSteps.conditionalSkip`,
            `Skipping ${step.getStepKind()} (${step.id}) - If/ElseIf chain already satisfied`,
          );

          continue;
        }
      } else {
        // Non-conditional step breaks the chain tracking
        ifElseBlockSatisfied = false;
      }

      try {
        if (hasBeenPreempted()) {
          log.info(ns`playSteps`, 'preempt1');

          return;
        }

        // When `hasEncounteredStartStep` has not yet been set to `true`, we don't
        // actually want to play anything but just traverse through the steps until we
        // encounter it.
        // We exclude MoveArmToV2 step from the substep condition because it is only allowed
        // to have waypoints as substeps and should be considered as a single chunk.
        if (
          !this.hasEncounteredStartStep &&
          (!step.substeps || step.getStepKind() === 'MoveArmToV2')
        ) {
          log.info(ns`playSteps`, 'loop around');
          continue;
        }

        const setCurrentStepID = () => {
          if (!skipSettingCurrentStepID) {
            this.publishCurrentStep(step);
          }
        };

        setCurrentStepID();

        while (!hasBeenPreempted() && this.state.isPaused) {
          await wait(STILL_GOING_CHECK_INTERVAL_MS);
        }

        if (hasBeenPreempted()) {
          return;
        }

        log.info(
          ns`stepPlay.beginning`,
          `Beginning step ${step.getStepKind()} (${step.id})`,
          {
            kind: step.getStepKind(),
            id: step.id,
            stepNumber: ii,
          },
        );

        this.currentlyRunningSteps = [step, ...this.currentlyRunningSteps];

        let isFailed = false;

        this.checkCollisions();

        log.info(ns`playSteps`, `Playing step ${ii + 1}/${steps.length}`);

        await step.play({
          pushMode,
          breakOutOfLoop,
          continueLoop,
          hasBeenPreempted,
          hasEncounteredStartStep: () => this.hasEncounteredStartStep,
          fail: async (failure) => {
            isFailed = true;

            if (!hasBeenPreempted()) {
              await this.pauseOrFail({
                ...failure,
                step,
              });
            }
          },
          playSubSteps: async (subArgs) => {
            // Prefer per-call substeps threaded through `subArgs.substeps`
            // (e.g. CallSubroutine's freshly-constructed subroutine tree),
            // falling back to the spec-bound `step.substeps` for steps whose
            // substeps are stable across dispatches (e.g. Loop, If).
            const subStepsToPlay = subArgs?.substeps ?? step.substeps;

            if (subStepsToPlay) {
              log.info(ns`playSubsteps`, 'playing substeps');

              // `...subArgs` spreads LAST on purpose: any key the dispatching
              // step passes explicitly — including an explicit `undefined` —
              // overrides this dispatch's inherited value. CallSubroutine
              // relies on this to sever `breakOutOfLoop`/`continueLoop` at
              // the routine boundary so a sub-resident LoopControl can't
              // drive the caller's Loop; absent keys inherit as before.
              await this.playSteps({
                steps: subStepsToPlay,
                pushMode,
                breakOutOfLoop,
                continueLoop,
                hasBeenPreempted,
                skipSettingCurrentStepID,
                parentStepID: step.id,
                ...subArgs,
              });

              log.info(ns`playSubSteps`, 'finished substeps');

              setCurrentStepID();
            }
          },
          pauseRoutine: (pauseDetails) => {
            return this.pauseRoutine(pauseDetails);
          },
        });

        log.info(ns`playSteps`, `Done playing step ${ii + 1}/${steps.length}`);

        // Update If/ElseIf chain state after step completes
        if (
          (step instanceof IfStep || step instanceof ElseIfStep) &&
          step.wasConditionSatisfied()
        ) {
          ifElseBlockSatisfied = true;

          log.info(
            ns`playSteps.conditionalSatisfied`,
            `If/ElseIf chain satisfied by ${step.getStepKind()} (${step.id})`,
          );
        }

        if (this.routineContext.loadedRoutineState) {
          this.bigQuery.insertRoutineStepCompletedEvent(
            this.routineContext.loadedRoutineState.id,
            step.getStepKind(),
            step.timers.self.getTime() / 1000,
            step.timers.full.getTime() / 1000,
          );
        } else {
          log.warn(
            ns`playSteps`,
            'this.routineContext.loadedRoutineState not found when trying to insert routine step completed event',
            {
              loadedRoutineState: this.routineContext?.loadedRoutineState,
            },
          );
        }

        this.currentlyRunningSteps = this.currentlyRunningSteps.filter(
          (runningStep) => runningStep !== step,
        );

        try {
          await step.onCleanup({
            fail: async (failure) => {
              log.error(ns`step.cleanup`, 'Cleanup reported failure', {
                stepId: step.id,
                stepKind: step.getStepKind(),
                failure,
              });
            },
          });
        } catch (error) {
          log.error(ns`step.cleanup`, 'Cleanup error', {
            stepId: step.id,
            stepKind: step.getStepKind(),
            error,
          });
        }

        if (hasBeenPreempted()) {
          // Preemption (e.g. a mid-step stop) is expected control flow, not a
          // failure — this path never calls fail(), so log at info, not error.
          log.info(
            ns`stepPlay.preempted`,
            `Step ${step.getStepKind()} (${step.id}) preempted`,
            {
              kind: step.getStepKind(),
              id: step.id,
              stepNumber: ii,
            },
          );

          return;
        }

        const stepElapsedTime = step.timers.full.getTime() / 1000;

        if (isFailed) {
          log.error(
            ns`stepPlay.failed`,
            `Step failed stepKind=${step.getStepKind()}`,
            {
              kind: step.getStepKind(),
              id: step.id,
              stepNumber: ii,
              stepFailedTime: stepElapsedTime,
            },
          );

          // Trigger Fault is a bit of special step that will always fail
          // So once recovered, we want to resume from NEXT step
          if (step.getStepKind() !== 'TriggerFault') {
            ii -= 1; // if we paused, we want to resume on the same step
          }
        } else {
          log.info(
            ns`stepPlay.succeeded`,
            `Step succeeded stepKind=${step.getStepKind()}`,
            {
              kind: step.getStepKind(),
              id: step.id,
              stepNumber: ii,
              stepSucceededTime: stepElapsedTime,
            },
          );
        }

        log.info(ns`step.stopped`, `Calling stop on Step ${step.id}`, {
          kind: step.getStepKind(),
          id: step.id,
          stepNumber: ii,
        });

        await step.stop({
          fail: async (failure) => {
            await this.pauseOrFail({
              ...failure,
              step,
            });
          },
        });
      } catch (error) {
        log.info(ns`playSteps`, `Error while play step ${ii}/${steps.length}`, {
          errorMsg: error?.message,
        });

        if (hasBeenPreempted()) {
          // Step rejected because we preempted it — expected control flow, not
          // a failure (no fail() call), so log at info, not error.
          log.info(
            ns`stepPlay.preempted`,
            `Step ${step.getStepKind()} (${
              step.id
            }) failed internally but was preempted`,
            {
              kind: step.getStepKind(),
              id: step.id,
              stepNumber: ii,
            },
          );

          return;
        }

        log.error(
          ns`playSteps.InternalFailure`,
          'Internal Failure in playSteps',
          { error, step },
        );

        // steps are never supposed to throw errors, but it could be an internal bug
        this.fail({
          failure: { kind: FailureKind.InternalFailure },
          failureReason: `Step ${step.getStepKind()} failed internally`,
          error,
        });

        return;
      }

      // Yield to the macrotask queue between step N's stop and step N+1's
      // play. Without this, the heartbeat setInterval can be starved for
      // 500-800ms when motion planning + ROS goal IPC + IO-state cascade
      // bundle into a single tick at the step boundary. Skip the yield
      // on the final iteration since there is no next step to break
      // away from.
      if (ii < steps.length - 1) {
        await yieldToEventLoop();
      }
    }

    if (parentStepID) {
      log.info(
        ns`stepPlay.parent.finished`,
        `Step finished parentStepID=${parentStepID}`,
        {
          parentStepID,
          parentStepTime: (Date.now() - playStepsStartTime) / 1000,
        },
      );
    }
  }

  private async cleanupCurrentlyRunningSteps(): Promise<void> {
    log.info(
      ns`cleanupCurrentlyRunningSteps`,
      'Cleaning up currently running steps',
    );

    if (this.currentlyRunningSteps.length === 0) {
      return;
    }

    const stepsToCleanup = [...this.currentlyRunningSteps];

    await Promise.all(
      stepsToCleanup.map(async (step) => {
        try {
          await step.onCleanup({
            fail: async (failure) => {
              log.error(ns`step.cleanup`, 'Cleanup reported failure', {
                stepId: step.id,
                stepKind: step.getStepKind(),
                failure,
              });
            },
          });
        } catch (error) {
          log.error(ns`step.cleanup`, 'Cleanup error', {
            stepId: step.id,
            stepKind: step.getStepKind(),
            error,
          });
        }
      }),
    );
  }

  private async stopArmAndGripper(): Promise<void> {
    return this.routineContext.stopArmAndGripper();
  }

  /**
   * if stopped in the middle of recovery, restore the failure
   */
  private restoreFailureStateKind() {
    if (this.state.kind !== 'Failure' && this.state.failureState) {
      log.info(ns`restoreFailureStateKind`, 'Restoring failure state', {
        stateKind: this.state.kind,
        stateFailureState: this.state.failureState,
      });

      this.state = {
        kind: 'Failure',
        kinematicState: this.state.kinematicState,
        equipmentState: this.state.equipmentState,
        configuration: this.state.configuration,
        failureState: this.state.failureState,
        visualizationState: this.state.visualizationState,
        forceRecoveryMode: this.state.forceRecoveryMode,
      };
    }
  }

  /**
   * Stops the current action entirely (no failure in itself)
   */
  private async stopRunningWork(): Promise<void> {
    // Stop any currently running routine steps first
    if (this.currentlyRunningSteps.length > 0) {
      await Promise.all(
        this.currentlyRunningSteps.map((runningStep) => {
          return runningStep.stop({
            fail: async (failure) => {
              await this.pauseOrFail({
                ...failure,
                step: runningStep,
              });
            },
          });
        }),
      );
    }

    // Cancel any in-flight ad-hoc motion (ROS1/ROS2 agnostic)
    if (this.adHocMotion) {
      if (
        !this.adHocMotion.hasEmittedAny(
          'complete',
          'failure',
          'cancel',
          'cancelled',
        )
      ) {
        log.info(ns`stop`, 'Emitting cancel event to adHocMotion');
        this.adHocMotion.emit('cancel');
        await this.adHocMotion.race('complete', 'failure', 'cancelled');
      } else if (this.tcJogInFlight) {
        // A TC jog's cancel always finishes (a ROS cancel may not), and a
        // stopArm now would take control mid-decel and latch the TC in ERROR.
        log.info(ns`stop`, 'Waiting for the TC jog cancel already in flight');
        await this.adHocMotion.race('complete', 'failure', 'cancelled');
      }
    } else {
      log.info(ns`stop`, 'No adHocMotion to cancel');
    }
  }

  public async stop(reasonForStopping = 'stopped by client') {
    log.info(ns`stopping`, `Stopping because: ${reasonForStopping}`, {
      reasonForStopping,
      stateKind: this.state.kind,
      currentlyRunningSteps: this.currentlyRunningSteps.map((st) => ({
        id: st.id,
        name: st.getStepKind(),
      })),
    });

    this.instructionID += 1;
    this.startOfGuidedMode = null;
    // Expire the guided-mode clock too, not just its start. An explicit stop
    // has to be visible to `isGuidedModeValid()` callers that gate a motion
    // they are about to start: clearing only `startOfGuidedMode` leaves the
    // last validation timestamp fresh for up to `guidedModeTimeoutMS`, so a
    // motion issued in that window sees a valid hold and proceeds. The staged
    // boxing move's inter-leg gate and the SUP-3983 queued-motion guard both
    // read this.
    this.lastGuidedModeValidation = new Date(0);
    await this.stopRunningWork();

    // A recoverable trigger fault ("User Can Resume") pauses the routine in
    // place: `RoutineRunning` + `isPaused` + a populated `failureState` whose
    // `recoveryType` is `Recoverable`. This is a resumable, software-level
    // pause — not a latched hardware failure — so Stop must abandon the run
    // (→ Idle) like any other pause. Latching it into `Failure` via
    // restoreFailureStateKind() below wedges the robot: resume rejects
    // (not paused-RoutineRunning), restart rejects (not Idle), and the
    // Recover button re-routes to resume — leaving no UI path out.
    const isPausedResumableTriggerFault =
      this.state.kind === 'RoutineRunning' &&
      this.state.isPaused &&
      this.state.failureState?.failure.kind ===
        FailureKind.TriggerFaultFailure &&
      this.state.failureState?.recoveryType === 'Recoverable';

    // State transition handling
    // If we have a failureState present, restore/maintain Failure state.
    // This automatically handles:
    // - Stopping during recovery (Recovering state has failureState)
    // - Stopping in ManualRecoveryMode (failureState with recoveryType)
    // - Stopping after fail() is called (already in Failure with failureState)
    //
    // To explicitly clear a failure and go to Idle, the failureState must be
    // cleared first (e.g., via successful recover() or resumeRoutine()), or
    // it is a paused resumable trigger fault (see above).
    if (this.state.failureState && !isPausedResumableTriggerFault) {
      this.restoreFailureStateKind();
    } else if (this.state.kind === 'RoutineRunning') {
      log.debug(ns`stop`, 'Transitioning RoutineRunning to Idle.', {
        oldStateKind: this.state.kind,
      });

      this.clearExternalControlFaults();

      this.state = {
        kind: 'Idle',
        forceRecoveryMode: this.state.forceRecoveryMode,
        kinematicState: this.state.kinematicState,
        equipmentState: this.state.equipmentState,
        configuration: this.state.configuration,
        failureState: null,
        visualizationState: visualizationAtRunBoundary(
          this.state.visualizationState,
        ),
        cannotStartReasons: this.cannotStartReasons(),
      };
    } else {
      log.debug(ns`stop`, 'Changing routine state.kind on stop.', {
        oldStateKind: this.state.kind,
        newStateKind: 'Idle',
      });

      this.clearExternalControlFaults();

      this.state = {
        kind: 'Idle',
        forceRecoveryMode: this.state.forceRecoveryMode,
        kinematicState: this.state.kinematicState,
        equipmentState: this.state.equipmentState,
        configuration: this.state.configuration,
        failureState: null,
        visualizationState: {
          visualizationConfigs: [],
          visibleItems: [],
          workpieceToOffsetVisualizations: [],
        },
        cannotStartReasons: this.cannotStartReasons(),
      };

      this.update();
    }

    await this.stopArmAndGripper().catch((error) => {
      log.error(
        ns`stopArmAndGripper.failure`,
        'Internal Failure in stopArmAndGripper',
        error,
      );

      const isArmControlTimeout =
        error instanceof Error && (error as any)?.armControlTimeout;

      // async-mutex throws this sentinel from `withTimeout(...)` acquires. It
      // can escape `stopArmAndGripper` when an arm-control-bot RPC happens to
      // contend on botman's request mutex (e.g. mid-unbrake). Route it to the
      // same recoverable failure kind as an ArmControlTimeoutError instead of
      // surfacing the cryptic "Internal communication failure." to the user.
      const isMutexAcquireTimeout =
        error === E_TIMEOUT ||
        (error instanceof Error && error.message === E_TIMEOUT.message);

      // Match on `name` not `instanceof`: prototype is lost across the botman worker boundary (robot.ts:936-939).
      const isMessengerRpcTimeout =
        error instanceof Error && error.name === 'TimeoutError';

      if (
        isArmControlTimeout ||
        isMutexAcquireTimeout ||
        isMessengerRpcTimeout
      ) {
        this.fail({
          failure: { kind: FailureKind.ArmControlBotTimeoutFailure },
          failureReason: 'Arm control bot timed out',
          error,
        });
      } else {
        this.fail({
          failure: { kind: FailureKind.InternalFailure },
          failureReason: 'Internal communication failure.',
          error,
        });
      }
    });
  }

  /**
   * Fails the running routine when an enabled gripper drops off the bus.
   *
   * The drivers already detect a physical disconnect and publish it in the
   * gripper's device state, but nothing consumed it — a routine only noticed at
   * its next actuation, and even then only after the actuate retry window
   * expired. A detached end effector mid-routine is worth stopping for whether
   * or not the next step happens to actuate it.
   *
   * Deliberately skipped while paused: unplugging equipment during a pause is a
   * normal way to swap hardware, and resuming will surface any real problem at
   * the next actuation. Whenever we aren't watching we also rebaseline, so a
   * disconnect that happened while we weren't looking can't fire on resume — the
   * monitor has to observe a gripper connected again before it can report it
   * dropping.
   */
  private checkGripperConnectivity(): void {
    if (this.state.kind !== 'RoutineRunning' || this.state.isPaused) {
      this.gripperDisconnectMonitor.reset();

      return;
    }

    const enabledGripperIds = new Set(
      this.routineContext.equipment.equipmentItems
        .filter((item) => item.isEnabled && isEndEffector(item.config.kind))
        .map((item) => item.id),
    );

    // No early return on an empty set: the monitor forgets grippers absent from
    // the samples, which is how disabling one clears its history. Bailing here
    // would strand a stale entry for the last enabled gripper, and re-enabling
    // it while it was still initializing would look like a fresh disconnect.
    const samples = this.state.equipmentState
      .filter((item) => enabledGripperIds.has(item.id))
      .map((item) => ({
        id: item.id,
        kind: item.kind,
        isConnected: getGripperConnectivity(item.state),
      }));

    const disconnected = this.gripperDisconnectMonitor.observe(samples);

    if (!disconnected) {
      return;
    }

    const message = `The ${disconnected.kind} gripper disconnected while the routine was running. Check that it is securely attached to the end effector.`;

    log.error(ns`gripper.disconnected`, message, {
      equipmentId: disconnected.id,
      kind: disconnected.kind,
    });

    this.fail({
      failure: { kind: FailureKind.GripperFailure, message },
      failureReason: message,
    });
  }

  /**
   * Stops the current action with a failure.
   *
   * Mostly a proxy for a synchronous RoutineContext.fail.
   */
  private async fail(failure: StepFailure) {
    // No reporting here. Every path that actually commits a failure goes
    // through `RoutineContext.fail`, which authors the customer record itself,
    // so reporting up front only adds the paths that DON'T commit — and those
    // are the ones that must not reach a customer. The idempotency check below
    // deliberately preserves an active EStopTriggered when a later PoweredOff
    // arrives; reporting first would have replaced the customer's ROBOT_ESTOP
    // with ROBOT_POWERED_OFF while the routine still said E-stop, so the two
    // surfaces would name different root causes for the same incident.
    if (
      this.state.failureState?.recoveryType === 'ManualRecoveryMode' ||
      (this.state.kind === 'RunningAdHocCommand' &&
        this.state.forceRecoveryMode)
    ) {
      // failures during manual recovery or ad-hoc commands in forced recovery do not exit recovery
      return;
    }

    if (this.state.kind === 'Failure') {
      // Three narrow upgrade rules that beat the default idempotency:
      //
      // 1. EStopTriggered/PoweredOff upgrades over a ControlSystemFailure — the
      //    firmware reason ("Control Box E-Stop button", etc.) is more
      //    user-facing than the generic ControlSystemFailure that
      //    arm-control-bot stamps repeatedly from the periodic
      //    ResultOnJoint stream while a trip counter decays.
      //
      // 2. A configured-IO EStop reason can be refined: arm-control-bot
      //    emits "configured safety input" generic on the firmware
      //    EStopTriggered CAN (immediate, no IOState dependency), then
      //    re-emits "configured safety input (1-2)" specific once the
      //    IOState resend lets it observe the firing pair. We allow the
      //    second event to update the failureReason so the UI sees the
      //    pair detail. The match is intentionally tight (previous text
      //    must be exactly the generic suffix, new text must add the
      //    pair detail prefix) so unrelated EStop→EStop transitions —
      //    e.g. cross-board cascade where a downstream PhysicalEstop
      //    arrives after an upstream PossibleShortError — keep falling
      //    through the idempotency check and preserve the root cause.
      const previousFailure = this.state.failureState?.failure;
      const previousReason = this.state.failureState?.failureReason;
      const previousKind = previousFailure?.kind;
      const nextKind = failure.failure.kind;

      const GENERIC_CONFIGURED_IO_SUFFIX = 'configured safety input';
      const SPECIFIC_CONFIGURED_IO_PREFIX = 'configured safety input (';

      const isUpgradeFromControlSystem =
        (nextKind === FailureKind.EStopTriggered ||
          nextKind === FailureKind.PoweredOff) &&
        previousKind === FailureKind.ControlSystemFailure;

      const isConfiguredIORefinement =
        nextKind === FailureKind.EStopTriggered &&
        previousKind === FailureKind.EStopTriggered &&
        previousReason?.endsWith(GENERIC_CONFIGURED_IO_SUFFIX) === true &&
        failure.failureReason.includes(SPECIFIC_CONFIGURED_IO_PREFIX);

      // 3. A genuine (hardware) E-Stop firing while the arm is powering off
      //    must win — a real emergency can never hide behind a deliberate
      //    power-off. The reverse (PoweredOff arriving during an active
      //    EStopTriggered) falls through to the idempotency check so the
      //    root cause is preserved.
      const isHardwareOverPowerOff =
        nextKind === FailureKind.EStopTriggered &&
        previousKind === FailureKind.PoweredOff;

      if (
        isUpgradeFromControlSystem ||
        isConfiguredIORefinement ||
        isHardwareOverPowerOff
      ) {
        log.info(
          ns`fail.upgradeReason`,
          (() => {
            if (isUpgradeFromControlSystem) {
              return `Replacing stale ControlSystemFailure with ${nextKind}`;
            }

            if (isHardwareOverPowerOff) {
              return 'Replacing PoweredOff with hardware EStopTriggered';
            }

            return 'Refining configured-IO EStop reason with triggered pair detail';
          })(),
          { previousReason, nextReason: failure.failureReason },
        );

        // Match the normal entry into Failure: drop the cached FK collision
        // result so the next loop tick re-evaluates against the new failure
        // (per the "keep both sides of every Failure transition symmetric"
        // rule above).
        this.resetCollisionCheckCache();

        await this.routineContext.fail(failure);
      }

      return;
    }

    // [SUP-4118] A safety / control-system stop pauses the routine with a
    // `failureState` that already names the real cause (e.g. "Joint 5:
    // DualInputEncoderDeviationExceeded", or a torque/joint-limit watchdog).
    // The in-flight motion goal then aborts as a side effect of that stop and
    // reaches us as a generic `ExecutionFailure` ("Controller aborted on …
    // signal") which would otherwise overwrite the detailed reason. When that
    // happens, surface the already-known control-system reason instead of the
    // generic catch-all.
    //
    // Scoped to a LIVE control-system pause (`RoutineRunning` + `isPaused`) so
    // we only adopt the reason of a *concurrent* stop. This deliberately
    // excludes `Recovering`, which retains the original incident's
    // `failureState`: a recovery-motion `ExecutionFailure` there must surface
    // its own reason, not be masked by the now-stale control-system text. A
    // genuine motion-planning `ExecutionFailure` (no concurrent paused
    // control-system state) is likewise left untouched.
    const activeControlSystemFailure =
      failure.failure.kind === FailureKind.ExecutionFailure &&
      this.state.kind === 'RoutineRunning' &&
      this.state.isPaused &&
      this.state.failureState != null &&
      CONTROL_SYSTEM_FAILURE_KINDS.has(this.state.failureState.failure.kind)
        ? this.state.failureState
        : null;

    if (activeControlSystemFailure) {
      log.info(
        ns`fail.preferControlSystemReason`,
        'Preferring active control-system failure reason over generic motion abort',
        {
          genericReason: failure.failureReason,
          preferredReason: activeControlSystemFailure.failureReason,
          preferredKind: activeControlSystemFailure.failure.kind,
        },
      );
    }

    // Use the concurrent control-system reason when present (see above);
    // otherwise fail with the incoming failure unchanged. A new local — rather
    // than reassigning the `failure` parameter — keeps the parameter immutable
    // (no-param-reassign).
    const resolvedFailure: StepFailure = activeControlSystemFailure
      ? {
          ...failure,
          failureReason: activeControlSystemFailure.failureReason,
          // The adopted reason names the control-system failure's step;
          // carry that failure's attribution too so the wire fields and
          // the reason text describe the same step.
          preservedAttribution: {
            failedStep: activeControlSystemFailure.failedStep,
            failedStepRoutineId: activeControlSystemFailure.failedStepRoutineId,
            failedStepRoutineName:
              activeControlSystemFailure.failedStepRoutineName,
            failedStepPosition: activeControlSystemFailure.failedStepPosition,
            failedStepLoadedRoutineStepId:
              activeControlSystemFailure.failedStepLoadedRoutineStepId,
          },
        }
      : failure;

    // Drop the checkCollisions cache on any genuine entry into Failure. The
    // Failure → Idle transition in recover() clears it too; keeping both sides
    // of the transition symmetric guarantees we never serve a stale cached FK
    // result across a state change.
    this.resetCollisionCheckCache();

    // If transitioning from anti-gravity mode to failure, reset the wrist button release flag
    // This ensures user must release and re-press the button to trigger auto-recovery
    if (
      this.state.kind === 'Antigravity' ||
      this.state.kind === 'AntigravitySlow'
    ) {
      this.hasWristButtonBeenReleasedSinceFailure = false;
    }

    // Strip `step` from the logged payload: `Step` instances carry a
    // back-reference to `RoutineContext` (which holds an `EventEmitter`
    // with self-references), so JSON-serializing the full failure object
    // throws "Converting circular structure to JSON".
    const { step: _step, ...failureForLog } = resolvedFailure;

    // Call sites that pass `step` without `stepID` still identify the step.
    const failedStepId =
      resolvedFailure.preservedAttribution?.failedStep ??
      resolvedFailure.stepID ??
      resolvedFailure.step?.id;

    log.error(
      ns`fail`,
      // System-level failures (equipment, joint limits) carry no step, and the
      // stop reason below already words those as "Failed: ...".
      failedStepId
        ? `Step ${failedStepId} failed: ${resolvedFailure.failureReason} (${resolvedFailure.failure.kind})`
        : `Failed: ${resolvedFailure.failureReason} (${resolvedFailure.failure.kind})`,
      { failure: failureForLog },
    );

    // Contain the awaited tail (mirroring `pauseOrFail`). Most of the ~30
    // callers fire-and-forget `fail()`; `stop()`'s arm/gripper RPCs (and the
    // failure-state publish) can reject on timeout — e.g. an e-stop mid-motion
    // races arm-control-bot mid-brake. An un-contained rejection here would
    // escape as an unhandledRejection that botman turns into process.exit(1).
    // Containing at the source makes every `fail()` call site crash-safe,
    // awaited or floating, so no caller needs its own `.catch()`.
    try {
      // Immediately publish failure state so UI and systems can react without waiting on stop()
      await this.routineContext.fail(resolvedFailure);

      // Then stop arm and devices
      if (failedStepId) {
        await this.stop(
          `Step ${failedStepId} failed: ${resolvedFailure.failureReason}`,
        );
      } else {
        await this.stop(`Failed: ${resolvedFailure.failureReason}`);
      }
    } catch (error) {
      log.error(
        ns`fail.error`,
        'Error publishing failure state or stopping after fail()',
        error,
      );
    }
  }

  public async emergencyStop(source: EStopSource, reason: string = '') {
    await this.routineContext.robot.emergencyStop({ source, reason });
  }

  /** Request deliberate arm-power re-enable. */
  public async enableArmPower(): Promise<void> {
    await this.routineContext.robot.enableArmPower();

    // enableArmPower can only succeed once the firmware has cleared its
    // latched-estop and isEStopTriggered() == false, so any prior power-side
    // failure (EStopTriggered, PoweredOff, or a power-derived
    // ControlSystemFailure such as PossibleShortError /
    // BrakeOvertemperatureError / PowerLossDetected /
    // CANReceiverWatchdogError, which arm-control-bot maps to
    // ControlSystemFailure via handlePowerErrorMessages) is acknowledged.
    // Drop the failureState so a subsequent EStop event surfaces its own
    // reason instead of being silenced by fail()'s idempotency check, and
    // so a lingering periodic ResultOnJoint trip doesn't keep stamping a
    // stale failure over the cleared one. Other failure kinds (collision,
    // safety watchdog, etc.) are preserved — they need their own recover()
    // flow.
    const failureKind = this.state.failureState?.failure.kind;

    const isPowerSideFailure =
      failureKind === FailureKind.EStopTriggered ||
      failureKind === FailureKind.PoweredOff ||
      failureKind === FailureKind.ControlSystemFailure;

    if (this.state.kind === 'Failure' && isPowerSideFailure) {
      log.info(
        ns`enableArmPower.clearFailure`,
        'Clearing power-side failure state after arm power re-enable',
        {
          previousReason: this.state.failureState.failureReason,
          previousKind: failureKind,
        },
      );

      await this.transitionToIdleFromFailure();
    }
  }

  /**
   * Clears the guided-mode collision pause overlay and failure without resuming.
   * - Keeps routine paused
   * - Removes the 'collision' pause message
   * - Preserves guided-mode checking state
   *
   * Intended for the guided-mode first-move flow: UI clears the overlay and keeps waiting for validation.
   */
  public async clearGuidedModeCollisionPause(): Promise<void> {
    if (this.state.kind !== 'RoutineRunning') {
      throw new Error(
        'Routine must be running to clear guided-mode collision pause',
      );
    }

    if (!this.state.isPaused) {
      throw new Error(
        'Routine must be paused to clear guided-mode collision pause',
      );
    }

    const hasCollisionPause = this.state.pauseDetails?.some(
      (d) => d.kind === 'collision',
    );

    if (!hasCollisionPause) {
      throw new Error('No collision pause present to clear');
    }

    // Clear only what's necessary:
    // - Clear failure overlay
    // - Remove the 'collision' pause message
    // - Keep paused; keep guided-mode check as-is
    const filteredPauseDetails =
      this.state.pauseDetails?.filter((d) => d.kind !== 'collision') ?? [];

    this.clearExternalControlFaults();

    this.state = {
      ...this.state,
      failureState: null,
      isPaused: true,
      pauseDetails: filteredPauseDetails,
      shouldCheckGuidedMode: this.state.shouldCheckGuidedMode,
    } as RoutineRunnerState;
  }

  /**
   * Clears the guided-mode 3PE pause overlay and failure without resuming.
   * - Keeps routine paused
   * - Removes the 'pause3pe' pause message
   * - Preserves guided-mode checking state
   *
   * Intended for the guided-mode first-move flow: UI clears the overlay and keeps waiting for validation.
   */
  public async clearGuidedMode3PEPause(): Promise<void> {
    if (this.state.kind !== 'RoutineRunning') {
      throw new Error('Routine must be running to clear guided-mode 3PE pause');
    }

    if (!this.state.isPaused) {
      throw new Error('Routine must be paused to clear guided-mode 3PE pause');
    }

    const has3PEPause = this.state.pauseDetails?.some(
      (d) => d.kind === 'pause3pe',
    );

    if (!has3PEPause) {
      throw new Error('No 3PE pause present to clear');
    }

    // Clear only what's necessary:
    // - Clear failure overlay
    // - Remove the 'pause3pe' pause message
    // - Keep paused; keep guided-mode check as-is
    const filteredPauseDetails =
      this.state.pauseDetails?.filter((d) => d.kind !== 'pause3pe') ?? [];

    this.clearExternalControlFaults();

    this.state = {
      ...this.state,
      failureState: null,
      isPaused: true,
      pauseDetails: filteredPauseDetails,
      shouldCheckGuidedMode: this.state.shouldCheckGuidedMode,
    } as RoutineRunnerState;
  }

  /**
   * Pauses the running routine
   *
   * @category Routine Playback
   */
  public async pauseRoutine(pauseDetails: RoutineRunningPauseDetails) {
    if (this.state.kind !== 'RoutineRunning') {
      throw new Error('Routine must be running to pause');
    }

    // If a device (lift) actuation is in progress, interpret user pause as lift-pause only
    if (
      pauseDetails.kind === 'user' &&
      this.state.deviceActuationState?.inProgress
    ) {
      for (const runningStep of this.currentlyRunningSteps) {
        if (runningStep.getStepKind() === 'MoveArmToV2') {
          runningStep.pause({
            fail: async (failure) => {
              await this.pauseOrFail({
                ...failure,
                step: runningStep,
              });
            },
          });

          break;
        }
      }

      if (this.state.kind !== 'RoutineRunning') {
        return;
      }

      // Acknowledge routine paused with deviceActuation reason
      this.state = {
        ...this.state,
        isPaused: true,
        pauseDetails: [
          ...(this.state.pauseDetails?.filter(
            (d) => d.kind !== 'deviceActuation',
          ) ?? []),
          { kind: 'deviceActuation', reason: 'Lift paused' } as any,
        ],
        deviceActuationState: { inProgress: true, paused: true },
      } as RoutineRunnerState;

      return;
    }

    if (!this.state.isPaused) {
      // A new pause episode: nothing has released the arm for it yet.
      this.armReleasedWhilePaused = false;

      log.info(ns`routine.pauseRoutine`, 'Pause routine', {
        pauseDetails,
        currentlyRunningSteps: this.currentlyRunningSteps.map((st) => ({
          id: st.id,
          name: st.getStepKind(),
        })),
      });

      if (!getFeatureFlagSync('ros2', true)) {
        this.stopArmAndGripper().catch(() => {});
      }

      this.currentlyRunningSteps.forEach((runningStep) => {
        runningStep.pause({
          fail: async (failure) => {
            await this.pauseOrFail({
              ...failure,
              step: runningStep,
            });
          },
          reason: pauseDetails.reason,
        });
      });
    } else {
      log.info(ns`routine.pauseRoutine`, 'Routine already paused', {
        pauseDetails,
      });
    }

    if (this.state.kind !== 'RoutineRunning') {
      return;
    }

    // Create a new state object to ensure the StatePatchServer detects the change
    this.state = {
      ...this.state,
      isPaused: true,
      pauseDetails: (
        this.state.pauseDetails?.filter((d) => d.kind !== pauseDetails.kind) ??
        []
      ).concat({
        ...pauseDetails,
        timestamp: Date.now(),
      }),
    };
  }

  private resumeRoutinePreChecks() {
    if (this.state.kind !== 'RoutineRunning') {
      throw new Error('Routine must be running and paused to resume');
    }

    if (!this.state.isPaused) {
      throw new Error('Routine must be paused to unpause');
    }

    const cannotStartReasons = this.cannotStartReasons();

    if (cannotStartReasons.length > 0) {
      if (cannotStartReasons.length === 1) {
        throw new Error(humanReadablePauseKind(cannotStartReasons[0]));
      }

      throw new Error(
        `Cannot resume routine because: ${cannotStartReasons
          .map(humanReadablePauseKind)
          .join(', ')}`,
      );
    }
  }

  /**
   * Resumes a paused routine
   *
   * @category Routine Playback
   */
  public async resumeRoutine() {
    // If a device (lift) actuation is in progress, interpret resume as lift-resume only
    if (
      this.state.kind === 'RoutineRunning' &&
      this.state.deviceActuationState?.inProgress
    ) {
      for (const runningStep of this.currentlyRunningSteps) {
        if (runningStep.getStepKind() === 'MoveArmToV2') {
          await runningStep.resume({
            fail: async (failure) => {
              await this.pauseOrFail({
                ...failure,
                step: runningStep,
              });
            },
          });

          break;
        }
      }

      if (this.state.kind !== 'RoutineRunning') {
        return;
      }

      // Keep routine paused; clear deviceActuation reason but remain paused until user resumes arm
      this.state = {
        ...this.state,
        isPaused: false,
        pauseDetails:
          this.state.pauseDetails?.filter(
            (d) => d.kind !== 'deviceActuation',
          ) ?? [],
        deviceActuationState: { inProgress: true, paused: false },
      } as RoutineRunnerState;

      return;
    }

    // A release of the arm's failure latch may still be in flight: the pause
    // card's acknowledge, or a resume that acknowledged for itself. Motion must
    // not be re-dispatched until it lands (the trajectory controller refuses a
    // rejoin against a held arm), so wait for it. If it fails, the acknowledge
    // has already put the stop back and the failure reaches this caller too,
    // since its resume did not happen. If another resume ran while waiting,
    // there is nothing left to do.
    if (this.pendingArmRelease != null) {
      log.info(
        ns`routine.resume.awaitingRelease`,
        'Waiting for the in-flight arm release before resuming',
      );

      await this.pendingArmRelease;

      if (this.state.kind !== 'RoutineRunning' || !this.state.isPaused) {
        return;
      }
    }

    this.resumeRoutinePreChecks();

    // A control-system stop that landed after the arm was released for the
    // previous one (acknowledgeControlSystemStop cleared that failureState and
    // set the flag): this resume is the tail of the operator's response to the
    // stop before it, not to this one. Resuming would wipe the new stop's
    // reason while arm-control-bot is latched in failure for it, the move
    // would be rejected, and update()'s latched-failure reconciliation would
    // fail the routine with a generic message. Stay paused instead: the new
    // stop already re-paused the routine with its own reason, so the card
    // shows it and the operator acknowledges and resumes from there.
    if (
      this.state.kind === 'RoutineRunning' &&
      this.state.failureState != null &&
      CONTROL_SYSTEM_FAILURE_KINDS.has(this.state.failureState.failure.kind) &&
      this.armReleasedWhilePaused
    ) {
      log.info(
        ns`routine.resume.newStopSinceAcknowledge`,
        'Not resuming: a control-system stop landed after the acknowledgement; staying paused with its reason',
        { failureReason: this.state.failureState.failureReason },
      );

      return;
    }

    log.info(ns`routine.resume`, 'Resume routine', {
      currentlyRunningSteps: this.currentlyRunningSteps.map((st) => ({
        id: st.id,
        name: st.getStepKind(),
      })),
    });

    // Detect collision pause presence
    const isCollisionPausePresent =
      this.state.kind === 'RoutineRunning' &&
      this.state.pauseDetails?.some((d: any) => d.kind === 'collision');

    // Detect 3PE pause presence
    const is3PEPausePresent =
      this.state.kind === 'RoutineRunning' &&
      this.state.pauseDetails?.some((d: any) => d.kind === 'pause3pe');

    // If resuming from a collision pause, first determine whether deviation is small or large.
    // Large deviation should fail into guided mode recovery; small deviation can proceed.
    if (isCollisionPausePresent) {
      if (!this.isCollisionDeviationSmall()) {
        log.info(
          ns`routine.resume`,
          'Large deviation detected during collision resume - failing into guided mode recovery',
        );

        await this.fail({
          failure: {
            kind: FailureKind.CollisionDeviationFailure,
          },
          failureReason:
            'Large trajectory deviation detected after collision. Routine restart required.',
          ...this.currentStepIdentity(),
        });

        return;
      }
    }

    if (this.state.kind === 'RoutineRunning') {
      const guidedRequired = this.state.shouldCheckGuidedMode;
      const guidedValid = this.isGuidedModeValid();

      if (guidedRequired && !guidedValid && isCollisionPausePresent) {
        await this.clearGuidedModeCollisionPause();

        // Do not resume; keep paused to await guided confirmation
        return;
      }

      if (guidedRequired && !guidedValid && is3PEPausePresent) {
        await this.clearGuidedMode3PEPause();

        // Do not resume; keep paused to await guided confirmation
        return;
      }
    }

    // Prevent resuming if guided mode is required but not validated (non-collision cases)
    if (
      this.state.kind === 'RoutineRunning' &&
      this.state.shouldCheckGuidedMode &&
      !this.isGuidedModeValid()
    ) {
      throw new Error(
        'Cannot resume routine: guided mode is required but has not been validated. Hold the guided mode button to continue.',
      );
    }

    // An un-acknowledged control-system stop: arm-control-bot still holds its
    // failure latch and nothing has released it this pause episode. The pause
    // card releases before it plays; a resume that arrives without that step
    // (the bottom-bar and dashboard controls, the control-box Play button,
    // external control) would write the routine unpaused while the arm is
    // still held, and update()'s latched-failure reconciliation would fail the
    // routine with a generic message on its next tick.
    //
    // Mirror the card's own split. A torque collision has an explicit "I have
    // checked for collisions" confirmation before the card acknowledges, so a
    // bare resume over it stays paused: no surface may release a collision
    // latch without that confirmation. Every other control-system stop the
    // card acknowledges on a plain Resume click, so acknowledge it here as
    // part of resuming.
    if (
      this.state.kind === 'RoutineRunning' &&
      this.state.failureState != null &&
      CONTROL_SYSTEM_FAILURE_KINDS.has(this.state.failureState.failure.kind) &&
      !this.armReleasedWhilePaused &&
      this.robot.getInFailureState()
    ) {
      if (isCollisionPausePresent) {
        log.info(
          ns`routine.resume.unacknowledgedCollisionStop`,
          'Not resuming: the collision stop has not been acknowledged; staying paused',
          { failureReason: this.state.failureState.failureReason },
        );

        return;
      }

      log.info(
        ns`routine.resume.acknowledge`,
        'Acknowledging the control-system stop as part of resuming',
        {
          failureKind: this.state.failureState.failure.kind,
          failureReason: this.state.failureState.failureReason,
        },
      );

      // The release must land in arm-control-bot before the steps below
      // re-dispatch their motion goals: the trajectory controller judges a
      // resume goal against the arm's live status, and while the latch still
      // holds the arm it refuses the rejoin ("Could not resume the original
      // motion from where the arm stopped"). So the RPC is awaited. That await
      // is the only suspension point between the checks above and the state
      // write below, so the state is re-checked afterwards: a stop or a
      // failure that landed during the round trip owns the state, and resuming
      // over it would discard it. A pause of a kind that was not present at
      // the release counts as such a stop even when it attaches no
      // failureState; writers that only drop or relabel existing pause kinds
      // (a heartbeat regained, for example) do not.
      const released = this.acknowledgeControlSystemStop(true);

      const pauseKindsAtRelease = new Set(
        this.state.pauseDetails.map((detail) => detail.kind),
      );

      // A failed release has already put the stop back (see
      // acknowledgeControlSystemStop); the error reaches the caller as is.
      await released;

      if (
        this.state.kind !== 'RoutineRunning' ||
        !this.state.isPaused ||
        this.state.failureState != null ||
        this.state.pauseDetails.some(
          (detail) => !pauseKindsAtRelease.has(detail.kind),
        )
      ) {
        log.info(
          ns`routine.resume.stateChangedDuringRelease`,
          'Not resuming: the routine state changed while the arm was being released',
          { kind: this.state.kind },
        );

        return;
      }
    }

    // Past this point, either not resuming from collision or deviation was small

    // Sequential, not forEach: `resume` is async (a CodeBlock step signals
    // its container), and an un-awaited call can be reordered against the
    // pause that preceded it.
    for (const runningStep of [...this.currentlyRunningSteps].reverse()) {
      // eslint-disable-next-line no-await-in-loop -- resume in the same order the steps were started
      await runningStep.resume({
        fail: async (failure) => {
          await this.pauseOrFail({
            ...failure,
            step: runningStep,
          });
        },
      });
    }

    // Set unpaused state after resuming the steps:
    // If a resume throws an error, then state will remain paused
    this.clearExternalControlFaults();

    this.state = {
      ...this.state,
      isPaused: false,
      pauseDetails: [],
      failureState: null,
    } as RoutineRunnerState;

    this.armReleasedWhilePaused = false;
  }

  /**
   * Hand a safety failure to the external-control record stream.
   *
   * Swallows everything: this runs inside failure handling, and a throw here
   * would replace a real robot fault with a reporting bug.
   */
  private reportRobotFailure(failure: StepFailure): void {
    try {
      this.externalControlFaultSink?.authorRobotFailure(failure.failure);
    } catch (err) {
      log.warn(ns`reportRobotFailure`, 'Failed to report a robot fault', {
        err,
      });
    }
  }

  /**
   * Clear the external-control latches.
   *
   * Called from every `failureState: null` transition. Neither latch has any
   * other clearing source, by design. Trajectory-controller state cannot clear
   * them, because an E-stop never touches the TC state machine — it only
   * selects slow-speed limits — so the TC sits in `TC_IDLE` throughout and
   * `TC_IDLE` arrives at 1 kHz. Nor can a session's own teardown, which runs a
   * millisecond after the failure and would put the fault on the wire for about
   * one broadcast while the arm is still unusable.
   *
   * The sink clears both together and publishes once; see `clearFaults`.
   */
  private clearExternalControlFaults(): void {
    try {
      this.externalControlFaultSink?.clearFaults();
    } catch (err) {
      log.warn(ns`clearFaults`, 'Failed to clear external-control faults', {
        err,
      });
    }
  }

  private async pauseOrFail(failure: StepFailure): Promise<void> {
    try {
      if (
        failure.failure.kind === FailureKind.TorqueCollisionFailure &&
        this.state.failureState?.failure.kind ===
          FailureKind.TorqueCollisionFailure
      ) {
        // if we are already in a collision state, we want to preserve the original failure
        log.debug(
          ns`pauseOrFail.collision`,
          'Subsequent collision detected, refailing but keeping failureReason',
          {
            partialState: {
              kind: this.state.kind,
              failureState: this.state.failureState,
            },
          },
        );

        return;
      }

      if (this.state.kind === 'RoutineRunning') {
        const pauseKind: RoutineRunningPauseKind | undefined = (() => {
          switch (failure.failure.kind) {
            case FailureKind.BotmanHeartbeatLost:
              return 'botmanHeartbeatLost';
            case FailureKind.CollisionFailure:
            case FailureKind.TorqueCollisionFailure:
              // Always pause on collision - we'll check deviation on resume
              return 'collision';
            case FailureKind.EStopTriggered:
            case FailureKind.PoweredOff:
              // Power is gone — immediately fail the routine to force restart
              return undefined;
            case FailureKind.SafetyWatchdogViolation:
              return 'safetyWatchdogViolation';
            case FailureKind.Pause3PEFailure:
              return 'pause3pe';
            case FailureKind.ExecutionFailure:
              switch (failure.failure.motionResult?.kind) {
                case 'collision': {
                  // For execution failures with collision, we don't have collision event data
                  // so we'll treat as large deviation (conservative approach)
                  return 'collision';
                }
                case 'eStopTriggered':
                  // For E-Stop, immediately fail the routine to force restart
                  return undefined;
                case 'safetyWatchdogViolation':
                  return 'safetyWatchdogViolation';
                case 'controlSystemEvent':
                  return 'controlSystemEvent';
                case 'imuCollision':
                  // IMU collisions are recoverable: pause like a control-system
                  // event so the routine can resume (SUP-4071). Without this the
                  // motion-result path falls through to default -> fail().
                  return 'controlSystemEvent';
                case 'botmanHeartbeatLost':
                  return 'botmanHeartbeatLost';
                case 'ioStateChanged':
                  // calls pauseRoutine internally if relevant
                  this.handleIOStateChange(failure.failure.motionResult);

                  return undefined;
                default:
                  return undefined;
              }
            case FailureKind.MotionPlannerFailure:
              return 'motionPlannerFailure';
            case FailureKind.ControlSystemFailure:
              return 'controlSystemEvent';
            case FailureKind.TriggerFaultFailure:
              if (failure.failure.isRecoverable) {
                return 'triggerFaultFailure';
              }

              return undefined;
            default:
              return undefined;
          }
        })();

        if (pauseKind) {
          const isAutoResume = this.isAutoResumePauseKind(pauseKind);

          log.info(ns`pauseOrFail`, 'Handling pausable failure', {
            pauseKind,
            isAutoResume,
            failureKind: failure.failure.kind,
          });

          // Reported here rather than at the top of the method, and only on the
          // pausing branch. A pause never reaches `RoutineContext.fail`, so
          // without this a collision or 3PE stop would stop the arm with the
          // customer told nothing. Everything that falls through to `fail()` is
          // authored there instead, so nothing is reported twice — and the
          // subsequent-collision early return above preserves the original
          // failure, which now means it preserves the customer's fault too.
          this.reportRobotFailure(failure);

          // For collisions and 3PE pauses, always pause and let user confirm with guided mode
          if (pauseKind === 'collision' || pauseKind === 'pause3pe') {
            await this.pauseRoutine({
              kind: pauseKind,
              reason: failure.failureReason,
              isAutoResume: false,
            });

            // Use the existing recovery type system to determine the appropriate recovery mode
            const recoveryType = getRecoveryType(failure.failure);

            const derived =
              this.routineContext.loadedRoutine?.deriveStepFailureFields(
                failure,
              ) ?? unattributedStepFailureFields(failure);

            this.state = {
              ...this.state,
              failureState: {
                failure: failure.failure,
                failureReason: derived.failureReason,
                isRecoverableWithWristButton: getIsRecoverableWithWristButton(
                  failure.failure,
                ),
                recoveryType,
                failedStep: derived.failedStep,
                failedStepRoutineId: derived.failedStepRoutineId,
                failedStepRoutineName: derived.failedStepRoutineName,
                failedStepPosition: derived.failedStepPosition,
                failedStepLoadedRoutineStepId:
                  derived.failedStepLoadedRoutineStepId,
              },
            };

            return;
          }

          // For other auto-resume cases, use the pause system
          await this.pauseRoutine({
            kind: pauseKind,
            reason: failure.failureReason,
            isAutoResume,
          });

          if (!isAutoResume) {
            // Use the existing recovery type system to determine the appropriate recovery mode
            const recoveryType = getRecoveryType(failure.failure);

            const derived =
              this.routineContext.loadedRoutine?.deriveStepFailureFields(
                failure,
              ) ?? unattributedStepFailureFields(failure);

            this.state = {
              ...this.state,
              failureState: {
                failure: failure.failure,
                failureReason: derived.failureReason,
                isRecoverableWithWristButton: getIsRecoverableWithWristButton(
                  failure.failure,
                ),
                recoveryType,
                failedStep: derived.failedStep,
                failedStepRoutineId: derived.failedStepRoutineId,
                failedStepRoutineName: derived.failedStepRoutineName,
                failedStepPosition: derived.failedStepPosition,
                failedStepLoadedRoutineStepId:
                  derived.failedStepLoadedRoutineStepId,
              },
            };
          }

          return;
        }
      }

      await this.fail(failure);
    } catch (error) {
      log.error(ns`pauseOrFail.error`, 'Error in pauseOrFail', error);
    }
  }

  private isAutoResumePauseKind(pauseKind: RoutineRunningPauseKind): boolean {
    switch (pauseKind) {
      case 'motionPlannerFailure':
      case 'botmanHeartbeatLost':
        return true;
      case 'ethernetIPDisconnection':
        return false; // User must manually resolve connection issue
      default:
        return false;
    }
  }

  /**
   * Mark the existing botmanHeartbeatLost pause as manual-resume-required.
   * Called from each bail-out branch of the heartbeat-regained handler.
   *
   * Without this, the pause stays with `isAutoResume: true` forever, so the
   * UI/event-detection path (`detectRoutineEvent.ts`) keeps treating it as
   * "will clear automatically" — but no future event will clear it (heartbeat
   * is already regained, no retry will fire). The user is left looking at a
   * paused routine with a misleading auto-resume indicator.
   *
   * Flipping `isAutoResume: false` lets the UI surface the pause as requiring
   * user action while keeping the same pause kind and timestamp for context.
   */
  private demoteHeartbeatPauseToManualResume(reason: string): void {
    if (this.state.kind !== 'RoutineRunning') {
      return;
    }

    const pauseDetails = this.state.pauseDetails.map((detail) =>
      detail.kind === 'botmanHeartbeatLost'
        ? { ...detail, isAutoResume: false }
        : detail,
    );

    this.state = {
      ...this.state,
      pauseDetails,
    } as RoutineRunnerState;

    log.warn(
      ns`routine.heartbeat`,
      `Auto-resume after heartbeat regained skipped — manual resume required. ${reason}`,
    );
  }

  /**
   * Snapshot of robot state fields used to gate auto-resume. Two snapshots are
   * "equal" when every field matches, so we can detect transitions in flight.
   */
  private getAutoResumeStateSnapshot(): {
    rosControl: boolean;
    inFailureState: boolean;
    safeguardState: SafeguardState;
    brakesEngaged: Six<boolean>;
  } {
    return {
      rosControl: this.robot.getROSControlEnabled(),
      inFailureState: this.robot.getInFailureState(),
      safeguardState: this.robot.getSafeguardState(),
      brakesEngaged: this.robot.getJointBrakesEngaged(),
    };
  }

  /**
   * Wait for the robot state snapshot to be unchanged for `stableDurationMs`,
   * polling at {@link AUTO_RESUME_POLL_INTERVAL_MS}. Returns true once the
   * snapshot has been stable for the required duration, or false if we hit
   * `timeoutMs` first.
   *
   * Stability is measured against poll samples: the loop only marks the
   * snapshot as "stable since now" once a poll confirms it hasn't changed.
   * This avoids declaring stability on the very first sample (which has been
   * confirmed unchanged for 0 polls).
   */
  private async waitForStableRobotSnapshot({
    stableDurationMs,
    timeoutMs,
  }: {
    stableDurationMs: number;
    timeoutMs: number;
  }): Promise<boolean> {
    const startedAt = Date.now();
    let lastSnapshot = this.getAutoResumeStateSnapshot();
    // null until we've observed at least one poll without change
    let stableSince: number | null = null;

    while (Date.now() - startedAt < timeoutMs) {
      await wait(AUTO_RESUME_POLL_INTERVAL_MS);

      const snapshot = this.getAutoResumeStateSnapshot();

      if (!isEqual(snapshot, lastSnapshot)) {
        lastSnapshot = snapshot;
        stableSince = null;
      } else if (stableSince === null) {
        stableSince = Date.now();
      }

      if (
        stableSince !== null &&
        Date.now() - stableSince >= stableDurationMs
      ) {
        return true;
      }
    }

    return false;
  }

  /**
   * Whether the robot's current observable state is safe for auto-resume.
   * "Safe" means: no failure state, safeguard not in eStop, no joint brakes
   * engaged. Note: `slowSpeed` safeguard is allowed because it is a normal
   * operational mode (motion is permitted, just at reduced speed).
   *
   * `rosControl` is NOT in this predicate — after a heartbeat-lost stop,
   * rosControl is expected to be false until the resume action re-enables it.
   */
  private isStateSafeToAutoResume(): boolean {
    return (
      !this.robot.getInFailureState() &&
      this.robot.getSafeguardState() !== 'eStop' &&
      !this.robot.getJointBrakesEngaged().some((b) => b)
    );
  }

  /**
   * Human-readable description of why {@link isStateSafeToAutoResume} returned
   * false. Used for logging.
   */
  private describeUnsafeAutoResumeState(): string {
    const reasons: string[] = [];

    if (this.robot.getInFailureState()) {
      reasons.push('inFailureState=true');
    }

    const safeguardState = this.robot.getSafeguardState();

    if (safeguardState === 'eStop') {
      reasons.push(`safeguardState=${safeguardState}`);
    }

    if (this.robot.getJointBrakesEngaged().some((b) => b)) {
      reasons.push('one or more joint brakes engaged');
    }

    return reasons.join('; ') || 'state appears safe (unexpected)';
  }

  private async removePauseKind(
    pauseKind: RoutineRunningPauseKind,
  ): Promise<void> {
    if (
      this.state.kind === 'RoutineRunning' &&
      this.state.isPaused &&
      this.state.pauseDetails.some((d) => d.kind === pauseKind)
    ) {
      if (this.state.pauseDetails.length === 1) {
        // this was the only pause reason, so can resume
        await this.resumeRoutine();
      } else {
        this.state = {
          ...this.state,
          pauseDetails: this.state.pauseDetails?.filter(
            (d) => d.kind !== pauseKind,
          ),
        };
      }
    }
  }

  /**
   * Returns data that can be used to show the robot's status in the UI,
   * generate a human readable message, and make programmatic decisions about
   * if the robot is ready to play a new routine, etc.
   */
  public getState(): RoutineRunnerState {
    this.update();

    return this.state;
  }

  /**
   * Returns the currently loaded routine
   */
  public getLoadedRoutineState(): LoadedRoutineState | undefined {
    return this.routineContext.loadedRoutineState;
  }

  /**
   * The space visible to the loaded routine: workspace globals plus the
   * routine's own local space. See
   * {@link RoutineContext.getLoadedRoutineVisibleSpace}.
   */
  public getLoadedRoutineVisibleSpace(): Space.Item[] {
    return this.routineContext.getLoadedRoutineVisibleSpace();
  }

  /**
   * Clear the in-memory loaded routine if (and only if) it matches the given
   * id and the runner is Idle. Returns `true` when the unload happens,
   * `false` when the call is a no-op (no routine loaded, id mismatch, or
   * runner is not Idle).
   *
   * Why this exists: the persistence layer can remove a routine row out from
   * under the runner — either explicitly (user deleting a durable routine
   * via the editor) or automatically (ephemeral-routine reaper on
   * `RoutineRunning → Idle`). Without an unload path the runner keeps
   * `loadedRoutineState` + `steps` pointing at a row that no longer exists,
   * which surfaces a "ghost" routine to anything reading the loaded state
   * (RoutineContext expression context, persistent-variable lookup, space
   * references, dashboard widgets) and allows a stale `playRoutine()` to
   * fire against in-memory steps whose backing row is gone.
   *
   * The Idle guard is defensive: the Feathers `before:remove` hook
   * (`guardLoadedEphemeralRemove`) already rejects removal of a loaded
   * ephemeral routine while the runner is non-Idle, but the runner-side
   * check ensures correctness even if the hook ordering ever changes or a
   * different removal path (e.g. a bulk reset) appears.
   *
   * TODO(ephemeral-routines): the non-Idle branch is a silent no-op. Today
   * the only callers are the durable user-delete path (gated by slice 2's
   * `before:remove` hook) and the ephemeral reaper (runs on
   * `RoutineRunning → Idle`), so the Idle invariant is upheld at the call
   * site. A future remove path that fires while the runner is non-Idle
   * (bulk reset, direct DB write, ops intervention, schema migration)
   * would leave loadedRoutineState permanently ghosted. If such a path is
   * added, either gate it on Idle at the call site or add a one-shot
   * pending-unload that retries on the next `kind → Idle` transition.
   */
  public unloadRoutine(routineId: string): boolean {
    const loadedId = this.routineContext.loadedRoutineState?.id;
    if (loadedId !== routineId) return false;

    if (this.state.kind !== 'Idle') {
      log.warn(
        ns`unloadRoutine.nonIdle`,
        'Refusing to unload loaded routine while runner is non-Idle',
        { routineId, runnerStateKind: this.state.kind },
      );

      return false;
    }

    log.info(ns`unloadRoutine`, 'Unloading routine from runner', {
      routineId,
    });

    // The routine's frame, step tree, workspace globals, and per-load caches
    // all live on the LoadedRoutine — dropping it unloads everything at once,
    // so frame-gated consumers (EtherNet/IP variable reads, external
    // `setVariable`, expression evaluation) read "no routine loaded".
    this.routineContext.clearLoadedRoutine();

    this.update();

    return true;
  }

  /**
   * Get the absolute maximum values that can be provided to `setRobotSpeedLimits`.
   *
   * @category Configuration
   */
  public getGlobalSpeedLimits(): {
    maxJointVelocities: ArmJointVelocities;
    maxJointAccelerations: ArmJointAccelerations;
    maxTooltipSpeed: number;
  } {
    const { modelType } = this.state.kinematicState;

    // Thor: per-voltage 0 kg row ceiling. Voltage is re-read so V24/V42 robots
    // get the lower hardware ceiling even if booted before ACB sampling completed.
    const maxJointAccelerations =
      modelType === ModelType.enum.Thor
        ? getThorMaxAccelForCBVoltage(getCBVoltage())
        : getMaxJointAccelerationsForModel(modelType);

    return {
      maxJointVelocities: ABSOLUTE_MAX_JOINT_SPEEDS,
      maxJointAccelerations,
      maxTooltipSpeed: ABSOLUTE_MAX_TOOLTIP_SPEED,
    };
  }

  /**
   * Set information about the payload for use in dynamics and planning.
   *
   * @param payload Information about the payload
   *
   * @category Configuration
   */
  public async setRobotPayload(payload: PayloadState) {
    await this.routineContext.setPayload(payload);
  }

  /**
   * Brakes the robot
   *
   * @returns promise that resolves when the robot has been braked
   * @category Manual Command
   */
  public async brake(): Promise<void> {
    log.info(ns`braking.started`, 'Braking');
    await this.robot.brake();
    log.info(ns`braking.success`, 'Braked');
    this.update();
  }

  /**
   * Unbrakes the robot
   *
   * @returns promise that resolves true if successful
   * @category Manual Command
   */
  public async unbrake(): Promise<void> {
    log.info(ns`unbraking.started`, 'Unbraking');
    await this.robot.unbrake();
    log.info(ns`unbraking.success`, 'Unbraked');
    this.update();
  }

  /**
   * Tell a step (`WaitForConfirmation`) that it's valid to continue
   *
   * @returns Whether that step was actually waiting for confirmation
   */
  public async confirmStep(stepID: string, input?: string): Promise<boolean> {
    const step = this.currentlyRunningSteps.find(
      (runningStep) => runningStep.id === stepID,
    );

    // Each step type owns its confirmation policy (when input is accepted,
    // whether release must be precheck-guarded); the runner only orchestrates.
    if (!step || !step.acceptsConfirmation()) {
      return false;
    }

    if (step.confirmationNeedsResumePrechecks) {
      this.resumeRoutinePreChecks();
    }

    step.confirm(input);

    // A confirm can be delivered to a RUNNING routine — e.g. a client retry
    // of a prompt tap whose first delivery landed and already resumed: the
    // step may still accept it, but there is nothing to resume, and
    // resumeRoutine's prechecks would throw 'Routine must be paused to
    // unpause'. Resume only when the routine is actually paused (the
    // historical confirmers' situation) or a device actuation is in progress
    // (resumeRoutine interprets that as lift-resume regardless of the pause
    // flag) — any other state has nothing to resume and must not throw on
    // the back of a successfully delivered confirm.
    const shouldResume =
      this.state.kind === 'RoutineRunning' &&
      (this.state.isPaused ||
        Boolean(this.state.deviceActuationState?.inProgress));

    if (shouldResume) {
      await this.resumeRoutine();
    }

    return true;
  }

  /**
   * Refresh the guided mode heartbeat timestamp. The UI calls this continuously
   * (every animation frame) while the operator holds the guided-mode button.
   * If calls stop for longer than {@link guidedModeTimeoutMS} (default 300 ms),
   * the robot pauses.
   */
  public validateGuidedMode() {
    if (!this.startOfGuidedMode) {
      this.startOfGuidedMode = new Date();
    }

    this.lastGuidedModeValidation = new Date();
  }

  /**
   * Helper to determine whether the robot arm is in the joint limits.
   *
   * @returns An array describing the limit violations for each joint.
   *          If error is `null`, the limit was ok.
   *          If it's positive, it was above the maximum allowed position.
   *          If it's negative, it was below the minimum allowed position.
   */
  private jointLimitViolations(): Six<{
    error: number | null;
    min: number;
    max: number;
    actual: number;
  }> {
    const angles = this.getJointAngles();
    const limits = this.routineContext.getJointLimits();

    return limits.map(({ min, max }, jointIndex) => {
      const angle = angles[jointIndex];

      const entry = {
        error: null as number | null,
        min,
        max,
        actual: angle,
      };

      if (angle < min) {
        entry.error = angle - min;
      } else if (angle > max) {
        entry.error = angle - max;
      }

      return entry;
    }) as Six<{
      error: number | null;
      min: number;
      max: number;
      actual: number;
    }>;
  }

  private isOutOfJointLimits(): boolean {
    return this.jointLimitViolations().some((n) => n.error != null);
  }

  private getJointAngles(): ArmJointPositions {
    return this.routineContext.getJointAngles();
  }

  /**
   * Have we received a validateGuidedMode() call during the proper window?
   */
  private isGuidedModeValid(): boolean {
    const now = new Date().getTime();
    const diff = now - this.lastGuidedModeValidation.getTime();

    return diff < this.guidedModeTimeoutMS;
  }

  /**
   * Resolve once the arm is at rest (joint velocities below threshold and
   * position steady), or reject if it is not still within `timeoutMS`. Thin
   * public wrapper over {@link untilStill} so callers outside the runner (the
   * ephemeral-session start/stop gate) can serialize work behind arm-at-rest —
   * preventing a new session from commanding the arm while it is still
   * decelerating from a prior session's motion.
   */
  public waitUntilStill(timeoutMS = Number.POSITIVE_INFINITY): Promise<void> {
    return this.untilStill(timeoutMS);
  }

  private untilStill(timeoutMS = Number.POSITIVE_INFINITY): Promise<void> {
    const velocities = this.state.kinematicState.jointVelocities;
    let tooFast = false;

    for (let ii = 0; ii < velocities.length; ii += 1) {
      if (Math.abs(velocities[ii]) > 0.1) {
        tooFast = true;
      }
    }

    if (!tooFast) {
      return Promise.resolve();
    }

    return new Promise((resolve, reject) => {
      let lastAngles: ArmJointPositions = six(0);
      const start = Date.now();

      const interval = setInterval(() => {
        if (Date.now() - start > timeoutMS) {
          clearInterval(interval);

          reject(
            new Error(
              `Timeout waiting for the robot to be still in ${timeoutMS}ms`,
            ),
          );

          return;
        }

        // eslint-disable-next-line @typescript-eslint/no-shadow
        const velocities = this.state.kinematicState.jointVelocities;

        for (let ii = 0; ii < velocities.length; ii += 1) {
          if (Math.abs(velocities[ii]) > 0.1) {
            return;
          }
        }

        const angles = this.state.kinematicState.jointAngles;

        for (let ii = 0; ii < angles.length; ii += 1) {
          if (
            Math.abs(angles[ii] - lastAngles[ii]) > HOLD_STEADY_POSITION_EPSILON
          ) {
            lastAngles = angles;

            return;
          }
        }

        clearInterval(interval);
        resolve();
      }, 100);
    });
  }

  public async setJointSafetyLimits(limits: JointSafetyLimits) {
    await this.routineContext.setJointSafetyLimits(limits);
  }

  /**
   * Fire-and-forget push of the current world→base pose to the TC. Called by
   * the host on equipment changes (the custom base is an equipment item); see
   * {@link RoutineContext.pushTcWorldToBase}.
   */
  public pushTcWorldToBase(): void {
    this.routineContext.pushTcWorldToBase();
  }

  public async setAccelerationCollisionThresholds(
    thresholds: AccelerationCollisionThresholds,
  ) {
    await this.routineContext.setAccelerationCollisionThresholds(thresholds);
  }

  public setJerkLimit(jerkLimit: JerkLimit) {
    this.routineContext.setJerkLimit(jerkLimit);
  }

  public async setInputSafeguardRules(
    safeguardRules: Array<InputSafeguardRule>,
  ) {
    this.state = {
      ...this.state,
      configuration: {
        ...this.state.configuration,
        inputSafeguardRules: safeguardRules,
      },
    };

    this.robot.setInputSafeguardRules(safeguardRules);
  }

  public async setOutputSafeguardRules(
    outputSafeguardRules: Array<OutputSafeguardRule>,
  ) {
    this.state = {
      ...this.state,
      configuration: {
        ...this.state.configuration,
        outputSafeguardRules,
      },
    };

    this.robot.setOutputSafeguardRules(outputSafeguardRules);
  }

  public async setSafetySettingsData(
    safetySettingsData: SafetySettingsData | null,
  ) {
    await this.routineContext.setSafetySettingsData(safetySettingsData);
  }

  public visionMethodRunner: VisionMethodRunner;

  public setTCPOffsetOption(tcpOffsetOption: TCPOffsetOption) {
    this.routineContext.setTCPOffsetOption(tcpOffsetOption);
  }

  /**
   * The operator's and the API's door in, and so where the write is refused
   * while a routine is running. Setting the Active Tooltip repoints the
   * motion frame, and a running routine's steps do their own motion math
   * against that frame, so moving it out from under them dives the arm by
   * the gap between the two tools.
   *
   * A paused run counts as running: it resumes into those same steps.
   *
   * The steps that set the Active Tooltip themselves — `SetActiveTooltip`
   * and `EnableGripper` — reach the routine context directly and are
   * unaffected. That is the point of guarding here rather than on the write:
   * the routine may repoint its own frame, an operator may not repoint the
   * routine's.
   *
   * botman's two callers already write only while the runner is settled: the
   * boot restore runs before anything plays, and the Active Tooltip
   * reconciler defers on anything but Idle or Failure.
   */
  public setActiveTooltip(frame: TCPFrame) {
    if (this.state.kind === 'RoutineRunning') {
      throw new Error(
        'Cannot set the Active Tooltip while a routine is running. Stop the routine and try again.',
      );
    }

    this.routineContext.setActiveTooltip(frame);
  }

  public async unbrakeJoint(jointNumber: JointNumber) {
    if (
      this.state.failureState?.recoveryType === 'ManualRecoveryMode' ||
      this.state.forceRecoveryMode
    ) {
      log.info(ns`unbrakeJoint`, 'setting manual recovery mode');
      await this.robot.setManualRecoveryMode(true);
    }

    log.info(ns`unbrakeJoint`, `unbraking joint ${jointNumber}`);

    return this.robot.unbrakeJoint(jointNumber);
  }

  public setROSControlEnabled(enable: boolean): Promise<void> {
    if (getFeatureFlagSync('useControlSourceManager', false)) {
      log.warn(
        'RoutineRunner',
        `Ignored setROSControlEnabled because ControlSourceManager is enabled`,
      );

      return Promise.resolve();
    }

    log.info('RoutineRunner', `setROSControlEnabled ${enable}`);

    return this.robot.setROSControlEnabled(enable);
  }

  public clearMotionPlanCache() {
    log.info('RoutineRunner', 'clear ros1 motion planner cache');
    this.motionPlanner.clearMotionPlanCache();
    log.info('RoutineRunner', 'clear ros2 motion plan cache');
    this.rosMotionPlanner.clearMotionPlanCache();
  }

  public setInFailureState(
    isInFailureState: boolean,
    isRecoverableWithWristButton: boolean,
  ): Promise<void> {
    return this.robot.setInFailureState(
      isInFailureState,
      isRecoverableWithWristButton,
    );
  }

  /**
   * Acknowledge a control-system stop and release the arm back to the controller.
   * Used when resuming from a collision, E-stop, or protective stop.
   *
   * Clears the latched `failureState` before releasing, so a resume that fails
   * reports its own reason and not the stop before it. The routine stays paused
   * until a resume succeeds. If the release itself fails, nothing was released
   * and there is no resume outcome to report: the stop is put back exactly as
   * it was, and the next resume releases again.
   *
   * The pause card calls this before it plays; resumeRoutine() calls it itself
   * for a control-system stop nothing has released yet (other than a torque
   * collision, whose confirmation checkbox is the acknowledgement), and waits
   * for any release still in flight before re-dispatching motion.
   */
  public acknowledgeControlSystemStop(
    isRecoverableWithWristButton: boolean,
  ): Promise<void> {
    const clearedStop =
      this.state.kind === 'RoutineRunning' &&
      this.state.isPaused &&
      this.state.failureState != null &&
      CONTROL_SYSTEM_FAILURE_KINDS.has(this.state.failureState.failure.kind)
        ? this.state.failureState
        : null;

    if (clearedStop != null) {
      log.info(
        ns`acknowledgeControlSystemStop`,
        'Clearing latched control-system failureState before releasing the arm',
        {
          clearedKind: clearedStop.failure.kind,
          clearedReason: clearedStop.failureReason,
        },
      );

      this.clearExternalControlFaults();

      this.state = {
        ...this.state,
        failureState: null,
      } as RoutineRunnerState;
    }

    // Any control-system stop that latches from here until the resume is a
    // new one; resumeRoutine() stays paused with it rather than resuming.
    const raisedReleaseFlag =
      this.state.kind === 'RoutineRunning' &&
      this.state.isPaused &&
      !this.armReleasedWhilePaused;

    if (raisedReleaseFlag) {
      this.armReleasedWhilePaused = true;
    }

    // Hold the reconciliation net only when there is a latch to clear: an
    // acknowledge sent while unlatched must not delay convergence of an
    // unrelated wedge that lands moments later.
    const graceStampBefore = this.latchReleaseRequestedAt;
    const graceStamp = this.robot.getInFailureState() ? Date.now() : null;

    if (graceStamp != null) {
      this.latchReleaseRequestedAt = graceStamp;
    }

    const release: Promise<void> = this.releaseArmLatch(
      isRecoverableWithWristButton,
      { clearedStop, raisedReleaseFlag, graceStamp, graceStampBefore },
    ).finally(() => {
      if (this.pendingArmRelease === release) {
        this.pendingArmRelease = null;
      }
    });

    this.pendingArmRelease = release;

    return release;
  }

  /**
   * Sends the latch release to arm-control-bot. If it fails, whatever the
   * acknowledge changed is put back: the stop's `failureState` (unless a newer
   * stop has claimed the slot since), the release flag if this acknowledge
   * raised it, and the reconciliation grace if this acknowledge stamped it. A
   * release recorded by an earlier, successful acknowledge stays recorded. The
   * arm is still held, so the next resume must release again rather than
   * re-dispatch motion against it.
   */
  private async releaseArmLatch(
    isRecoverableWithWristButton: boolean,
    rollback: {
      clearedStop: Extract<
        RoutineRunnerState,
        { kind: 'RoutineRunning' }
      >['failureState'];
      raisedReleaseFlag: boolean;
      graceStamp: number | null;
      graceStampBefore: number | null;
    },
  ): Promise<void> {
    try {
      await this.robot.setInFailureState(false, isRecoverableWithWristButton);
    } catch (error) {
      if (
        rollback.clearedStop != null &&
        this.state.kind === 'RoutineRunning' &&
        this.state.isPaused &&
        this.state.failureState == null
      ) {
        this.state = {
          ...this.state,
          failureState: rollback.clearedStop,
        } as RoutineRunnerState;
      }

      if (rollback.raisedReleaseFlag) {
        this.armReleasedWhilePaused = false;
      }

      if (
        rollback.graceStamp != null &&
        this.latchReleaseRequestedAt === rollback.graceStamp
      ) {
        this.latchReleaseRequestedAt = rollback.graceStampBefore;
      }

      log.warn(
        ns`acknowledgeControlSystemStop.releaseFailed`,
        'Releasing the arm failed; the routine stays paused with its stop',
        { error },
      );

      throw error;
    }
  }

  public setForceRecoveryMode(forceRecoveryMode: boolean): Promise<boolean> {
    if (this.state.kind !== 'Idle') {
      log.warn(
        `setForceRecoveryMode`,
        'Cannot set recovery mode when not kind != Idle',
        {
          kind: this.state.kind,
          intendedForceRecoveryMode: forceRecoveryMode,
        },
      );

      return Promise.resolve(false);
    }

    this.state = {
      ...this.state,
      forceRecoveryMode,
    };

    return Promise.resolve(this.state.forceRecoveryMode);
  }

  /**
   * Evaluates a conditional expression using the current routine context.
   * This method exposes the RoutineContext's conditional evaluation functionality.
   *
   * @param condition The conditional expression to evaluate
   * @returns Promise that resolves to the evaluation result
   */
  public async evaluateConditional(condition: string): Promise<boolean> {
    if (!this.routineContext.loadedRoutineFrame) {
      // No routine loaded — throw rather than returning `false`, which a
      // caller could conflate with a real-evaluation `false`. External
      // callers (the wire protocol receiver, the ResumeFromStepModal)
      // should only invoke this after `loadRoutine` completes.
      throw new Error('Cannot evaluate conditional: no routine is loaded');
    }

    const conditional = JSON.parse(condition);

    return this.routineContext.loadedRoutineFrame.evaluateConditional(
      conditional,
    );
  }

  /**
   * Sets a variable state in the routine context.
   * This method exposes the RoutineContext's variable state management.
   *
   * @param name Variable name
   * @param value Variable value
   */
  public setVariableState(name: string, value: any): void {
    this.routineContext.setLoadedRoutineVariable(name, value);
  }

  /**
   * Subscribe to input IO state changes for external control systems.
   * Returns a function to cancel the subscription.
   *
   * @param callback Function called when input IO state changes with formatted input port states
   * @returns Function to cancel the subscription
   */
  public onInputIOChange(
    callback: (inputIO: Record<AnyInputPortID, IOLevel>) => void,
  ): () => void {
    return this.externalControlIOEvents.on('inputIO', callback);
  }

  /**
   * Calculate joint deviations between current and expected positions
   * @param currentJointPositions Current joint positions
   * @returns Object containing max deviation and individual joint deviations, or null if no trajectory data available
   */
  private calculateJointDeviations(currentJointPositions: ArmJointPositions): {
    maxDeviation: number;
    jointDeviations: ArmJointPositions;
  } | null {
    // Determine expected positions using rolling buffer around collision/pause time
    const expectedPositions =
      this.jointTrajectorySubscription?.getLastValidJointPositions();

    if (!expectedPositions) {
      return null;
    }

    const jointDeviations: ArmJointPositions = [0, 0, 0, 0, 0, 0];
    let maxDeviation = 0;

    for (let i = 0; i < currentJointPositions.length; i += 1) {
      const deviation = Math.abs(
        currentJointPositions[i] - expectedPositions[i],
      );

      jointDeviations[i] = deviation;
      maxDeviation = Math.max(maxDeviation, deviation);
    }

    return {
      maxDeviation,
      jointDeviations,
    };
  }

  /**
   * Determine if a collision caused a small or large deviation based on joint trajectory comparison
   * @returns true if the collision is small (should speed pause), false if large (should stop and enter guided mode)
   */
  private isCollisionDeviationSmall(): boolean {
    // Use joint trajectory comparison for more accurate deviation measurement
    const currentJointPositions = this.getJointAngles();

    const deviations = this.calculateJointDeviations(currentJointPositions);

    if (!deviations) {
      // If we can't calculate joint deviation, treat as large collision (conservative approach)
      return false;
    }

    const isSmall = !deviations.jointDeviations.some((deviation, i) => {
      if (deviation > this.jointDeviationThresholdsRadians[i]) {
        log.info(ns`routine.resume`, 'Large deviation detected', {
          joint: i,
          deviation,
          threshold: this.jointDeviationThresholdsRadians[i],
        });
      }

      return deviation > this.jointDeviationThresholdsRadians[i];
    });

    if (!isSmall) {
      log.info(ns`routine.resume`, 'Large deviation detected deviations', {
        deviations,
      });
    }

    // Check if any joint exceeds its specific threshold (preloaded from YAML)
    return isSmall;
  }

  /**
   * Call inverse kinematics (forward kinematics) on the hybrid motion planner
   */
  public async inverseKinematics(request: InverseKinematicsRequest) {
    return await this.hybridMotionPlanner.inverseKinematics(request);
  }

  public async forwardKinematics(request: ForwardKinematicsRequest) {
    return await this.hybridMotionPlanner.forwardKinematics(request);
  }

  public async doMotion(
    params: Parameters<RoutineContext['doMotion']>[0],
  ): Promise<ReturnType<RoutineContext['doMotion']>> {
    log.info('doMotion', 'Creating adHoc motion', {
      params,
    });

    return await this.adHocMotionMutex.runExclusive(() => {
      this.adHocMotion = this.routineContext.doMotion(params);

      return this.adHocMotion;
    });
  }

  /**
   * Batch jog-direction feasibility for the jog panel's button greyout.
   * `request.planner` selects the planner that answers; see
   * {@link GetToolDirectionsRequest} for what each planner reads and why it
   * must be the planner that would execute the jog.
   * @throws if the robot is braked and `request.planner` is 'tc'
   */
  public async getToolDirections(
    request: GetToolDirectionsRequest,
  ): Promise<FrameDirections> {
    if (request.planner === 'tc') {
      // A braked arm cannot jog, so there is nothing to grey out, and the TC
      // answers queries even while it is not authorized to command. Checked
      // here rather than in the TC so it reads this runner's own robot.
      if (this.robot.isRobotBraked()) {
        throw new Error('Cannot validate jog directions while robot is braked');
      }

      return getJogDirectionsWithTC(
        this.routineContext,
        request.velocityExpressedIn,
      );
    }

    const jointState = this.getJointAngles();

    const directions =
      await this.routineContext.rosMotionPlanner.getToolDirections(
        jointState,
        request.translationDistance,
        request.rotationAngle,
        request.frameOfReference,
        request.rejectWristSingularityCrossing,
      );

    // A colliding start pose reports every direction blocked, with no reason.
    if (isEveryDirectionBlocked(directions)) {
      await this.recheckCollisionsAfterPlannerRejection('toolDirections');
    }

    return directions;
  }
}
